import _ from 'lodash';
import { UtilsMomentJS } from '../store/utilsMoment';
import moment from 'moment/moment';
import config from '../app/config';
import Formatter from './formatter';
import DictParser from './dictParser';
import { obsStatuses, printValue } from '../pages/ward/forms/observation/obsUtils';
import { getConfig } from './configUtil';
import { PATIENT_AGE_TYPE, PATIENT_CONTEXT_TYPE } from './patientContextUtil';
import { findPatientFlagTitle } from '../pages/ward/mainScreen/util/patientFlagsUtil';
import {
    cmToFeetInch,
    convertStatus,
    feetInchToCm,
    SEE_AND_TREAT_STATUSES,
    SEPSIS_TOOL_TYPE,
    sepsisToolAgeParameter,
} from '../pages/ward/forms/formsUtil';
import { AlarmStage } from '../pages/ward/mainScreen/alarmStage';
import { getFullFormName } from '../pages/ward/forms/vte/vte_data';
import { checkIfSectionSelected } from '../pages/ward/sepsis/sepsisUtil';
import { getConsultantCode, getConsultantName } from '../controls/agyle/clinician/clinitianUtils';
import { addNoteForPatient } from '../store/reducers/notes';

const getLocalDateTextReferrals = (obj, type, from) => {
    const local =
        from === 'requested'
            ? UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                  obj.ActivityServiceRequestDate_EmergencyCare,
                  obj.ActivityServiceRequestTime_EmergencyCare,
              )
            : UtilsMomentJS.getLocalDateFromUTCECDSFormat(obj.ReferredToServiceAssessmentDate, obj.ReferredToServiceAssessmentTime);

    if (local) {
        return type === 'date' ? local.format(config.defaultDateFormat) : local.format(config.defaultTimeFormat);
    }
    return '-';
};

/*
From Markdown

Title ## and italic * *
Subtitle ###
Sub-subtle ####
and normal text
 */
class Notes {
    static getConfirmDemographicsNote(user, date) {
        let note;
        note = `## *The patient demographics updated by ${user} ${date ? moment(date).format(config.defaultDateTimeFormat) : moment().format(config.defaultDateTimeFormat)}*\n`;
        return note;
    }

    static getDischargeMove() {
        let note;
        note = `## *The patient discharge completed*\n`;
        return note;
    }

    static getDischargeRemove() {
        let note;
        note = `## *The patient has been removed from ED*\n`;
        return note;
    }

    static getEmptyDiagnosisNote(body) {
        let note;
        note = `## *Diagnosis Not Required Confirmation*\n`;
        note += `### Diagnosis Not Required was set by ${body?.user}\n`;
        note += `* Reason: ${body?.reason ? body.reason?.__description : '-'} \n`;
        note += `* Info: ${body?.text || '-'}`;

        return note;
    }

    static getCFSNotRequired(body) {
        let note;
        note = `## *Clinical Frailty Score Not Required Confirmation*\n`;
        note += `### Clinical Frailty Score Not Required was set by ${body?.user}\n`;

        return note;
    }

    //  Observations notes
    static getObservationNote(fullName, type, displayValues, obs) {
        let note;
        note = `## *Observations*\n`;
        note += `### ${fullName}\n`;

        if (obs?.ambulanceObs) {
            note += `\n### **Observations prior to attendance**\n`;
        }

        note += `|Position|Value|\n|---:|:---:|\n`;
        _.forEach(displayValues, (nValue) => {
            if (nValue.type !== 'button' && (!nValue.obsTypes || _.includes(nValue.obsTypes, type))) {
                note += `|${nValue.description}|${printValue(obs, nValue)}|  \n`;
            }
        });

        note += `\n### Outcome\n`;
        let clinicalResponse = obs.clinicalResponse?.descriptions;
        if (obs.clinicalResponse?.__description) {
            const cr = obs.clinicalResponse?.__description.split('\n');
            clinicalResponse = cr.map((t) => t + '\n');
        }

        if (clinicalResponse) {
            _.forEach(clinicalResponse, (desc) => {
                note += `${desc.replaceAll('•', '* ').replaceAll('\n', '  ')}\n`;
            });
        }
        return note;
    }

    static getCancelObservationNote(obs, type, fullName) {
        let note;
        note = `## *Observations*\n`;
        note += `### ${fullName}\n`;
        note += `* Observation at ${moment(obs?.completedDate).format(config.defaultDateTimeFormat)} have been cancelled by ${obs.canceledByFullName}`;

        return note;
    }

    static getNextObservationStatusNote(nextObs) {
        let note;
        note = `## *Observations*\n`;
        note += `### Next observation status change\n`;
        note += `\n * **Next observation status set to**: ${_.startCase(nextObs.nextObsStatus)}\n`;
        if (nextObs.nextObsStatus !== obsStatuses.cancelled) {
            note += `* **Next observation date set to**: ${moment(nextObs.nextObsDate).format(config.defaultDateTimeFormat)}\n`;
        }
        note += `* **Reason**: ${nextObs.nextObsReason}\n`;

        return note;
    }

    static getObservationEditNote(obs, tests) {
        let note = '## *Observations*\n\n';
        note += `### Update Timestamp of Observations\n\n`;
        note += `Observation from ${moment(obs.examinationStart).format(config.defaultDateTimeFormat)}\n`;
        _.forEach(tests, (test) => {
            note += `* ${_.startCase(test.name)} Observation Date set to ${moment(test.observationDate).format(config.defaultDateTimeFormat)}\n`;
        });

        return note;
    }

    static getPainScoreNote(value) {
        let note;
        note = `## *Observations*\n`;
        note += `### Pain Score\n`;

        note += `|Position|Value|\n|---:|:---:|\n`;
        note += `|Pain Score|${value}|\n`;

        return note;
    }

    // Icons notes
    static getIconsNote(type, icon, action) {
        let note = type === 'task' ? '## *Task Action Information* \n\n' : '## *Speciality Action Information* \n\n';
        note += `* Action ${icon} is ${action}`;

        return note;
    }

    static getReferralsIconNote(otherService) {
        let note = '';
        note = '## *Referrals to Other Services*\n';
        if (!_.isEmpty(otherService.ReferralsToOtherServices)) {
            _.forEach(otherService.ReferralsToOtherServices, (element) => {
                note += `* ${element.ReferredToService_SnomedCt.__description}, Requested at ${getLocalDateTextReferrals(element, 'date', 'requested')} ${getLocalDateTextReferrals(element, 'time', 'requested')}, Assessment `;
                note +=
                    element.ReferredToServiceAssessmentDate && element.ReferredToServiceAssessmentTime
                        ? `at ${getLocalDateTextReferrals(element, 'date', 'assessment')} ${getLocalDateTextReferrals(element, 'time', 'assessment')}\n`
                        : 'not set.\n';
            });
        } else {
            note += 'Referrals to Other Services have been cleared.\n';
        }

        return note;
    }

    // Forms notes
    static getPaediatricSafeguardingNote(paediatricChecksForm, safeguardingForm, parentForm) {
        let note = '## *Paediatric Checks & Level* \n\n';
        if (parentForm) {
            note += `#### Submitted from parent form: ${parentForm}\n`;
        }

        note += '### Pediatric Checks \n\n';
        note += config.alternativePAEDSSafeguarding
            ? '\n #### I receive help from social welfare or another institution. \n'
            : '\n #### Do you have any help from social care or any other agency?\n';
        note += paediatricChecksForm.socialCare ? `* ${paediatricChecksForm.socialCare}\n` : '';
        if (config.alternativePAEDSSafeguarding) {
            if (paediatricChecksForm.socialCare === 'Yes') {
                note += `* Name Social Worker/Health Visitor: ${paediatricChecksForm.socialCareName} \n\n`;
            } else {
                note += `* Additional Info: ${paediatricChecksForm.socialCareInfo}\n\n`;
            }
        } else {
            note += `* Name Social Worker/Health Visitor: ${paediatricChecksForm.socialCareName} \n`;
            note += `* Contact: ${paediatricChecksForm.socialCareContact}\n`;
            note += `* Additional Info: ${paediatricChecksForm.socialCareInfo}\n\n`;
        }

        note += config.alternativePAEDSSafeguarding ? '#### The child is taken care of. \n' : '#### Is this a looked after child?\n';
        note += paediatricChecksForm.lookedAfterChild ? `* ${paediatricChecksForm.lookedAfterChild}\n` : '';
        note += `* Additional Info: ${paediatricChecksForm.lookedAfterChildInfo}\n\n`;

        note += config.alternativePAEDSSafeguarding
            ? '#### We routinely share information about all ED attendances with other health care providers. Sometimes, we also' +
              'share information about attendances with local Social Care Team. \n'
            : '#### We routinely share information about all ED attendances with other health care providers. Sometimes, we also \n' +
              '#### share information about attendances with local Social Care Team. Do you consent to us doing this?\n';
        if (config.alternativePAEDSSafeguarding) {
            if (paediatricChecksForm.agreement === 'Yes') {
                note += `* Stated to Family \n`;
                note += `* Additional Info: ${paediatricChecksForm.agreementInfo}\n\n`;
            } else {
                note += `* Not Stated to Family \n`;
                note += `* Additional Info: ${paediatricChecksForm.agreementInfo}\n\n`;
            }
        } else {
            note += paediatricChecksForm.agreement ? `* ${paediatricChecksForm.agreement}\n` : '';
            note += `* Additional Info: ${paediatricChecksForm.agreementInfo}\n\n`;
        }

        note += '#### Check any CPO and CPIS Information.\n';
        note += `* ${paediatricChecksForm.CPOCPIS}\n\n`;

        note += '#### Review number of attendances in last 12 months ED or other UEC\n';
        note += `* ${paediatricChecksForm.attendances}\n\n`;

        note += '### Safeguarding Level \n';
        note += `* ${safeguardingForm.level}\n\n`;

        if (safeguardingForm.level === 'amber' || safeguardingForm.level === 'red') {
            note += '#### Reason\n\n';
            note += `${safeguardingForm.reasons.reason}\n\n`;

            note += '#### Safeguarding\n\n';
            note += `|Description|Code|\n|:---|:---:|\n`;

            safeguardingForm.reasons.safeguarding.forEach((element) => {
                if (element.snomedDescription) {
                    note += `|${element.snomedDescription}|`;
                    note += `${element.snomedCode !== null ? element.snomedCode : '----'}|\n`;
                }
            });

            note += '### Action Taken\n';

            note += safeguardingForm.reasons.infoNurse ? '* &#9745; Informed Nurse in Charge\n' : '';
            note += safeguardingForm.reasons.trustSafeguarding ? '* &#9745; Discussed with: Trust Safeguarding Team\n' : '';
            note += safeguardingForm.reasons.refHealthVisitor ? '* &#9745; Referral to: Health Visitor/Early Help/School Nurse\n' : '';
            note += safeguardingForm.reasons.refCHaD ? '* &#9745; Referral to: CHaD\n' : '';
            note += safeguardingForm.reasons.refCAMHS ? '* &#9745; Referral to: CAMHS\n' : '';
            note += safeguardingForm.reasons.refOther ? '* &#9745; Referral to: Other\n' : '';
            note += safeguardingForm.reasons.refPolice ? '* &#9745; Referral to: Police\n' : '';
            note += safeguardingForm.reasons.additionalInfo ? '* &#9745; Additional Referral Information\n' : '';

            if (safeguardingForm.reasons.refOther) {
                note += '\n #### Referral to Other:\n\n';
                note += `${safeguardingForm.reasons.refOtherDescription}\n\n`;
            }
            if (safeguardingForm.reasons.additionalInfo) {
                note += '\n #### Additional Referral Information\n\n';
                note += `${safeguardingForm.reasons.additionalInfoDescription}\n\n`;
            }
        }
        return note;
    }

    static getClinicalConclusionNote(data, hospital, dod) {
        const checkStatus = (status) => {
            return status === 'Admitted' ? 'Ready to Admit' : status;
        };

        let note;
        note = '## *Clinical Conclusion*\n\n';
        //note += `* **Clinician**: ${data.who?.__description ? data.who.__description : "Not Set"}\n`;
        note += `* **Clinician**: ${data.who ? getConsultantName(data.who) : 'Not Set'}\n`;
        note += `* **Clinical Conclusion Date and Time**: ${data.when ? moment(data.when).format(config.defaultDateTimeFormat) : 'Not Set'}\n`;
        note += `* **Status** : ${data.status ? checkStatus(data.status) : 'Not Set'}\n`;
        note += data.status && data.status === 'Transfer' ? `* **Transfer To**: ${hospital}\n` : '';
        note +=
            data?.status === 'Deceased' && dod && config?.modules?.patient?.dodDateUpdateEnabled
                ? `* **Date of Death**: ${moment(dod).format(config.defaultDateTimeFormat)}\n`
                : '';
        note += config.clinicalConclusionNoteEnabled ? `* **Note**: ${data.note || '-'}` : ' ';

        return note;
    }

    static getClinicalCourseNote(clinicalCourse, patientType) {
        let note = '';
        note = `## ${patientType !== PATIENT_CONTEXT_TYPE.EDAU ? '*Message to GP / Plan & Advice Given*' : '*Message to GP*'} \n`;
        note += `* **Actions Given Status**: ${clinicalCourse.actionsGivenStatus ? clinicalCourse.actionsGivenStatus : '-'} \n`;
        note += `* **Actions Given**: ${clinicalCourse.actionsGiven ? clinicalCourse.actionsGiven : '-'} \n`;
        note += `* **Advice Given**: ${clinicalCourse.adviceGiven ? clinicalCourse.adviceGiven : '-'} \n`;
        if (patientType !== PATIENT_CONTEXT_TYPE.EDAU) {
            note += `* **Message to GP**: ${clinicalCourse.clinicalCourse ? clinicalCourse.clinicalCourse : '-'} \n`;
        }
        return note;
    }

    static getCSMContactTimeNote(form, sr) {
        const getBedStatus = (status) => {
            switch (status) {
                case 'req':
                    return 'Requested';
                case 'ready':
                case 'canceled':
                case 'allocated':
                    return _.startCase(status);
                default:
                    return 'Not Set';
            }
        };

        let note;
        note = '## *CSM Contact Time*\n\n';
        //note += `* **Clinician**: ${form.by ? form.by.__description : "Not Set"}\n`;
        note += `* **Clinician**: ${form.by ? getConsultantName(form.by) : 'Not Set'}\n`;
        note += `* **CSM Contact Date and Time**: ${form.when ? moment(form.when).format(config.defaultDateTimeFormat) : 'Not Set'}\n`;
        note += `* **Ward**: ${form.ward ? form.ward?.wardName : 'Not Set'}\n`;
        note += `* **Activity Treatment** : ${form.activityTreatment ? form.activityTreatment?.__description : 'Not Set'}\n`;
        note += `* **Bed Request**: ${getBedStatus(form.bed)}\n`;
        note += `* **Side Room**: ${sr ? 'Yes' : 'No'}\n`;

        return note;
    }

    static getVerificationOfDeathNote(objectToSave) {
        const checkRadioValue = (val) => {
            if (_.isBoolean(val)) return val ? 'Yes' : 'No';
            else if (_.isString(val)) return val === 'yes' ? 'Yes' : 'No';
            return 'Not Set';
        };

        const checkCheckboxOfExaminations = (val) => {
            return objectToSave?.absenceExamine?.[val] ? 'Checked' : 'Not checked';
        };

        let note;
        note = '## *Verification Of Death*\n\n';
        note += '### *Illustration of Requirements*\n';
        //note += `* **Name of person verifying death**: ${objectToSave?.clinicianName?.__description || 'Not Set'}\n`;
        note += `* **Name of person verifying death**: ${objectToSave?.clinicianName ? getConsultantName(objectToSave?.clinicianName) : 'Not Set'}\n`;
        note += `* **Grade of person verifying death**: ${objectToSave?.clinicianGrade?.name || 'Not Set'}\n`;
        note += `* **Location of patient**: ${objectToSave?.clinicianLocation?.wardName || 'Not Set'}\n`;
        note += `* **I looked after the patient while alive:**: ${checkRadioValue(objectToSave?.iLooked)}\n`;
        note += `* **I checked the patient's wristband**: ${checkRadioValue(objectToSave?.methodOfIdentification?.wristband)}\n`;
        note += `* **Those present**: ${objectToSave?.thosePresent || 'Not Set'}\n\n`;

        note += '### *Examination*\n';
        note += `* **Absence of a central pulse on palpitation for five minutes**: ${checkCheckboxOfExaminations('centralPulse')}\n`;
        note += `* **Absence of heart and breath sounds on auscultation for five minutes**: ${checkCheckboxOfExaminations('hearthBreathSounds')}\n`;
        note += `* **Absence of the pupillary responses to light**: ${checkCheckboxOfExaminations('pupilatoryResponses')}\n`;
        note += `* **Absence of the corneal reflexes**: ${checkCheckboxOfExaminations('cornealReflexes')}\n`;
        note += `* **Absence of any motor response to supra-orbital pressure**: ${checkCheckboxOfExaminations('motorResponse')}\n`;

        note += `* **Date of death**: ${objectToSave?.dateOfDeath ? moment(objectToSave?.dateOfDeath).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **Time of death**: ${objectToSave?.timeOfDeath ? moment(objectToSave?.timeOfDeath).format(config.defaultTimeFormat) : 'Not Set'}\n`;

        if (objectToSave?.declaration === 'dec') {
            note += `* **Declaration**: I declare that I have confirmed the identity of this patient and that death has occurred at the date and time given above\n\n`;
        } else {
            note += `* **Declaration**: No declaration made\n\n`;
        }

        note += '### *Cause of Death - Illustration of Requirements*\n';
        if (objectToSave?.coronerRef === 'suspect') {
            note += `* **Coroner Referral - Medical Cause of Death**: I have reason to suspect that this person’s death requires the Senior Coroner for Dorset to be notified\n`;
        } else {
            note += `* **I do not have reason to suspect that this person's death requires the Senior Coroner for Dorset to be noticed, and I feel the following is the medical cause of death**\n\n`;
            note += '#### *Proposed cause of death, pending Medical Examiner scrutiny*:\n';
            note += `* **Disease or Condition leading directly to death**: ${objectToSave?.causeOfDeath1 || '-'}\n`;
            note += `* **Other diseases or conditions If any, leading to 1a**: ${objectToSave?.causeOfDeath2 || '-'}\n`;
            note += `* **Other diseases or conditions If any, leading to 1b**: ${objectToSave?.causeOfDeath3 || '-'}\n`;
            note += `* **Other significant conditions contributing to death**: ${objectToSave?.causeOfDeath4 || '-'}\n`;
        }

        note += `* **Short narrative to explain the circumstances surrounding death, and why you feel the Coroner should be informed**: \n ${objectToSave?.circumstances || 'Not Set'}\n`;
        note += `* **Provide your preferred email address in case the Medical Examiner or Coroner’s Officer needs to contact you**: \n ${objectToSave?.clinicianEmail || 'Not Set'}\n`;
        return note;
    }

    static getFrailtyFormNote(objectToSave) {
        let note;

        const array = [
            'lives',
            'liveIn',
            'lift',
            'warden',
            'beenAdapted',
            'housebound',
            'carers',

            'whoProvideCare',
            'patient',
            'withHelpOf',

            'cognitive',
            'diagnosis',

            'health',
            'financial',

            'feeding',
            'meal',
            'shopping',
            'washing',
            'dressing',
            'toileting',
        ];
        function getBoolean(value) {
            return value ? 'Yes' : 'No';
        }
        function getException(key) {
            const exceptions = {
                lives: 'Lives',
                alone: 'Alone',
                withSpouseOrPartner: 'With spouse or partner',
                withFamily: 'With family',
                other: 'Other',
                liveIn: 'Live in a',
                houseWithStairs: 'House with stairs',
                flat: 'Flat',
                bungalow: 'Bungalow',
                careHome: 'Care home',
                nursingHome: 'Nursing home',
                otherLiveIn: 'Other',

                lift: `Lift:`,
                warden: `Is there a Warden?`,
                beenAdapted: `Has this been Adapted?`,
                housebound: `Housebound?`,
                carers: `Carer(s) in place?`,

                careFrequency: 'careFrequency',

                whoProvideCare: 'Who provide care?',
                formal: 'Formal',
                privatelyFunded: 'Privately funded',
                informal: 'Informal',

                patient: 'Patient / Patient is / Patient uses',
                independent: 'Independent',
                drives: 'Drives',
                furnitureWalk: 'Furniture walk',
                stick: 'Stick',
                frame: 'Frame',
                rollator: 'Rollator',
                mobilityScooter: 'Mobility scooter',
                wheelchair: 'Wheelchair',
                transfersOnly: 'Transfers only',

                withHelpOf: 'Transfer with help of',
                one: 'One',
                two: 'Two',
                hoist: 'Hoist',
                otherWithHelpOf: 'Other',
                patientOtherDetails: '',

                cognitive: `Cognitive impairment?`,
                diagnosis: `Diagnosis of Dementia?`,

                diagnosisDetails: '',
                health: 'Health',
                yesSeen: 'Yes - Original Document Seen',
                yesNotSeen: 'Yes - Documentation not Seen',
                financial: 'Financial',

                feeding: 'Feeding',
                meal: 'Meal',
                shopping: 'Shopping',
                washing: 'Washing',
                dressing: 'Dressing',
                toileting: 'Toileting',

                yes: 'Yes',
                no: 'No',
                na: 'N/A',
                unknown: 'Unknown',
            };
            return exceptions[key];
        }

        note = '## *Frailty Form*\n\n';

        for (let key of array) {
            if (_.isBoolean(objectToSave[key])) {
                const boolValue = getBoolean(objectToSave[key]);
                const toggleCheck = getException(key, objectToSave);
                const details = objectToSave[`${key}Details`] ? `**Details**: ${objectToSave[`${key}Details`]}` : '';
                note += `* **${toggleCheck}** ${boolValue} ${details}\n`;
            } else if (objectToSave[`${objectToSave[key]}Details`]) {
                const cos = getException(objectToSave[key])
                    ? `* **${getException(key, objectToSave)}:** ${getException(objectToSave[key])} **Details:**`
                    : ' ';
                note += `${cos} ${objectToSave[`${objectToSave[key]}Details`]}\n`;
            } else if (objectToSave[`${key}Details`]) {
                const cos = getException(objectToSave[key])
                    ? `* **${getException(key, objectToSave)}:** ${getException(objectToSave[key])} **Details:**`
                    : ' ';
                note += `${cos} ${objectToSave[`${key}Details`]}\n`;
            } else if (getException(objectToSave[key], objectToSave)) {
                const cos2 = `* **${getException(key, objectToSave)}**`;
                note += `${cos2} ${getException(objectToSave[key], objectToSave)}\n`;
            }
        }

        return note;
    }

    static getAbbeyPainScaleNote(objectToSave, cancelledEntry = false) {
        const getPainType = (val) => {
            const num = Number(val);
            const painMap = {
                0: 'None',
                1: 'Mild',
                2: 'Moderate',
                3: 'Severe',
            };
            return painMap[num] || '';
        };

        let note;

        note = '## *Abbey Pain Scale*\n\n';
        if (cancelledEntry) {
            note += `### Abbey Pain Scale Observation from ${moment(objectToSave.dateAndTime).format(config.defaultDateTimeFormat)} has been cancelled`;
        } else {
            note += '### *Patients Info*\n';
            note += `* **Unit No**: ${objectToSave?.unitNo || 'Not Set'}\n`;
            note += `* **Surame**: ${objectToSave?.familyName || 'Not Set'}\n`;
            note += `* **Name**: ${objectToSave?.firstName || 'Not Set'}\n`;
            note += `\n`;

            note += '### *Date*\n';
            note += `* **Inicials**: ${objectToSave?.initials1 || 'Not Set'}\n`;
            note += `* **Date and time**: ${objectToSave?.dateAndTime ? moment(objectToSave?.dateAndTime).format(config.defaultDateTimeFormat) : 'Not Set'}\n`;
            note += `\n`;

            note += '### *Questions*\n';
            note += `* **Vocalisation**: ${objectToSave?.vocalisation || 'Not Set'} ${objectToSave?.vocalisation ? getPainType(objectToSave?.vocalisation) : ''}\n`;
            note += `* **Facial expression**: ${objectToSave?.facialExpression || 'Not Set'} ${objectToSave?.facialExpression ? getPainType(objectToSave?.facialExpression) : ''}\n`;
            note += `* **Change in body language**: ${objectToSave?.changeInBody || 'Not Set'} ${objectToSave?.changeInBody ? getPainType(objectToSave?.changeInBody) : ''}\n`;
            note += `* **Behavioural**: ${objectToSave?.behavioural || 'Not Set'} ${objectToSave?.behavioural ? getPainType(objectToSave?.behavioural) : ''}\n`;
            note += `* **Physiological**: ${objectToSave?.physiological || 'Not Set'} ${objectToSave?.physiological ? getPainType(objectToSave?.physiological) : ''}\n`;
            note += `* **Physical**: ${objectToSave?.physical || 'Not Set'} ${objectToSave?.physical ? getPainType(objectToSave?.physical) : ''}\n`;
            note += `* **Total score**: ${objectToSave?.total || 'Not Set'}\n`;
            note += `\n`;

            note += '### *Type of pain*\n';
            note += `* **Type of pain**: ${objectToSave?.typeOfPain || 'Not Set'}\n`;
        }

        return note;
    }

    static getFrailtySDECNote(objectToSave) {
        const groups = [
            {
                title: 'Visit Information',
                fields: [
                    { label: 'Attendance Date', key: 'attendanceDate', type: 'date' },
                    { label: 'Attendance Time', key: 'attendanceTime', type: 'time' },
                    { label: 'No of ED attendance', key: 'noOfEDAttendance' },
                    { label: 'No of ED admission', key: 'noOfEDAdmission' },
                    { label: 'Advanced care plan', key: 'advancedCarePlan', type: 'radio' },
                    { label: 'Assessment initiated by', key: 'assessmentInitiatedBy' },
                ],
            },
            {
                title: 'Lasting Power Of Attorney',
                fields: [
                    { label: 'Lasting Power Of Attorney', key: 'lastingPowerOfAttorney', type: 'radio' },
                    { label: 'Comment', key: 'lastingPowerOfAttorneyText' },
                ],
            },
            {
                title: 'Complains',
                fields: [
                    { label: 'Presenting Complaint', key: 'presentingComplaint' },
                    { label: 'History Of Presenting Complaint', key: 'historyOfPresentingComplaint' },
                    { label: 'Past Medical History', key: 'pastMedicalHistory' },
                    { label: 'Allergies', key: 'allergies' },
                    { label: 'Medication', key: 'medication' },
                ],
            },
            {
                title: 'Administration',
                fields: [
                    { label: 'Administered medication', key: 'administrationChooseItem', type: 'object' },
                    { label: 'Comment', key: 'administrationText' },
                    { label: 'Concordance', key: 'administrationConcordance' },
                ],
            },
            {
                title: 'Falls',
                fields: [
                    { label: 'No falls in past year', key: 'fallsNoInpastYear', type: 'objectDescription' },
                    { label: 'Description Of Falls', key: 'descriptionOfFalls' },
                    { label: 'Symptoms - Dizziness', key: 'symptoms.dizziness', type: 'checkbox' },
                    { label: 'Symptoms - Sweat', key: 'symptoms.sweat', type: 'checkbox' },
                    { label: 'Symptoms - Palpitations', key: 'symptoms.palpitations', type: 'checkbox' },
                    { label: 'Symptoms - Loss', key: 'symptoms.loss', type: 'checkbox' },
                ],
            },
            {
                title: 'Osteoporosis',
                fields: [
                    { label: 'History Of Fractures', key: 'osteoporosisHistoryOfFractures' },
                    { label: 'Patient On Bone Protection', key: 'patientOnBoneProtection' },
                    { label: 'Comment', key: 'patientOnBoneProtectionText' },
                    {
                        label: 'Patient details - Has history of fractures',
                        key: 'patientRadioDetails.historyOfFractures',
                        type: 'checkbox',
                    },
                    { label: 'Patient details - Risk factors present', key: 'patientRadioDetails.riskFactors', type: 'checkbox' },
                    { label: 'Patient details - Not on bone protection', key: 'patientRadioDetails.notOnBone', type: 'checkbox' },
                ],
            },
            {
                title: 'Mood and Memory',
                fields: [
                    { label: 'Diagnosis Of Dementia', key: 'diagnosisOfDementia' },
                    { label: 'Further Details', key: 'furtherDetails' },
                    { label: 'Alertness', key: 'alertness' },
                    { label: 'AMT4', key: 'delirium4at' },
                    { label: 'Attention', key: 'attention' },
                    { label: 'Fluctuation', key: 'fluctuation' },
                    { label: 'Total', key: 'total' },
                    { label: 'Mood', key: 'mood' },
                ],
            },
            {
                title: 'Social History',
                fields: [
                    { label: 'Accomodation', key: 'accomodation', type: 'object' },
                    { label: 'Comment', key: 'accomodationText' },
                    { label: 'Stairs', key: 'stairs', type: 'radio' },
                    { label: 'Toilet', key: 'toilet', type: 'object' },
                    { label: 'Bathroom', key: 'bathroom', type: 'object' },
                    { label: 'Lives with', key: 'livesWith', type: 'object' },
                    { label: 'Social History Comment', key: 'socialHistoryText' },
                ],
            },
            {
                title: 'Mobility And Transfers',
                fields: [
                    { label: 'Life space', key: 'lifeSpace' },
                    { label: 'Walking aid - None', key: 'walkingAid.none', type: 'checkbox' },
                    { label: 'Walking aid - Stick', key: 'walkingAid.stick', type: 'checkbox' },
                    { label: 'Walking aid - Crutches', key: 'walkingAid.crutches', type: 'checkbox' },
                    { label: 'Walking aid - Wheeled Zimmer Frame/Walker', key: 'walkingAid.wheeled', type: 'checkbox' },
                    { label: 'Walking aid - Ross Return/Sara Stedy', key: 'walkingAid.ross', type: 'checkbox' },
                    { label: 'Walking aid - Stand aid', key: 'walkingAid.stand', type: 'checkbox' },
                    { label: 'Walking aid - Hoist', key: 'walkingAid.hoist', type: 'checkbox' },
                    { label: 'Walking aid - Bed Bound', key: 'walkingAid.bed', type: 'checkbox' },
                    { label: 'Bed type', key: 'bedType', type: 'object' },
                    { label: 'Equipment', key: 'equipment' },
                    { label: 'Bed transfer issues', key: 'bedTransferIssues' },
                    { label: 'Chair type', key: 'chairType', type: 'object' },
                    { label: 'Difficulties', key: 'difficulties' },
                    { label: 'Toilet equipment', key: 'toiletEquipment' },
                ],
            },
            {
                title: 'Activitie Of Daily Living',
                fields: [
                    { label: 'Carers', key: 'carers' },
                    { label: 'Agency', key: 'agency' },
                    { label: 'Wash and dressing', key: 'washAndDressing' },
                    { label: 'Toileting including continence', key: 'toiletingIncludingContinence' },
                    { label: 'Meals', key: 'meals' },
                    { label: 'Shopping', key: 'shopping' },
                    { label: 'Domestic tasks', key: 'domesticTasks' },
                    { label: 'Roles and occupation', key: 'rolesAndOccupation' },
                ],
            },
            {
                title: 'Additional Information',
                fields: [
                    { label: 'Care line', key: 'careLine', type: 'radio' },
                    { label: 'Key safe', key: 'keySafe', type: 'radio' },
                    { label: 'Code', key: 'code' },
                    { label: 'Pressure areas', key: 'pressureAreas' },
                    { label: 'Mattress', key: 'mattress' },
                    { label: 'Cushion', key: 'cushion' },
                ],
            },
            {
                title: 'Communication',
                fields: [
                    { label: 'Hearing', key: 'hearing' },
                    { label: 'Vision', key: 'vision' },
                    { label: 'Speech and language', key: 'speechAndLanguage' },
                    { label: 'Preferred method of communication', key: 'preferredMethodOfCommunication' },
                ],
            },
            {
                title: 'Diet, Nutrition And Swallowing',
                fields: [
                    { label: 'Height', key: 'height' },
                    { label: 'Weight', key: 'weight' },
                    { label: 'BMI', key: 'bmi' },
                    { label: 'Signs of visible weight loss', key: 'signsOfVisibleWeightLoss' },
                    { label: 'Reduced appetite', key: 'reducedAppetite', type: 'radio' },
                    { label: 'Comment', key: 'reducedAppetiteText' },
                    { label: 'Reported swallowing difficulty', key: 'reportedSwallowingDifficulty', type: 'radio' },
                    { label: 'Comment', key: 'reportedSwallowingDifficultyText' },
                    { label: 'Food consistency', key: 'foodConsistency', type: 'object' },
                    { label: 'Fluids', key: 'fluids', type: 'object' },
                ],
            },
            {
                title: 'Physical Examination',
                fields: [
                    { label: 'Physical examination - Jaundice', key: 'physicalExamination.jaundice', type: 'checkbox' },
                    { label: 'Physical examination - Anaemia', key: 'physicalExamination.anaemia', type: 'checkbox' },
                    { label: 'Physical examination - Clubbing', key: 'physicalExamination.clubbing', type: 'checkbox' },
                    { label: 'Physical examination - Cyanosis', key: 'physicalExamination.cyanosis', type: 'checkbox' },
                    { label: 'Physical examination - Oedma', key: 'physicalExamination.oedma', type: 'checkbox' },
                    { label: 'Physical examination - Lyphadenopathy', key: 'physicalExamination.lyphadenopathy', type: 'checkbox' },
                    { label: 'General comments', key: 'generalComments' },
                ],
            },
            {
                title: 'Systems',
                fields: [
                    { label: 'Cardiovascular', key: 'cardiovascular' },
                    { label: 'Respiratory', key: 'respiratoryText' },
                    { label: 'Gastrointestinal', key: 'gastrointestinalText' },
                    { label: 'Musculoskeletal', key: 'musculoskeletalText' },
                    { label: 'Neurology', key: 'neurologyText' },
                    { label: 'Blood results', key: 'bloodResultsText' },
                ],
            },
            {
                title: 'Problems',
                fields: [
                    { label: "Patient's Perceived Problems", key: 'patientsPerceivedProblems' },
                    { label: "Carer's Perceived Problems", key: 'carersPerceivedProblems' },
                    { label: 'Problem List', key: 'problemList' },
                    { label: 'Treatment Plan', key: 'treatmentPlanText' },
                ],
            },
        ];

        const radioMap = {
            yes: 'Yes',
            no: 'No',
            copd: 'COPD',
            immobility: 'Immobility',
            history: 'History of falls',
            smoker: 'Smoker',
            hypogonadism: 'Hypogonadism',
            endocrine: 'Endocrine conditions',
            malabsorption: 'Malabsorption conditions',
            bmi: 'BMI less than 18.5kg/m2',
            previousOsteoporotic: 'Previous osteoporotic fragility fracture',
            parentalHip: 'Parental hip fracture',
            oralCorticosteroids: 'Frequent oral corticosteroids',
            alcohol: 'Alcohol - over 14 units per week',
            chronicKidney: 'Chronic kidney disease',
            chronicLiver: 'Chronic liver failure',
            hypogonadismMenopause: 'Hypogonadism including untreated premature menopause',
            rheumatoid: 'Rheumatoid arthritis or other inflammatory arthropathies',
            lift: 'Lift',
            stairlift: 'Stairlift',
            bedDownstairs: 'Bed downstairs',
        };

        const getNestedValue = (obj, path) => {
            return path.split('.').reduce((acc, part) => acc?.[part], obj);
        };

        const getValue = (label, key, type) => {
            const value = getNestedValue(objectToSave, key);

            const formattedValue = {
                radio: () => radioMap[value] || 'Not Set',
                checkbox: () => (value === true ? 'Checked' : 'Not checked'),
                object: () => value?.__description || 'Not Set',
                objectDescription: () => value?.description || 'Not Set',
                date: () => (value ? moment(value).format(config.defaultDateFormat) : 'Not Set'),
                time: () => (value ? moment(value).format(config.defaultTimeFormat) : 'Not Set'),
                default: () => value || 'Not Set',
            };

            const output = (formattedValue[type] || formattedValue.default)();
            return `* **${label}**: ${output}`;
        };

        let note = '';
        note = '## *Frailty SDEC Comprehensive Geriatric Assessment*\n\n';

        groups.forEach((group, index) => {
            if (index !== 0) note += '\n';
            note += `### *${group.title}*\n`;

            group.fields.forEach(({ label, key, type }, i) => {
                const value = getValue(label, key, type);
                const isLastField = i === group.fields.length - 1;
                const newline = isLastField ? '\n\n' : '\n';
                note += `${value}${newline}`;
            });
        });

        return note;
    }

    static getBedRequestNote(data) {
        function getStatus(val, nullState, noAsNull) {
            if (typeof val === 'string') return val;
            else {
                switch (val) {
                    case true:
                        return 'Yes';
                    case false:
                        return nullState || noAsNull ? 'No' : 'N/R';
                    default:
                        return noAsNull ? 'No' : 'N/R';
                }
            }
        }

        function getGroupDataTable(field, label) {
            let text = '';
            text += `|${label}|${getStatus(data[field], false)}`;
            //text += `|${data[`${field}Who`] ? data[`${field}Who`]?.__description : "Not Set"}`;
            text += `|${data[`${field}Who`] ? getConsultantName(data[`${field}Who`]) : 'Not Set'}`;
            text += `|${data[`${field}When`] ? moment(data[`${field}When`]).format(config.defaultDateTimeFormat) : 'Not Set'}`;
            text += `|${data[`${field}Note`] ? data[`${field}Note`] : '-'}|\n`;
            return text;
        }

        function getGroupData(field, label, nullState, noAsNull) {
            let text = `* **${label}**: ${getStatus(data[field], nullState, noAsNull)} | `;
            text += `**Comment**: ${data[`${field}Note`] ? data[`${field}Note`] : '-'}\n`;
            return text;
        }

        let note;
        note = '## *Bed Request*\n\n';
        note += `### *Bed status*:\n`;
        note += `|Type|Status|Clinician|Date and Time|Comment|\n|:---|:---:|:---:|:---:|:---:|\n`;
        note += getGroupDataTable('requested', 'Requested');
        note += getGroupDataTable('allocated', 'Allocated');
        note += getGroupDataTable('ready', 'Ready');
        note += getGroupDataTable('canceled', 'Canceled');
        note += '\n\n';

        note += `* **Reason for Admissions**: ${data.reasonForAdmissions ? data.reasonForAdmissions : '-'}\n`;
        note += getGroupData('sideRoom', 'Side Room', false);
        note += getGroupData('patientPost', 'Patient Post Take Complete', false, true);
        note += `* **ECDS Activity Treatment** : ${data.activityTreatment ? data.activityTreatment?.__description : 'Not Set'}\n`;

        if (config?.modules?.forms?.bedBooking?.enableAnotherDestination) {
            note += getGroupData('anotherDestination', 'Site bed is requested at', false);
        }
        note += `* **Ward**: ${data.ward ? data.ward?.wardName : 'Not Set'}\n`;
        note += getGroupData('withinHour', 'Within an hour', true);
        note += getGroupData('onHoldWard', 'On Hold Ward', false, true);
        return note;
    }

    static getBedRequestExternalNote(data, formClose) {
        function getStatus(val, nullState, noAsNull) {
            if (typeof val === 'string') return val;
            else {
                switch (val) {
                    case true:
                        return 'Yes';
                    case false:
                        return nullState || noAsNull ? 'No' : 'N/R';
                    default:
                        return noAsNull ? 'No' : 'N/R';
                }
            }
        }

        function getGroupDataTable(field, label) {
            let text = '';
            text += `|${label}|${getStatus(data[field]?.checked, false, true)}`;
            // text += `|${(data[field]?.who ? data[field]?.who?.__description : data[field]?.user) || '-'}`;
            text += `|${(data[field]?.who ? getConsultantName(data[field]?.who) : data[field]?.user) || '-'}`;
            if (data[field]?.when || data[field]?.timestamp) {
                text += `|${data[field]?.when ? moment(data[field]?.when).format(config.defaultDateTimeFormat) : moment(data[field]?.timestamp).format(config.defaultDateTimeFormat)}`;
            } else {
                text += '|-';
            }
            text += `|${data[field]?.note ? data[field]?.note : '-'}|\n`;
            return text;
        }

        function getGroupData(field, label, nullState, noAsNull) {
            let text = `* **${label}**: ${getStatus(data[field]?.checked, nullState, noAsNull)} | `;
            text += `**Comment**: ${data[field]?.note || '-'}\n`;
            return text;
        }

        function getWard() {
            if (data?.destination?.wardName) {
                return `Ward: ${data?.destination?.wardName}, Bay: ${data?.destination?.bayName || '-'}, Space: ${data?.destination?.bedName || '-'}`;
            }
            return 'Not Set';
        }

        let note;
        note = '## *Bed Request*\n\n';
        if (formClose) {
            note += `## **Bed Request closed**\n\n`;
        }

        note += `### *Bed status*:\n`;
        note += `|Type|Status|Clinician|Date and Time|Comment|\n|:---|:---:|:---:|:---:|:---:|\n`;
        note += getGroupDataTable('requested', 'Requested');
        note += getGroupDataTable('requestedAcknowledge', 'Requested Acknowledge');
        note += getGroupDataTable('allocated', 'Allocated');
        note += getGroupDataTable('ready', 'Patient Ready');
        note += getGroupDataTable('canceled', 'Cancelled');
        note += getGroupDataTable('canceledAcknowledge', 'Cancelled Acknowledge');
        note += '\n\n';

        // note += `* **ECDS Activity Treatment** : ${data.activityTreatment?.__description || 'Not Set'}\n`;
        note += `* **Specialty Requested** : ${data.specialty?.__description || 'Not Set'}\n`;
        note += `* **Reason for Admissions**: ${data.reasonForAdmissions || '-'}\n`;
        // note += getGroupData('onHoldWard', 'On Hold Ward', false, true);
        note += getGroupData('sideRoom', 'Side Room', false);
        if (data?.otherData?.sideRoomReason?.__description) {
            note += `* **Side Room Reason**: ${data?.otherData?.sideRoomReason?.__description || '-'}\n`;
        }
        if (data?.otherData?.sideRoomReason?.showText) {
            note += `* **Side Room Reason Note**: ${data?.otherData?.sideRoomReasonText || '-'}\n`;
        }
        note += getGroupData('ipc', 'IPC requirements', false);

        if (!_.isEmpty(data?.otherData?.bedOptions)) {
            note += `* **Bed Requirements Equipment**: ${data?.otherData?.bedOptions.map((opt) => opt.__description).join(', ') || '-'}\n`;
        }
        if (data?.otherData?.requirements) {
            note += `* **Special Bed Requirements**: ${data?.otherData?.requirements || '-'}\n`;
        }
        // note += getGroupData('patientPost', 'Patient Post Take Complete', false, true);
        // note += getGroupData('withinHour', 'Within an hour', true);
        note += `* **Destination**: ${getWard()}\n`;
        return note;
    }

    static getDeliriumNote(values, totalScore, lastScore, outcome, parentForm) {
        let note = '';
        note = '## *4AT Assessment Test For Delirium & Cognitive Impairment*\n';
        if (parentForm) {
            note += `#### Submitted from parent form: ${parentForm}\n`;
        }
        note += `* **Category 1**: ${values?.cat1?.text ? values?.cat1?.text : 'Not selected'}, Score: ${_.isNumber(values?.cat1?.score) ? values?.cat1?.score : '-'}\n`;
        note += `* **Category 2**: ${values?.cat2?.text ? values?.cat2?.text : 'Not selected'}, Score: ${_.isNumber(values?.cat2?.score) ? values?.cat2?.score : '-'}\n`;
        note += `* **Category 3**: ${values?.cat3?.text ? values?.cat3?.text : 'Not selected'}, Score: ${_.isNumber(values?.cat3?.score) ? values?.cat3?.score : '-'}\n`;
        note += `* **Category 4**: ${values?.cat4?.text ? values?.cat4?.text : 'Not selected'}, Score: ${_.isNumber(values?.cat4?.score) ? values?.cat4?.score : '-'}\n\n`;
        note += `**Total Score**: ${totalScore} \n`;
        note += lastScore
            ? `Trend compared to the last entry: ${lastScore > totalScore ? '↓ \n' : lastScore === totalScore ? '- \n' : '↑ \n'}`
            : '';
        note += `### Outcome: ${outcome}`;
        return note;
    }

    static getHouseholdNote(firstVersion, mainHousehold) {
        let note = '';
        if (!_.isEqual(firstVersion, mainHousehold)) {
            note = '## *Household Information*\n\n';
            _.each(mainHousehold, (element, index) => {
                note += `### Household ${index + 1}:\n`;
                note += element.householdInformation ? `* **Household Additional Information**: ${element.householdInformation}\n\n` : '';
                if (!_.isEmpty(element.householdPeople)) {
                    note += `|Person Name|Parental Responsibility|Age|Relationship|Contact Details|\n|:---|:---:|:---:|:---:|:---:|\n`;
                    _.forEach(element.householdPeople, (people) => {
                        note += `|${people.name}|${people.parentalResponsibility ? 'Yes' : 'No'}|${people.age}|${people.relationship}|${people.contact}|\n`;
                    });
                }
            });
        }
        return note;
    }

    static getReferralReviewNote(objectToSave) {
        let note;
        note = '## *Referral review*\n\n';

        if (!objectToSave.hasSR) {
            note += '### *Allergy/Intolerance:*\n';
            note += `* **Allergy/Intolerance for:** ${objectToSave?.allergyIntoleranceFor ? objectToSave?.allergyIntoleranceFor : '-'}\n`;
            note += `* **Clinical Status:** ${objectToSave?.clinicalStatus ? objectToSave?.clinicalStatus : '-'}\n`;
            note += `* **Last updated:** ${objectToSave?.lastUpdated ? objectToSave?.lastUpdated : '-'}\n`;
            note += `* **Verification status:** ${objectToSave?.verificationStatus ? objectToSave?.verificationStatus : '-'}\n`;
            note += `* **Category:** ${objectToSave?.allergyIntoleranceCategory ? objectToSave?.allergyIntoleranceCategory : '-'}\n`;
            note += `* **Reaction Severity:** ${objectToSave?.reactionSeverity ? objectToSave?.reactionSeverity : '-'}\n`;
            note += `* **Reaction Manifestation:** ${objectToSave?.reactionManifestation ? objectToSave?.reactionManifestation : '-'}\n\n`;
        }
        note += '### *Care Plan:*\n';
        if (!objectToSave.hasSR) {
            note += `* **Intent:** ${objectToSave?.carePlanIntent ? objectToSave?.carePlanIntent : '-'}\n`;
            note += `* **Status:** ${objectToSave?.carePlanStatus ? objectToSave?.carePlanStatus : '-'}\n`;
            note += `* **Last updated:** ${objectToSave?.carePlanLastUpdated ? objectToSave?.carePlanLastUpdated : '-'}\n`;
            note += `* **Pathways Assessment SD Code:** ${objectToSave?.pathwaysAssessmentSDCode ? objectToSave?.pathwaysAssessmentSDCode : '-'}\n`;
            note += `* **Pathways Assessment DX Code:** ${objectToSave?.pathwaysAssessmentDXCode ? objectToSave?.pathwaysAssessmentDXCode : '-'}\n`;
            note += `* **Presenting Complaint - selected Pathway or Template:** ${objectToSave?.presentingComplaintselectedPathwayTemplate ? objectToSave?.presentingComplaintselectedPathwayTemplate : '-'}\n`;
            note += `* **Pathways Assessment SG Code - Chief Complaint:** ${objectToSave?.pathwaysAssessmentSGCodeChiefComplaint ? objectToSave?.pathwaysAssessmentSGCodeChiefComplaint : '-'}\n`;
            note += `* **Pathway Assessment:** ${objectToSave?.pathwayAssessment ? objectToSave?.pathwayAssessment : '-'}\n`;
            note += `* **Person expectations and wishes:** ${objectToSave?.personExpectationsAndWishes ? objectToSave?.personExpectationsAndWishes : '-'}\n`;
            note += `* **Safeguarding Concerns Flag:** ${objectToSave?.safeguardingConcernsFlag ? objectToSave?.safeguardingConcernsFlag : '-'}\n`;
            note += `* **Additional Information Sources:** ${objectToSave?.carePlanAdditionalInformationSources ? objectToSave?.carePlanAdditionalInformationSources : '-'}\n\n`;
        }
        if (objectToSave.hasSR) {
            note += `* **OUTCOME:** ${objectToSave?.streamingType ? objectToSave?.streamingType : '-'}\n`;
            note += `* **ECDS Acuity Score:** ${objectToSave?.ecdsAcuityScore ? objectToSave?.ecdsAcuityScore : '-'}\n`;
            note += `* **Last updated:** ${objectToSave?.carePlanLastUpdated ? objectToSave?.carePlanLastUpdated : '-'}\n`;
            note += `* **Safeguarding:** ${objectToSave?.safeguarding ? objectToSave?.safeguarding : '-'}\n`;
            note += `* **Pathways Assessment SD Code (${objectToSave?.pathwaysAssessmentSDCode ? objectToSave?.pathwaysAssessmentSDCode : '-'}):**  ${objectToSave?.pathwaysAssessmentSD ? objectToSave?.pathwaysAssessmentSD : '-'} \n`;
            note += `* **Pathways Assessment SG Code - Chief Complaint (${objectToSave?.pathwaysAssessmentSGCodeChiefComplaint ? objectToSave?.pathwaysAssessmentSGCodeChiefComplaint : '-'}):** ${objectToSave?.pathwaysAssessmentSGChiefComplaint ? objectToSave?.pathwaysAssessmentSGChiefComplaint : '-'}\n`;
            note += `* **Intent:** ${objectToSave?.carePlanIntent ? objectToSave?.carePlanIntent : '-'}\n`;
            note += `* **Pathways Assessment DX Code (${objectToSave?.pathwaysAssessmentDXCode ? objectToSave?.pathwaysAssessmentDXCode : '-'}):** ${objectToSave?.pathwaysAssessmentDX ? objectToSave?.pathwaysAssessmentDX : '-'}\n`;
            note += `* **Presenting Complaint - selected Pathway or Template Code (${objectToSave?.presentingComplaintselectedPathwayTemplateCode ? objectToSave?.presentingComplaintselectedPathwayTemplateCode : '-'}):** ${objectToSave?.presentingComplaintselectedPathwayTemplate ? objectToSave?.presentingComplaintselectedPathwayTemplate : '-'}\n`;
            note += `* **Status:** ${objectToSave?.carePlanStatus ? objectToSave?.carePlanStatus : '-'}\n`;
            note += `* **Pathway Assessment:** ${objectToSave?.pathwayAssessment ? objectToSave?.pathwayAssessment : '-'}\n`;
            note += `* **Consultation Summary:** ${objectToSave?.consultationSummary ? objectToSave?.consultationSummary : '-'}\n\n`;
        }

        if (!objectToSave.hasSR) {
            note += '### *Consent:*\n';
            note += `* **Date and Time:** ${objectToSave?.consentDateAndTime ? objectToSave?.consentDateAndTime : '-'}\n`;
            note += `* **Status:** ${objectToSave?.consentStatus ? objectToSave?.consentStatus : '-'}\n\n`;

            note += '### *Medication Statement:*\n';
            note += `* **Date Asserted:** ${objectToSave?.medicationStatementDateAsserted ? objectToSave?.medicationStatementDateAsserted : '-'}\n`;
            note += `* **Effective Date Time:** ${objectToSave?.effectiveDateTime ? objectToSave?.effectiveDateTime : '-'}\n`;
            note += `* **Category:** ${objectToSave?.medicationStatementCategory ? objectToSave?.medicationStatementCategory : '-'}\n`;
            note += `* **Medication:** ${objectToSave?.medication ? objectToSave?.medication : '-'}\n`;
            note += `* **Dosage:** ${objectToSave?.dosage ? objectToSave?.dosage : '-'}\n`;
            note += `* **Dosage Route:** ${objectToSave?.dosageRoute ? objectToSave?.dosageRoute : '-'}\n`;
            note += `* **Dosage Frequency:** ${objectToSave?.dosageFrequency ? objectToSave?.dosageFrequency : '-'}\n`;
            note += `* **Duration:** ${objectToSave?.duration ? objectToSave?.duration : '-'}\n`;
            note += `* **Reason Code:** ${objectToSave?.reasonCode ? objectToSave?.reasonCode : '-'}\n`;
            note += `* **Symptom:** ${objectToSave?.symptom ? objectToSave?.symptom : '-'}\n\n`;

            note += '### *Observation:*\n';
            note += `* **Date Asserted:** ${objectToSave?.observationDateAsserted ? objectToSave?.observationDateAsserted : '-'}\n`;
            note += `* **Status:** ${objectToSave?.observationStatus ? objectToSave?.observationStatus : '-'}\n`;
            note += `* **Expectations and wishes:** ${objectToSave?.expectationsAndWishes ? objectToSave?.expectationsAndWishes : '-'}\n`;
            note += `* **Note Author:** ${objectToSave?.observationNoteAuthor ? objectToSave?.observationNoteAuthor : '-'}\n`;
            note += `* **Note:** ${objectToSave?.observationNote ? objectToSave?.observationNote : '-'}\n\n`;

            note += '### *Organization:*\n';
            note += `* **Provided By:** ${objectToSave?.providedBy ? objectToSave?.providedBy : '-'}\n\n`;

            note += '### *Practitioner:*\n';
            note += `* **ID Number:** ${objectToSave?.idNumber ? objectToSave?.idNumber : '-'}\n`;
            note += `* **Name:** ${objectToSave?.name ? objectToSave?.name : '-'}\n`;
            note += `* **Surname:** ${objectToSave?.surname ? objectToSave?.surname : '-'}\n`;
            note += `* **Contact Method:** ${objectToSave?.contactMethod ? objectToSave?.contactMethod : '-'}\n`;
            note += `* **Contact Type:** ${objectToSave?.contactType ? objectToSave?.contactType : '-'}\n`;
            note += `* **Telephone Number:** ${objectToSave?.telephoneNumber ? objectToSave?.telephoneNumber : '-'}\n\n`;

            note += '### *Practitioner Role:*\n';
            note += `* **Role:** ${objectToSave?.role ? objectToSave?.role : '-'}\n`;
            note += `* **Speciality:** ${objectToSave?.speciality ? objectToSave?.speciality : '-'}\n\n`;

            note += '### *Questionnaire Response:*\n';
            note += `* **Additional Information Sources:** ${objectToSave?.questionnaireResponseAdditionalInformationSources ? objectToSave?.questionnaireResponseAdditionalInformationSources : '-'}\n`;
            note += `* **Additional Sources:** ${objectToSave?.additionalSources ? objectToSave?.additionalSources : '-'}\n`;
            note += `* **Additional Lookup Sources:** ${objectToSave?.additionalLookupSources ? objectToSave?.additionalLookupSources : '-'}\n`;
            note += `* **Source Name:** ${objectToSave?.sourceName ? objectToSave?.sourceName : '-'}\n`;
            note += `* **Source:** ${objectToSave?.source ? objectToSave?.source : '-'}\n`;
            note += `* **Source Name:** ${objectToSave?.sourceName2 ? objectToSave?.sourceName2 : '-'}\n`;
            note += `* **SPN Title:** ${objectToSave?.spnTitle ? objectToSave?.spnTitle : '-'}\n`;
            note += `* **SPN Details:** ${objectToSave?.spnDetails ? objectToSave?.spnDetails : '-'}\n`;
            note += `* **SPN Active Date:** ${objectToSave?.spnActiveDate ? objectToSave?.spnActiveDate : '-'}\n`;
            note += `* **SPN Review Date:** ${objectToSave?.spnReviewDate ? objectToSave?.spnReviewDate : '-'}\n\n`;

            note += '### *Service Request:*\n';
            note += `* **Intent:** ${objectToSave?.serviceRequestIntent ? objectToSave?.serviceRequestIntent : '-'}\n`;
            note += `* **Status:** ${objectToSave?.serviceRequestStatus ? objectToSave?.serviceRequestStatus : '-'}\n`;
            note += `* **Rejected Services - Details:** ${objectToSave?.rejectedServicesDetails ? objectToSave?.rejectedServicesDetails : '-'}\n`;
            note += `* **Rejected Services - Flags:** ${objectToSave?.rejectedServicesFlags ? objectToSave?.rejectedServicesFlags : '-'}\n`;
            note += `* **Category:** ${objectToSave?.serviceRequestCategory ? objectToSave?.serviceRequestCategory : '-'}\n`;
            note += `* **Authored On:** ${objectToSave?.authoredOn ? objectToSave?.authoredOn : '-'}\n`;
            note += `* **Occurrence Period:** ${objectToSave?.occurrencePeriod ? objectToSave?.occurrencePeriod : '-'}\n`;
            note += `* **Patient Instruction:** ${objectToSave?.patientInstruction ? objectToSave?.patientInstruction : '-'}\n`;
        }
        return note;
    }

    static getFitnessForWorkNoteDuplicate(fitnessForWork) {
        let note;
        note = '## *eMed3*\n\n';
        note += '### *The eMed3 form has been duplicated:*\n';
        note += `* **Original creation date:** ${fitnessForWork?.assessedCase ? moment(fitnessForWork?.assessedCase).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **Unique Id:** ${fitnessForWork?.uniqueId ? fitnessForWork?.uniqueId : 'Not Set'}\n`;
        return note;
    }

    static getFitnessForWorkNote(fitnessForWork) {
        const checkCheckboxOfExaminations = (val) => {
            return fitnessForWork?.benefits?.[val] ? '`&#x2714;`' : '`-`';
        };

        let note;
        note = '## *eMed3*\n\n';
        note += '### *Fitness for work*\n';
        note += `* **I assessed your case on:** ${fitnessForWork?.assessedCase ? moment(fitnessForWork?.assessedCase).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **and because of the following condition(s):** ${fitnessForWork?.assessedCaseDetails ? fitnessForWork?.assessedCaseDetails : 'Not Set'}\n`;
        note += `* **Advise:** ${fitnessForWork?.advise === 'notFit' ? 'aaa' : fitnessForWork?.advise === 'maybeFit' ? 'bbb' : 'Not Set'}\n`;

        note += `* **If available, and with your employer's agreement, you may benefit from:**\n`;
        note += `a phased return to work(${checkCheckboxOfExaminations('phasedReturn')}) altered hours(${checkCheckboxOfExaminations('alteredHours')}) amended duties(${checkCheckboxOfExaminations('amendedDuties')}) workplace adaptations(${checkCheckboxOfExaminations('workplaceAdaptations')})\n`;

        note += `* **Comments, including functional effects of your condition(s):** ${fitnessForWork?.benefitsDetails ? fitnessForWork?.benefitsDetails : 'Not Set'}\n`;
        note += `* **This will be the case for:** ${fitnessForWork?.caseForNumber ? fitnessForWork?.caseForNumber : 'Not Set'} ${fitnessForWork?.caseForInterval ? fitnessForWork?.caseForInterval + '(s)' : 'Not Set'}\n`;
        note += `* **from:** ${fitnessForWork?.caseFrom ? moment(fitnessForWork?.caseFrom).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **to:** ${fitnessForWork?.caseTo ? moment(fitnessForWork?.caseTo).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **NI number:** ${fitnessForWork?.niNumber ? fitnessForWork?.niNumber : 'Not Set'}\n`;
        note += `* **Unique Id:** ${fitnessForWork?.uniqueId ? fitnessForWork?.uniqueId : 'Not Set'}\n\n`;

        note += '### *GP demographics*\n';
        note += `* **Issuer's name:** ${fitnessForWork?.issuersName ? fitnessForWork?.issuersName : 'Not Set'}\n`;
        note += `* **Issuer's profession:** ${fitnessForWork?.issuersProfession ? fitnessForWork?.issuersProfession : 'Not Set'}\n`;
        note += `* **Date of statement:** ${fitnessForWork?.dateOfStatement ? moment(fitnessForWork?.dateOfStatement).format(config.defaultDateFormat) : 'Not Set'}\n`;
        note += `* **Issuer's address:** ${fitnessForWork.issuersAddress ? fitnessForWork.issuersAddress : 'Not Set'} **Postal:** ${fitnessForWork.issuersPostal ? fitnessForWork.issuersPostal : 'Not Set'}\n\n`;

        note += '### *Patient demographics*\n';
        note += `* **Surname:** ${fitnessForWork?.surname ? fitnessForWork?.surname : 'Not Set'}\n`;
        note += `* **Other names:** ${fitnessForWork?.otherNames ? fitnessForWork?.otherNames : 'Not Set'}\n`;
        note += `* **Address:** ${fitnessForWork.address ? fitnessForWork.address : 'Not Set'} **Postal:** ${fitnessForWork.postalCode ? fitnessForWork.postalCode : 'Not Set'}\n`;
        note += `* **Date of birth:** ${fitnessForWork?.dateOfBirth ? moment(fitnessForWork?.dateOfBirth).format(config.defaultDateFormat) : 'Not Set'}\n`;

        return note;
    }

    static getInjuryNote(injury, age, parentForm, isInjury, drugsAlcoholInvolved) {
        const getDates = (type) => {
            if (type === 'Illness') {
                const date = moment(injury?.cwilted?.incidentDate, config.ecdsDateFormat).format(config.defaultDateFormat);
                const time = moment(injury?.cwilted?.incidentTime, config.ecdsTimeFormat).format(config.defaultTimeFormat);
                return `${date} ${time}`;
            } else {
                const localDate = UtilsMomentJS.getLocalDateFromUTCECDSFormat(injury.injuryDate, injury.injuryTime);
                return `${localDate.format(config.defaultDateFormat)} ${localDate.format(config.defaultTimeFormat)}`;
            }
        };

        let note = '';
        note = age >= 18 ? '## *Injury Data Set*\n\n' : '## *CWILTED Data Set*\n\n';
        if (parentForm) {
            note += `#### Submitted from parent form: ${parentForm}\n`;
        }
        if (age < 18) {
            note += `\n### CWILTED\n`;
            note += injury?.cwilted?.incident ? `* **Concern and Incident**: ${injury.cwilted.incident}\n` : '';
            note += injury?.cwilted?.witnessed ? `* **Witnessed**: ${injury.cwilted.witnessed ? 'Yes' : 'No'}\n` : '';
            note += injury?.cwilted?.details ? `* **Witnessed details**: ${injury.cwilted.details}\n` : '';

            if (injury?.cwilted && injury?.cwilted?.escort && injury?.cwilted?.escort?.length > 0) {
                note += `\n#### Escort\n`;
                note += `|Name|Age|Relationship|Contact Details|Parental Responsibility|Attended with a child|\n|:---|:---:|:---:|:---:|:---:|:---:|\n`;
                _.forEach(injury?.cwilted?.escort, (element) => {
                    note += `|${element.name}|${element.age}|${element.relationship}|${element.contact}|${element.parentalResponsibility ? 'Yes' : 'No'}|${element.attendedWithChild ? 'Yes' : 'No'}| \n`;
                });
            }

            note += injury?.cwilted?.demeanour ? `* **Demeanour**: ${injury.cwilted.demeanour}\n` : '';

            if (parentForm !== 'Triage' || (parentForm === 'Triage' && isInjury !== '1')) {
                note += injury?.cwilted?.incidentDate && injury.cwilted.incidentTime ? `\n## Illness\n` : '';
                note +=
                    injury?.cwilted?.incidentDate && injury.cwilted.incidentTime
                        ? `* **Illness Date and Time**: ${getDates('Illness')}\n`
                        : '';
                note += injury?.cwilted?.incidentLocation
                    ? `* **Illness Location**: ${injury.cwilted.incidentLocation.__description}\n`
                    : '';
                note += injury?.cwilted?.incidentLocationName ? `* **Location Name**: ${injury.cwilted.incidentLocationName}\n` : '';
            }
        }

        if (parentForm !== 'Triage' || (parentForm === 'Triage' && isInjury === '1')) {
            note += injury.injuryDate && injury.injuryTime ? `\n### Injury\n` : '';
            note += injury.injuryDate && injury.injuryTime ? `* **Injury Date and Time**: ${getDates()}\n` : '';
            note += injury.placeOfInjury ? `* **Place of Injury**: ${injury.placeOfInjury.__description}\n` : '';
            note += injury.injuryIntent ? `* **Injury Intent**: ${injury.injuryIntent.__description}\n` : '';
            note += injury.injuryActivityStatus ? `* **Injury Activity Status**: ${injury.injuryActivityStatus.__description}\n` : '';
            note += injury.injuryActivityType ? `* **Injury Activity Type**: ${injury.injuryActivityType.__description}\n` : '';
            note += injury.injuryMechanism ? `* **Injury Mechanism**: ${injury.injuryMechanism.__description}\n` : '';
            note += injury.assaultLocationDescription ? `* **Assault Location Description**: ${injury.assaultLocationDescription}\n` : '';

            if (!_.isEmpty(injury.injuryAlcoholOrDrug) && drugsAlcoholInvolved) {
                note += `* **Injury Alcohol or Drug Involvement**: `;
                _.forEach(injury.injuryAlcoholOrDrug, (element) => {
                    note += `${element.__description}, `;
                });
            }
        }

        return note;
    }

    static getNutritionScreenNote(form, age, unit, bmi, bmiCal) {
        let note;
        if (age >= 18) note = '## *Weight / Height / BMI*\n\n';
        else note = '## *Weight / Height*\n\n';

        note += `* **Age**: ${age}\n`;
        if (unit === 'si') {
            note +=
                form.weightSI !== ''
                    ? `* **Weight**: ${form.weightSI} kg [${(form.weightSI / 0.45359237).toFixed(1)} lb]\n`
                    : '* **Weight**: Not Recorded\n';
            note +=
                form.heightSI !== ''
                    ? `* **Height**: ${form.heightSI} cm [${cmToFeetInch(form.heightSI)}]\n`
                    : '* **Height**: Not Recorded\n';
            note += !_.isNil(form.muac)
                ? `* **MUAC**: ${form.muac} cm [${(form.muac / 2.54).toFixed(1)} in]\n`
                : '* **MUAC**: Not Recorded\n';
        } else {
            note +=
                form.weightStone !== '' && form.weightPounds !== ''
                    ? `* **Weight**: ${form.weightStone}st ${form.weightPounds}lb [${(form.weightStone * 6.35 + form.weightPounds * 0.4536).toFixed(1)} kg]\n`
                    : '* **Weight**: Not Recorded\n';
            note +=
                form.heightFeet !== '' && form.heightInches !== ''
                    ? `* **Height**: ${form.heightFeet}ft ${form.heightInches}in [${feetInchToCm(form.heightFeet, form.heightInches)}]\n`
                    : '* **Height**: Not Recorded\n';
            note += form.muac !== '' ? `* **MUAC**: ${form.muac} im [${(form.muac * 2.54).toFixed(1)} cm]\n` : '* **MUAC**: Not Recorded\n';
        }
        note += `* **BMI**: ${bmi ? bmi : 'Not Recorded'}\n`;
        note += `* **BMI Result**: ${bmiCal ? bmiCal : 'Not Recorded'}\n`;

        return note;
    }

    static getCancelNutritionScreenNote(cancelObj, age) {
        let note = age >= 18 ? '## *Weight / Height / BMI*\n\n' : '## *Weight / Height*\n\n';
        note += `* Nutrition at ${moment(cancelObj?.date).format(config.defaultDateTimeFormat)} have been cancelled`;

        return note;
    }

    static getUrinalysisTestNote(selectedTest, formData, additionalInfoData) {
        let note = '';
        note = '## *Urinalysis Assessment*\n\n';
        if (selectedTest) note += `[${selectedTest}] a test strip was used\n\n`;
        note += `### Results\n\n`;
        if (!_.isEmpty(formData)) {
            note += `|Category|Value|\n|:--|:--|\n`;
            _.forEach(_.sortBy(formData, ['position']), (test) => {
                note += `|${test.category} [${test.categoryShortcut}]|${test.value}|  \n`;
            });
            note += '\n';
        }
        note += `* **Beta-HCG**: ${additionalInfoData.bhcg ? _.startCase(additionalInfoData.bhcg) : '-'} \n`;
        note += `* **Additional Information**: ${additionalInfoData.additionalInformation ? additionalInfoData.additionalInformation : '-'}\n`;

        return note;
    }

    static getRapidAssessment(seen, isClear, tester, clinician, time) {
        let note = '## *Rapid Assessment*\n\n';
        if (isClear) {
            note += `The form was cleared by ${tester}`;
        } else {
            note += `* **Rapid Assessment Clinician**: ${getConsultantName(clinician)}\n`;
            note += `* **Date and Time**: ${time}\n`;
            note += Formatter.formatDiagnosisTreatmentInvestigationToNote(null, seen.treatments, seen.investigations);
            note += `* **Rapid Assessment Summary**: ${seen.additionalInformation ? seen.additionalInformation : '-'} \n`;
            if (seen.rapidAssessment) note += `* **Rapid Assessment**: ${seen.rapidAssessment.__description}`;
        }
        return note;
    }

    static getReferralsNote(otherService, originReferrals) {
        let note = '';
        note = '## *Referrals to Other Services*\n\n';
        if (!_.isEmpty(otherService.ReferralsToOtherServices)) {
            _.forEach(otherService.ReferralsToOtherServices, (element) => {
                note += `* ${element.ReferredToService_SnomedCt.__description}, Requested at ${getLocalDateTextReferrals(element, 'date', 'requested')} ${getLocalDateTextReferrals(element, 'time', 'requested')}, Assessment `;
                note +=
                    element.ReferredToServiceAssessmentDate && element.ReferredToServiceAssessmentTime
                        ? `at ${getLocalDateTextReferrals(element, 'date', 'assessment')} ${getLocalDateTextReferrals(element, 'time', 'assessment')}\n`
                        : 'not set.\n';
            });
        } else {
            note += 'Referrals to Other Services have been cleared.\n';
        }

        // deleted referrals
        if (!_.isEmpty(originReferrals)) {
            let deletedReferrals = _.cloneDeep(originReferrals);

            if (!_.isEmpty(otherService?.ReferralsToOtherServices)) {
                otherService.ReferralsToOtherServices.forEach((element) => {
                    deletedReferrals = _.filter(deletedReferrals, (filter) => {
                        return filter.id !== element.id;
                    });
                });
            }

            if (!_.isEmpty(deletedReferrals)) {
                note += `\n### Canceled Referrals\n\n`;
                _.forEach(deletedReferrals, (element) => {
                    note += `* ~${element.ReferredToService_SnomedCt.__description}, Requested at ${element.ActivityServiceRequestDate_EmergencyCare} ${element.ActivityServiceRequestTime_EmergencyCare}, Assessment `;
                    note +=
                        element.ReferredToServiceAssessmentDate && element.ReferredToServiceAssessmentTime
                            ? `at ${element.ReferredToServiceAssessmentDate} ${element.ReferredToServiceAssessmentTime}~\n`
                            : 'not set.~\n';
                });
            }
        }

        return note;
    }

    static getClinicalFrailtyScoreNote(dataForm, lastScore, parentForm) {
        let note = '';
        note = '## *Clinical Frailty Score*\n\n';
        if (parentForm) {
            note += `#### Submitted from parent form: ${parentForm}\n`;
        }
        note += `* **Scale**: [${dataForm?.scale?.__code}] ${dataForm?.scale?.__description}\n`;
        note += `* **Information**: \n${dataForm.action}\n`;
        note += dataForm.details !== '' ? `* **Additional Details**: ${dataForm.details}\n` : '';
        note +=
            dataForm?.scale?.__code == 4 || dataForm?.scale?.__code == 5
                ? `* **Patient consent to referral**: ${dataForm.patientConsentToReferral ? 'Yes' : 'No'}\n`
                : '';
        note +=
            dataForm?.scale?.__code == 4 || dataForm?.scale?.__code == 5
                ? `* **Referral has been made**: ${dataForm.referralCompleted ? 'Yes\n' : 'No'}\n`
                : '';
        note += lastScore
            ? `Trend compared to the last entry: ${
                  lastScore > dataForm?.scale?.__code ? '↓ \n' : lastScore === dataForm?.scale?.__code ? '- \n' : '↑ \n'
              }`
            : '';

        return note;
    }

    static getClinicalFrailtyScoreEditNote(dataForm, orgTime) {
        let note = '## *Clinical Frailty Score*\n\n';
        note += `### Update Timestamp of Clinical Frailty Score\n\n`;
        note += `Time change from ${
            moment(orgTime, config.defaultDateTimeFormat).isValid() ? orgTime : moment(orgTime).format(config.defaultDateTimeFormat)
        } to ${moment(dataForm.timestamp).format(config.defaultDateTimeFormat)}`;
        return note;
    }

    static getSeeAndTreatNote(dataForm) {
        let note = '## *See and Treat*\n\n';

        // Clinical note for draft
        if (dataForm.status === SEE_AND_TREAT_STATUSES.draft) {
            note += '## Draft saved\n';
            note +=
                `* Time of documentation start ${moment(dataForm.timeOfDocumentation).format(config.defaultDateTimeFormat)}` +
                ` by user ${dataForm.by}, draft saved at ${moment(dataForm.firstFormSaved).format(config.defaultDateTimeFormat)}`;
            if (dataForm.firstFormSaved !== dataForm.formSaved) {
                note += '\n';
                note +=
                    `* Draft opened at ${moment(dataForm.timeOfOpenForm).format(config.defaultDateTimeFormat)}` +
                    ` by user ${dataForm.by}, saved at ${moment(dataForm.formSaved).format(config.defaultDateTimeFormat)}`;
            }
        } else {
            note +=
                `* Time of documentation start at ${moment(dataForm.timeOfDocumentation).format(config.defaultDateTimeFormat)}` +
                ` by user ${dataForm.by} \n`;
            //note += `* **Clerking Clinician**: ${dataForm.clinician ? dataForm.clinician?.__description : "-"}\n`;
            note += `* **Clerking Clinician**: ${dataForm.clinician ? getConsultantName(dataForm.clinician) : '-'}\n`;
            note += `* **Date and Time of See and Treat**: ${dataForm.when ? moment(dataForm.when).format(config.defaultDateTimeFormat) : '-'}\n`;
            if (dataForm.presentation) {
                note += `* **${config?.modules?.customLabels?.seeAndTreat?.presentation || 'Presentation'}**: ${dataForm.presentation ? dataForm.presentation?.__description : '-'}\n`;
            }
            const discriminator = dataForm.presentation ? 'Discriminator' : 'Acuity Score';
            note += `* **${discriminator}**:  ${dataForm.discriminator ? dataForm.discriminator?.__description : '-'}\n`;
            note += `* **Dependency**:  ${dataForm.dependency ? dataForm.dependency?.__description : '-'}\n`;
            note += `* **Chief Complaint**:  ${dataForm.chiefComplaint ? DictParser.chiefComplaint(dataForm.chiefComplaint) : '-'}\n`;
            note += `* **History of Presenting Complaint**: ${dataForm.historyOfPresentingComplaint ? dataForm.historyOfPresentingComplaint : '-'}\n`;

            if (dataForm.painScoreId !== '') {
                note += `* **Pain Score**: ${dataForm.painScore}\n`;
            }

            note += `* **Analgesia Given**: ${Formatter.formatBooleanAsString(dataForm.analgesiaGiven)}\n`;
            if (dataForm.analgesiaGiven) {
                note += `* **Analgesia Details**: ${dataForm.analgesiaDetails ? dataForm.analgesiaDetails : '-'}\n`;
            }
            note += `* **Analgesia Refused**: ${Formatter.formatBooleanAsString(dataForm.analgesiaRefused)}\n`;

            if (config.showDifferentialDiagnosisSeeAndTreat) {
                note += `* **Examination**: ${dataForm.examination ? dataForm.examination : '-'}\n`;
                note += `* **Differential Diagnosis**: ${dataForm.workingDiagnosis ? dataForm.workingDiagnosis : '-'}\n\n`;
            } else {
                note += `* **Examination**: ${dataForm.examination ? dataForm.examination : '-'}\n\n`;
            }

            note += Formatter.formatDiagnosisTreatmentInvestigationToNote(dataForm.diagnosis, dataForm.treatment, dataForm.investigation);

            note += `\n### Safeguarding:\n`;
            note += Formatter.formatSafeguardingToNote(dataForm.safeguarding);

            note += `\n## Additional Information:\n`;
            note += `* **Past Medical History**: ${dataForm.pastMedicalHistory ? dataForm.pastMedicalHistory : '-'}\n`;
            note += `* **Allergies and Reactions**: ${dataForm.allergiesAndReactions ? dataForm.allergiesAndReactions : '-'}\n`;
            note += `* **Medications**: ${dataForm.medications ? dataForm.medications : '-'}\n`;
            note += `* **Social History**: ${dataForm.socialHistory ? dataForm.socialHistory : '-'}\n`;
        }
        return note;
    }

    static getActionNote(type, triageObject) {
        let note = '';
        note = `## *Time Stamp ${type}*\n\n\ `;

        note +=
            //`* **${type} Consultant**: ${triageObject.triageClinicians ? triageObject?.triageClinicians?.__description : '-'} \n` +
            `* **${type} Consultant**: ${triageObject.triageClinicians ? getConsultantName(triageObject?.triageClinicians) : '-'} \n` +
            `* **Start Time of ${type}**: ${moment(triageObject.when).format(config.defaultDateTimeFormat)}\n`;

        return note;
    }

    static getActionClerkingNote(clerkingObject) {
        let note = '';
        note = `## *Time Stamp Clerking*\n\n\ `;

        note +=
            //`* **Clerking Clinician**: ${clerkingObject.clinician ? clerkingObject.clinician?.__description : "-"} \n` +
            `* **Clerking Clinician**: ${clerkingObject.clinician ? getConsultantName(clerkingObject.clinician) : '-'} \n` +
            `* **Start Time of Clerking**: ${moment(clerkingObject.when).format(config.defaultDateTimeFormat)}\n`;

        return note;
    }
    static getActionSRNote(seniorReviewObject) {
        let note = '';
        note = `## *Time Stamp Senior Review*\n\n\ `;

        note +=
            `* **Senior Review Clinician**: ${seniorReviewObject.who ? getConsultantName(seniorReviewObject.who) : '-'} \n` +
            `* **Start Time of Senior Review**: ${moment(seniorReviewObject.when).format(config.defaultDateTimeFormat)}\n`;

        return note;
    }

    static getActionSATNote(dataForm) {
        let note = '';
        note = `## *Time Stamp See and Treat*\n\n\ `;

        //note += `* **Clerking Clinician**: ${dataForm.clinician ? dataForm.clinician?.__description : "-"}\n`;
        note += `* **Clerking Clinician**: ${dataForm.clinician ? getConsultantName(dataForm.clinician) : '-'}\n`;
        note += `* **Date and Time of See and Treat**: ${dataForm.when ? moment(dataForm.when).format(config.defaultDateTimeFormat) : '-'}\n`;

        return note;
    }

    static getActionDischargeNote(dataForm) {
        let note = '';
        note = `## *Time Stamp Discharge*\n\n\ `;

        note +=
            `* **Departure Date and Time**: ${dataForm?.departureDate ? moment(dataForm?.departureDate).format(config.defaultDateFormat) : '-'} ` +
            `${dataForm?.departureTime ? moment(dataForm?.departureTime).format(config.defaultTimeFormat) : '-'}\n` +
            //`* **Discharge Clinician**: ${dataForm?.who ? dataForm?.who?.__description : "-"}\n` +
            `* **Discharge Clinician**: ${dataForm?.who ? getConsultantName(dataForm.who) : '-'}\n` +
            `* **Discharge Status**: ${dataForm?.dischargeStatus?.__description || '-'}\n` +
            `* **Discharge Destination**: ${dataForm?.dischargeDestination?.__description || '-'}\n` +
            `* **Discharge Follow Up**: ${dataForm?.dischargeFollowUp?.__description || '-'}\n`;

        return note;
    }

    static getActionRapidAssessmentNote(clinician, time) {
        let note = '';
        note = `## *Time Stamp Rapid Assessment*\n\n\ `;

        note += `* **Rapid Assessment Clinician**: ${getConsultantName(clinician)} \n` + `* **Start Time of Clerking**: ${time}\n`;

        return note;
    }

    static getRemoveTimestampNote(type, removedCurrentClinician = false, removedFirstClinician = false) {
        let note = '';
        note = `## *Timestamp has been removed from ${_.startCase(type)}*\n`;
        if (removedCurrentClinician) note += '* First Clerking Clinician has been removed \n';
        if (removedFirstClinician) note += '* Current clinician has been removed \n';
        return note;
    }

    static getClerkingNote(clerkingObject, patientType, isFromSAT, patient, patientContextType) {
        let note = '';

        note = `## *${patientContextType === PATIENT_CONTEXT_TYPE.UTC ? 'UTC ' : ''}Clerking*\n\n`;
        const raEmpty = _.isEmpty(patient?.seen?.treatments) || _.isEmpty(patient?.seen?.investigations);

        if (isFromSAT) note += '#### Note auto-generated from See and Treat\n';

        note +=
            //`* **Clerking Clinician**: ${clerkingObject.clinician ? clerkingObject.clinician?.__description : "-"} \n` +
            `* **Clerking Clinician**: ${clerkingObject.clinician ? getConsultantName(clerkingObject.clinician) : '-'} \n` +
            `* **Start Time of Clerking**: ${moment(clerkingObject.when).format(config.defaultDateTimeFormat)}\n` +
            `* **ECDS Chief Complaint**: ${clerkingObject.chiefComplaint ? DictParser.chiefComplaint(clerkingObject.chiefComplaint) : '-'} \n\n`;

        note +=
            `#### Additional Information:\n` +
            `* **Pre-Hospital / Ambulance**: ${clerkingObject.preHospitalAmbulanceInfo ? clerkingObject.preHospitalAmbulanceInfo : '-'} \n${!raEmpty ? '\n' : ''}` +
            Formatter.formatDiagnosisTreatmentInvestigationToNote(
                null,
                patient?.seen?.treatments,
                patient?.seen?.investigations,
                'Rapid Assessment',
            ) +
            `* **Rapid Assessment Summary**: ${patient?.seen?.additionalInformation ? patient.seen?.additionalInformation : '-'} \n` +
            `* **Triage History of Presenting Complaint**: ${patient?.triage?.historicPresentingComplaint ? patient?.triage?.historicPresentingComplaint : '-'} \n\n`;

        note +=
            `#### Presenting Complain:\n` +
            `* **Presenting Complain**: ${clerkingObject.presentingComplaint ? clerkingObject.presentingComplaint : '-'} \n` +
            `* **History of Presenting Complain**: ${clerkingObject.historicPresentingComplaint ? clerkingObject.historicPresentingComplaint : '-'} \n\n`;

        note += `#### History:\n`;
        if (patientType === PATIENT_AGE_TYPE.PAEDS)
            note += `* **Patient Weight**: ${clerkingObject.patientWeight ? `${clerkingObject.patientWeight} ${clerkingObject.patientWeightUnits}` : '-'} \n`;
        note +=
            `* **Past Medical History**: ${clerkingObject.pastMedicalHistory ? clerkingObject.pastMedicalHistory : '-'}\n` +
            `* **Allergies and reactions**: ${clerkingObject.allergies ? clerkingObject.allergies : '-'} \n` +
            `* **Medications**: ${clerkingObject.medications ? clerkingObject.medications : '-'} \n` +
            `* **Social History**: ${clerkingObject.socialHistory ? clerkingObject.socialHistory : '-'} \n\n`;

        note += `#### Diagnosis:\n`;
        note += `* **Clerking Examinations**: ${clerkingObject.examination ? clerkingObject.examination : '-'} \n`;
        if (config.clerkingReview) note += `* **Review**: ${clerkingObject.clerkingReview ? clerkingObject.clerkingReview : '-'} \n`;
        note += `* **Differential Diagnosis / Problem List**: ${clerkingObject.workingDiagnosis ? clerkingObject.workingDiagnosis : '-'} \n\n`;
        note += Formatter.formatDiagnosisTreatmentInvestigationToNote(clerkingObject.diagnosis, null, null, 'ECDS');

        note += `#### Treatment and Investigations:\n`;
        note += `* **Clerking Management Plan**: ${clerkingObject.additionalInformation ? clerkingObject.additionalInformation : '-'} \n ${!_.isEmpty(clerkingObject.investigations) ? '\n' : ''}`;
        note += Formatter.formatDiagnosisTreatmentInvestigationToNote(null, null, clerkingObject.investigations, 'ECDS');
        note += `* **Clerking Investigations Results**: ${clerkingObject.investigationsDetails ? clerkingObject.investigationsDetails : '-'} \n\n`;
        note += Formatter.formatDiagnosisTreatmentInvestigationToNote(null, clerkingObject.treatmentAndManagementPlan, null, 'ECDS');

        note += `#### Safeguarding:\n`;
        note += `* **Safeguarding Concerns**: ${clerkingObject.safeguardingConcerns ? 'Yes' : 'No'}\n`;
        if (clerkingObject.safeguardingConcerns) {
            note += Formatter.formatSafeguardingToNote(clerkingObject.safeguarding);
            if (patientType === PATIENT_AGE_TYPE.OVER75) {
                note += `\n#### Concerns & Actions taken:\n`;
                note += `* ${clerkingObject.safeguardingConcernsDetails}\n`;
            }
        }
        if (patientType !== PATIENT_AGE_TYPE.PAEDS) {
            note += `* **Child Protection Concerns**: ${clerkingObject.childProtectionConcerns ? 'Yes' : 'No'}\n`;
            if (clerkingObject.childProtectionConcerns) {
                note += `\n#### Concerns & Actions taken:\n`;
                note += `* ${clerkingObject.childConcernsDetails}\n\n`;
            }
        }
        return note;
    }

    static getDependencyChange(value) {
        let note = '';
        note = '## *Triage Dependency change*\n';
        note += `* **Dependency**: ${value?.__description ? value.__description : '-'} \n`;

        return note;
    }

    static getTriageNote(triageObject, patientType, isFromSAT, escortList) {
        const getAnalgesia = () => {
            if (triageObject.analgesia) return 'Yes';
            if (triageObject.analgesiaRefused) return 'Refused';
        };

        let note = '';
        note = '## *Triage*\n';

        if (isFromSAT) {
            note += '#### Note auto-generated from See and Treat\n';
        }

        note +=
            //`* **Triage Clinician**: ${triageObject.triageClinicians ? triageObject?.triageClinicians?.__description : '-'} \n` +
            `* **Triage Clinician**: ${triageObject.triageClinicians ? getConsultantName(triageObject?.triageClinicians) : '-'} \n` +
            `* **Start Time of Triage**: ${moment(triageObject.when).format(config.defaultDateTimeFormat)}\n`;

        if (triageObject.presentation) {
            note += `* **${config?.modules?.customLabels?.triage?.presentation || 'Presentation'}**: ${triageObject.presentation?.__description ? triageObject.presentation.__description : '-'} \n`;
        }
        const discriminator = triageObject.presentation ? 'Discriminator' : 'Acuity Score';

        note +=
            `* **${discriminator}**: ${triageObject.discriminator?.__description ? triageObject.discriminator.__description : '-'} \n` +
            `* **Dependency**: ${triageObject.dependencyTriage?.__description ? triageObject.dependencyTriage.__description : '-'} \n` +
            `* **Chief Complaint**: ${triageObject.chiefComplaint ? DictParser.chiefComplaint(triageObject.chiefComplaint) : '-'} \n` +
            `${triageObject.painScoreId ? `* **Pain Score**: ${triageObject.painScore}\n` : ''}` +
            `* **History of Presenting Complain**: ${triageObject.historicPresentingComplaint ? triageObject.historicPresentingComplaint : '-'} \n` +
            `* **Allergies and reactions**: ${triageObject.allergies ? triageObject.allergies : '-'} \n` +
            `* **Past Medical History**: ${triageObject.pastMedicalHistory ? triageObject.pastMedicalHistory : '-'}\n` +
            `* **Medications**: ${triageObject.medications ? triageObject.medications : '-'} \n` +
            `* **Analgesia**: ${triageObject.analgesia || triageObject.analgesiaRefused ? getAnalgesia() : '-'} \n` +
            `* **Analgesia Details**: ${triageObject.analgesiaDetails ? triageObject.analgesiaDetails : '-'} \n`;

        if (config.priorAnalgesiaInTriage) {
            note += `* **${config?.triageAnalgesiaPriorTextChange ? 'Medications before triage' : 'Patient took analgesia prior to hospital'}**: ${triageObject.analgesiaPrior ? 'Yes' : 'No'} \n`;
            if (triageObject.analgesiaPrior)
                note += `* **${config?.triageAnalgesiaPriorTextChange ? 'Medications before triage' : 'Analgesia taken prior to the hospital'}**: ${triageObject.analgesiaPriorDetails ? triageObject.analgesiaPriorDetails : '-'} \n`;
        }

        note += `* **Social History**: ${triageObject.socialHistory ? triageObject.socialHistory : '-'} \n`;
        if (!config?.disableTriageExamination)
            note += `* **Examination & Investigations Information**: ${triageObject.examination ? triageObject.examination : '-'} \n`;
        note += `* **Treatment and Management Plan Information**: ${triageObject.additionalInformation ? triageObject.additionalInformation : '-'} \n`;

        if (patientType === PATIENT_AGE_TYPE.PAEDS) {
            note += `* **Patient Weight**: ${triageObject.patientWeight ? `${triageObject.patientWeight} ${triageObject.patientWeightUnits}` : '-'} \n`;
        }

        if (triageObject.observationNotRequired) {
            note += `* **Triage Observation status**: Observation not required\n`;
        }

        note += `* **Safeguarding Concerns**: ${triageObject.safeguardingConcerns ? 'Yes' : 'No'}\n`;
        if (triageObject.safeguardingConcerns) {
            note += `\n#### Safeguarding:\n`;
            note += Formatter.formatSafeguardingToNote(triageObject.safeguarding);
            if (patientType === PATIENT_AGE_TYPE.OVER75) {
                note += `#### Concerns & Actions taken:\n`;
                note += `* ${triageObject.safeguardingConcernsDetails}\n`;
            }
        }
        if (patientType !== PATIENT_AGE_TYPE.PAEDS) {
            note += `* **Child Protection Concerns**: ${triageObject.childProtectionConcerns ? 'Yes' : 'No'}\n`;
            if (triageObject.childProtectionConcerns) {
                note += `\n#### Concerns & Actions taken:\n`;
                note += `* ${triageObject.childConcernsDetails}\n`;
            }
        }

        if (config?.escortInTriage && escortList && escortList.length > 0) {
            note += `\n#### Escort\n`;
            note += `|Name|Age|Relationship|Contact Details|Parental Responsibility|Attended with a child|\n|:---|:---:|:---:|:---:|:---:|:---:|\n`;
            _.forEach(escortList, (element) => {
                note += `|${element.name}|${element.age}|${element.relationship}|${element.contact}|${element.parentalResponsibility ? 'Yes' : 'No'}|${element.attendedWithChild ? 'Yes' : 'No'}| \n`;
            });
        }

        note += `\n`;
        note += Formatter.formatDiagnosisTreatmentInvestigationToNote(
            triageObject.diagnosis,
            triageObject.treatmentAndManagementPlanTriage,
            triageObject.investigationsTriage,
        );

        const summaryParts = [];
        note += `* **Is this presentation related to a mental health condition or crisis?**: ${triageObject.mentalHealthRisk ? 'Yes' : 'No'}\n`;
        if (triageObject.mentalHealthRisk) {
            note += `* **Mental health condition risk**: ${triageObject.mentalHealthRiskLevel}\n`;
            summaryParts.push('Complete the mental health triage risk form');
        }
        note += `* **Is this presentation related to drugs or alcohol use or intoxication?**: ${triageObject.drugRelated ? 'Yes' : 'No'}\n`;
        if (triageObject.drugRelated) {
            summaryParts.push('Recommended the S-CERAT form');
        }
        note += `* **Is this presentation related to an assault?**: ${triageObject.assaultRelated ? 'Yes' : 'No'}\n`;
        if (triageObject.assaultRelated) {
            summaryParts.push('Complete the assault data collection');
        }
        if (summaryParts.length > 0) {
            note += `* **Summary**:\n`;
            note += summaryParts.join('\n');
        }

        return note;
    }

    static getVTENote(formToNote, formSettings) {
        let note;
        note = `## *${formToNote.name}*\n\n`;
        note += `### ${getFullFormName(formToNote.type)}`;
        formSettings.components.forEach((component) => {
            note += `\n\n ### ${component.label} \n`;
            if (_.isEmpty(formToNote.values[component.set])) {
                note += `\n * None`;
            }
            Object.keys(formToNote.values[component.set]).forEach((key) => {
                note += `\n * ${formToNote[component.set + 'Texts'][key]}`;
                if (component.saveAllValues) note += ` ${formToNote.values[component.set][key] ? 'YES' : 'NO'}`;
            });
        });

        note += `\n\n ### Outcome \n ${formToNote.outcome}`;
        note += `\n\n ${formSettings.staticOutcomeWarning}`;
        return note;
    }

    static getPatientFlagsNote(changeInFlags) {
        let note;
        if (!_.isEmpty(changeInFlags)) {
            note = '## *Patient Flags*\n';
            _.forEach(changeInFlags, (element) => {
                note += `* Flag ${findPatientFlagTitle(element.name)} has been ${element.checked ? 'activated' : 'deactivated'}\n`;
            });
        }
        return note;
    }

    static getPatientAlarmsNote(alarm) {
        let note = '';
        note = '## *Alarm*\n\n';
        if (alarm.deleted) {
            note += `### Alarm has been removed\n`;
        }
        note += `* **Group**: ${alarm.group}\n`;
        note += `* **Name**: ${alarm.name}\n`;
        note += `* **Description**: ${alarm.description ? alarm.description : '-'}\n`;
        note += `* **Risk / Priority**: ${AlarmStage.AlarmStageName(alarm.stage)}\n`;
        if (alarm.state) note += `* **Status**: ${_.startCase(alarm.state)}\n`;
        note += `* **Expire date**: ${moment(alarm.expireDate).format(config.defaultDateTimeFormat)}\n`;
        note += `* **Banner**: ${alarm.onBanner ? 'Yes' : 'No'}\n`;
        return note;
    }

    static getNursingAdmissionNote(nursingAdmission, propertyList = true) {
        const filteredPropertyList = _.map(nursingAdmission.propertyList, (property) => {
            if (property.checked === true || property.comment) return property;
        }).filter((el) => !!el);

        const getSwitchFieldNote = (field) => {
            const noteValue =
                nursingAdmission[field].value === 'yes' ? 'Yes' : nursingAdmission.namebands.value === 'no' ? 'No' : 'Not Recorded';
            const noteComment = nursingAdmission[field].comment ? ` **Nurse Comment**: ${nursingAdmission[field].comment}` : '';

            return `* **${_.startCase(field)}**: ${noteValue}${noteComment}\n`;
        };

        let note = '';
        note = `## *${propertyList ? 'Property List' : 'Nursing Admission'}*\n\n`;
        if (!propertyList) {
            note += `* **Observation date**: ${moment(nursingAdmission.when).format(config.defaultDateTimeFormat)}\n`;
            note += `${getSwitchFieldNote('undressedIntoGown')}`;
            note += `${getSwitchFieldNote('namebands')}`;
            note += `${getSwitchFieldNote}chFieldNote("analgesia")}`;
        }

        if (filteredPropertyList.length > 0) {
            if (!propertyList) note += `* **Property list**: \n`;
            filteredPropertyList.map(
                (property) =>
                    (note += `    - ${property.name[0].toUpperCase() + property.name.slice(1)}: ${property.checked ? 'possessed' : 'not possessed'}${property.checkedOption ? `, ${property.checkedOption}.` : '.'}${property.comment ? ' Nurse comment: ' + property.comment + '.' : ''}\n`),
            );
        }
        return note;
    }

    static getNonContactObsNote = (nonContactObs, deletion = false) => {
        const printNonContactValue = (property) => {
            switch (property.value) {
                case 'yes':
                    return 'Yes';
                case 'no':
                    return 'No';
                case null:
                    return 'NR';
                default:
                    return '';
            }
        };

        const printCommentValue = (property) => {
            let tempNote = '';
            if (!property.comment) {
                tempNote += '-';
            }
            if (property.comment) {
                tempNote += `${property.comment}`;
            }
            return tempNote;
        };

        let note;
        note = `## *NON - Contact Physical Health Observations*\n`;

        if (deletion) {
            note += `### Following observation has been removed\n`;
        }

        note += `|Position|Value|Comment|\n|---:|:---:|:---:|\n`;

        note += `|Airway|${printNonContactValue(nonContactObs.airway)}| ${printCommentValue(nonContactObs.airway)}| \n`;
        note += `|Breathing|${printNonContactValue(nonContactObs.breathing)}|  ${printCommentValue(nonContactObs.breathing)}| \n`;
        note += `|Circulation|${printNonContactValue(nonContactObs.circulation)}|  ${printCommentValue(nonContactObs.circulation)}| \n`;
        note += `|Disability|${printNonContactValue(nonContactObs.disability)}|  ${printCommentValue(nonContactObs.disability)}|  \n`;
        note += `|Exposure|${printNonContactValue(nonContactObs.exposure)}|  ${printCommentValue(nonContactObs.exposure)}| \n`;

        return note;
    };

    static getPatientSafetyChecklistNote(
        safetyChecklist,
        safetyChecklistOld,
        isAdult,
        validSection1,
        validSection2,
        validSection3,
        strokeScore,
    ) {
        const version = config?.client || 'DCH';
        const isUHD = version === 'UHD';

        const checkOptions = (field, title, onlyComment) => {
            const opt = safetyChecklist[field];
            const optOld = safetyChecklistOld[field];

            const getVal = () => {
                if (typeof opt?.value === 'string' || typeof opt?.value === 'number') return opt?.value;
                return opt?.value ? 'Yes' : 'No';
            };

            if (opt && !_.isEqual(opt, optOld)) {
                let checkText = '';
                if (!onlyComment) {
                    if (field.startsWith('vipScore')) {
                        checkText = ' ';
                    } else {
                        checkText = opt?.notRequired || opt?.value ? 'Check: ' : '';
                    }
                }
                return (
                    ` ${title} ${checkText}${!onlyComment ? (opt?.notRequired ? 'Not Required' : getVal()) : ''}` +
                    `${opt?.by ? ` by ${opt?.by}` : ''}${opt?.date ? ` at ${moment(opt?.date).format(config.defaultDateTimeFormat)}` : ''}` +
                    `${opt?.comment ? `\n Comment / Info: ${opt?.comment}` : ''} \n`
                );
            } else {
                return '';
            }
        };

        let note = '';
        note = '## *Patient Safety Checklist* \n\n';

        let note1H =
            `${checkOptions('triage', '* **Assessment/Triage**:')}` +
            `${checkOptions('obsFirst', '* **Vital signs measured**:')}` +
            `${checkOptions('obsEscalation', '* **Escalated to senior DR**:')}` +
            `${checkOptions('ECGRecorded', '* **ECG recorded**:')}` +
            `${checkOptions('ECGReviewed', '* **ECG reviewed by doctor**:')}` +
            `${checkOptions('undressedAndGown', '* **Undressed and gown**:')}` +
            `${checkOptions('IDBand', '* **ID Band**:')}` +
            `${checkOptions('painScore', '* **Pain Score**:')}` +
            `${checkOptions('analgesia', '* **Analgesia administered (if appropriate)**:')}` +
            `${checkOptions('infectionScreening', '* **Infection control screening**:')}` +
            `${checkOptions('patientAlertsFlags', '* **Check patient alerts/flags - isolation required/escalated**:')}`;

        if (!isUHD) {
            note1H += `${checkOptions('diabetic', '* **Identified as diabetic**:')}`;
        }

        note1H +=
            `${checkOptions('sepsisSuspected', '* **Sepsis suspected**:')}` +
            `${checkOptions('sepsisSeniorSignOff', '* **Sepsis Senior Sign Off**:')}` +
            `${checkOptions('IVAccess', '* **IV access**:')}`;

        if (isUHD) {
            note1H += `${checkOptions('vipScore', '* **Visual Infusion Phlebitis Score**:')}`;
        }

        note1H += `${checkOptions('bloodTests', '* **Blood test**:')}`;

        if (!isUHD) {
            note1H +=
                `${checkOptions('strokeCall', '* **Consider Stroke Call**:')}` +
                `${checkOptions('specTeamsNotified', '* **Speciality teams notified**:')}`;
        }

        if (isUHD) {
            note1H +=
                `${checkOptions('mentalHealth', '* **Mental Health Risk Assessment Required**:')}` +
                `${checkOptions('nursingComments', '* **Nursing Comments**:', true)}`;
        }

        if (isAdult) {
            note1H +=
                `${checkOptions('dementia', '* **4AT Screening for Delirium and Cognitive Impairment**:')}` +
                `${checkOptions('dementiaScore', '* **Dementia identified**:')}` +
                `${checkOptions('clinicalFrailty', '* **Clinical Frailty Assessment Completed**:')}`;
        }

        if (isUHD) {
            note1H += `${checkOptions('sectionDue1', '* **Next Safety Check due**:')}`;
        }

        if (note1H) {
            note += `### ${isUHD ? 'Admission and Initial Safety Check' : '1st Hour Completion Time'}\n` + note1H;
        }

        if (validSection1) {
            let note2H =
                `${checkOptions('obsSecond', '* **Vital signs measured**:')}` +
                `${checkOptions('painScoreAssessedSecond', '* **Pain score assessed**:')}` +
                `${checkOptions('analgesiaSecond', '* **Analgesia administered**:')}` +
                `${checkOptions('nok', '* **NoK identified**:')}` +
                `${checkOptions('nokContacted', '* **NoK contacted**:')}` +
                `${checkOptions('pressureAreas', '* **Pressure areas examined**:')}` +
                `${checkOptions('criticalDrugsGiven', '* **Critical drugs given**:')}`;

            if (!isUHD) {
                note2H +=
                    `${checkOptions('refreshmentSecond', '* **Refreshments offered**:')}` +
                    `${checkOptions('medicinesSecond', '* **Any medicines charted and administered**:')}` +
                    `${checkOptions('diabeticPatientsSecond', '* **Diabetic patients**:')}`;
            }

            if (isUHD) {
                note2H +=
                    `${checkOptions('mentalHealthSecond', '* **Mental Health or challenging behavior observation required**:')}` +
                    `${checkOptions('nursingCommentsSecond', '* **Nursing Comments**:', true)}` +
                    `${checkOptions('IVAccessSecond', '* **IV access**:')}` +
                    `${checkOptions('bloodTestsSecond', '* **Blood test**:')}` +
                    `${checkOptions('vipScoreSecond', '* **Visual Infusion Phlebitis Score**:')}` +
                    `${checkOptions('sectionDue2', '* **Next Safety Check due**:')}`;
            }

            if (note2H) {
                note += `\n### ${isUHD ? '2nd Safety & Welfare Check' : '2nd Hour Completion Time'}\n` + note2H;
            }
        }

        if (validSection2) {
            let note3H =
                `${checkOptions('obsThird', '* **Vital signs measured**:')}` +
                `${checkOptions('painScoreAssessedThird', '* **Pain score assessed**:')}` +
                `${checkOptions('analgesiaThird', '* **Analgesia administered**:')}` +
                `${checkOptions('medicinesThird', '* **Any medicines charted and administered**:')}` +
                `${checkOptions('refreshmentThird', '* **Refreshments offered**:')}` +
                `${checkOptions('patientUpdatePlan', '* **Patient updated with plan**:')}`;

            if (!isUHD) {
                note3H += `${checkOptions('diabeticPatientsThird', '* **Diabetic patients: condition reassessed**:')}`;
            }

            if (isUHD) {
                note3H +=
                    `${checkOptions('mentalHealthThird', '* **Mental Health or challenging behavior observation required**:')}` +
                    `${checkOptions('nursingCommentsThird', '* **Nursing Comments**:', true)}` +
                    `${checkOptions('IVAccessThird', '* **IV access**:')}` +
                    `${checkOptions('vipScoreThird', '* **Visual Infusion Phlebitis Score**:')}` +
                    `${checkOptions('bloodTestsThird', '* **Blood test**:')}` +
                    `${checkOptions('sectionDue3', '* **Next Safety Check due**:')}`;
            }

            if (note3H) {
                note += `### ${isUHD ? '3rd Safety & Welfare Check' : '3rd Hour Completion Time'}\n` + note3H;
            }
        }

        if (validSection3) {
            let note4H =
                `${checkOptions('obsFourth', '* **Vital signs measured**:')}` +
                `${checkOptions('painScoreAssessedFourth', '* **Pain score assessed**:')}` +
                `${checkOptions('analgesiaFourth', '* **Analgesia administered**:')}` +
                `${checkOptions('refreshmentFourth', '* **Refreshments offered**:')}` +
                `${checkOptions('medicinesFourth', '* **Any medicines charted and administered**:')}`;

            if (!isUHD) {
                note4H += `${checkOptions('diabeticPatientsFourth', '* **Diabetic patients: condition reassessed**:')}`;
            }

            if (isUHD) {
                note4H +=
                    `${checkOptions('patientUpdatePlanFourth', '* **Patient updated with plan and welfare checked**:')}` +
                    `${checkOptions('mentalHealthFourth', '* **Mental Health or challenging behavior observation required**:')}` +
                    `${checkOptions('nursingCommentsFourth', '* **Nursing Comments**:', true)}` +
                    `${checkOptions('IVAccessFourth', '* **IV access**:')}` +
                    `${checkOptions('vipScoreFourth', '* **Visual Infusion Phlebitis Score**:')}` +
                    `${checkOptions('bloodTestsFourth', '* **Blood test**:')}` +
                    `${checkOptions('sectionDue4', '* **Next Safety Check due**:')}`;
            }

            if (note4H) {
                note += `### ${isUHD ? '4th Safety & Welfare Check' : '4th Hour Completion Time'}\n` + note4H;
            }
        }

        if (safetyChecklist.nextHours.length > 0) {
            const checkOptionsArray = (field, element, title, onlyComment) => {
                const opt = element[field];
                const optOld = safetyChecklistOld.nextHours?.[element.counter - 5]?.[field];

                const getVal = () => {
                    if (typeof opt?.value === 'string' || typeof opt?.value === 'number') return opt?.value;
                    return opt?.value ? 'Yes' : 'No';
                };

                if (opt && !_.isEqual(opt, optOld)) {
                    return (
                        ` ${title} ${onlyComment ? '' : opt?.notRequired || opt?.value ? 'Check: ' : ''}${onlyComment ? '' : opt?.notRequired ? 'Not Required' : getVal()}` +
                        `${opt?.by ? ` by ${opt?.by}` : ''}${opt?.date ? ` at ${moment(opt?.date).format(config.defaultDateTimeFormat)}` : ''}` +
                        `${opt?.comment ? `\nComment / Info: ${opt?.comment}` : ''}\n`
                    );
                } else {
                    return '';
                }
            };

            _.forEach(safetyChecklist.nextHours, (element) => {
                const counter = element?.counter;

                let noteOther =
                    `${checkOptionsArray('obs', element, '* **Vital signs measured**:')}` +
                    `${checkOptionsArray('painScore', element, '* **Pain score assessed**:')}` +
                    `${checkOptionsArray('analgesia', element, '* **Analgesia administered**:')}` +
                    `${checkOptionsArray('refreshment', element, '* **Refreshments offered**:')}` +
                    `${checkOptionsArray('medicines', element, '* **Any medicines charted and administered**:')}`;

                if (!isUHD) {
                    noteOther += `${checkOptionsArray('diabeticPatients', element, '* **Diabetic patients: condition reassessed**:')}`;
                }

                if (isUHD) {
                    if (counter === 6) {
                        noteOther =
                            `${checkOptionsArray('reasonForDelay', element, '* **Reason for Delay**:', true)}` +
                            `${checkOptionsArray('delayExplained', element, '* **Delay explained to patient**:')}` +
                            `${checkOptionsArray('transferredIntoHospitalBed', element, '* **Has the patient been transferred into a hospital bed**:')}` +
                            `${checkOptionsArray('nurseAware', element, '* **Nurse in charge aware**:')}` +
                            `${checkOptionsArray('medicines', element, '* **Any medicines charted and administered**:')}` +
                            `${checkOptionsArray('mentalHealth', element, '* **Mental Health or challenging behavior observation required**:')}` +
                            `${checkOptionsArray('nursingComments', element, '* **Nursing Comments**:', true)}`;
                    } else {
                        noteOther +=
                            `${checkOptionsArray('patientUpdatePlan', element, '* **Patient updated with plan and welfare checked**:')}` +
                            `${checkOptionsArray('mentalHealth', element, '* **Mental Health or challenging behavior observation required**:')}` +
                            `${checkOptionsArray('nursingComments', element, '* **Nursing Comments**:', true)}`;
                    }

                    noteOther +=
                        `${checkOptionsArray('IVAccess', element, '* **IV access**:')}` +
                        `${checkOptionsArray('vipScore', element, '* **Visual Infusion Phlebitis Score**:')}` +
                        `${checkOptionsArray('bloodTests', element, '* **Blood test**:')}` +
                        `${checkOptionsArray('sectionDue', element, '* **Next Safety Check due**:')}`;
                }

                if (noteOther) {
                    note +=
                        `### ${isUHD ? `${element.counter}th Safety & Welfare Check` : `${element.counter}th Hour Completion Time`}\n` +
                        noteOther;
                }
            });
        }

        let notePRfA =
            `${checkOptions('wardTransfer', '* **Ready for ward transfer**:')}` +
            `${checkOptions('bed', '* **Bed confirmed**:')}` +
            `${checkOptions('propertyFormComplete', '* **Property form complete**:')}`;

        if (notePRfA && !config?.patientSafetyChecklist?.disableReadyToAdmission) {
            note += `Patient Ready for Admission\n` + notePRfA;
        }

        const checkOptions1 = (field, title) => {
            const opt = safetyChecklist.stroke[field];
            const optOld = safetyChecklistOld.stroke[field];
            if (opt && !_.isEqual(opt, optOld)) {
                return ` ${title} ${opt} \n`;
            } else {
                return '';
            }
        };

        let noteRA =
            `${checkOptions1('LOCOrSyncope', '* **Has there been LOC or syncope?: Score**:')}` +
            `${checkOptions1('seizure', '* **Has there been seizure activity?: Score**:')}` +
            `${checkOptions1('asymmetricFacial*', '**Asymmetric facial weakness: Score**:')}` +
            `${checkOptions1('asymmetricArm*', '**Asymmetric arm weakness: Score**:')}` +
            `${checkOptions1('asymmetricLeg*', '**Asymmetric leg weakness: Score**:')}` +
            `${checkOptions1('speech*', '**Speech disturbance: Score**:')}` +
            `${checkOptions1('visual*', '**Visual field deficit: Score**:')}` +
            `${checkOptions1('strokeScore*', '**Total Score**:')}`;

        if (noteRA && !config?.patientSafetyChecklist?.disableRosierAssessment) {
            note += `ROSIER Assessment` + noteRA;
        }

        return note;
    }

    // EDAU forms notes
    static getECSEmailNote(type, mail, text) {
        let note = `## *${type} EMAIL*\n\n`;
        note += `* **Email to**: ${mail.name} (${mail.emails})\n`;
        note += `* **Text**: ${text}\n`;
        return note;
    }

    static getMedicationFormNote(form) {
        let note;
        note = '## *Medical Checklist Questions*\n\n';
        //note += `* **Medical Practitioner**: ${form.who ? form.who : "Not Set"}\n`;
        note += `* **Medical Practitioner**: ${form.who ? getConsultantName(form.who) : 'Not Set'}\n`;
        note += `* **Date and Time**: ${form.when ? moment(form.when).format(config.defaultDateTimeFormat) : 'Not Set'}\n`;
        note += `* **Analgesia**: ${form.analgesia ? 'Yes' : 'No'}\n`;
        note += form.analgesia ? `* Analgesia Description: ${form.analgesiaDescription ? form.analgesiaDescription : '-'}\n` : '';
        note += `* **Medication**: ${form.medication ? 'Yes' : 'No'}\n`;
        note += form.medication ? `* **Medication Description**: ${form.medicationDescription ? form.medicationDescription : '-'}\n` : '';

        return note;
    }

    static getNurseFormNote(form) {
        let note;
        note = '## *Nursing Checklist Questions*\n\n';
        //note += `* **Nurse**: ${form.who ? form.who.__description : "Not Set"}\n`;
        note += `* **Nurse**: ${form.who ? getConsultantName(form.who) : 'Not Set'}\n`;
        note += `* **Date and Time**: ${form.when ? moment(form.when).format(config.defaultDateTimeFormat) : 'Not Set'}\n`;
        note += `* **MRSA Screen Completed**: ${form.mrsa ? 'Yes' : 'No'}\n`;
        note += `* **Property Form Completed**: ${form.property ? 'Yes' : 'No'}\n`;
        note += `* **Description**: ${form.description ? form.description : '-'}\n`;

        return note;
    }

    // Discharge Notes
    static getDischargeNote(
        delayTime,
        dataForm,
        diagnosis,
        treatments,
        investigations,
        dialogType,
        hideDiagTreatInv,
        clinicalCourse,
        dischargeGroup,
        age,
        dischargeTemplate,
    ) {
        const nokQuestionEnabled = getConfig('modules.forms.discharge.enableDestinationNokQuestion', false);
        const nokQuestionCodes = getConfig('modules.forms.discharge.nokQuestionDestinationsCodes', []);

        let note = dialogType === 'dischargeEDAU' ? '## *Patient Discharge from EDAU*\n\n' : '## *Patient Discharge*\n\n';

        if (dischargeTemplate) {
            note += '## *Discharge form Template*\n';
            note += `Template: ${dischargeTemplate?.description || 'UNK'}\n\n`;
        }

        note +=
            `### Delay Reasons\n` +
            `${delayTime <= 4 ? 'No delay\n\n' : ''}` +
            `${
                delayTime > 4
                    ? `* **Primary Delay Reason**: ${
                          dataForm?.delayReasons?.primaryDelayReason ? dataForm?.delayReasons?.primaryDelayReason?.__description : '-'
                      }\n`
                    : ''
            }` +
            `${
                delayTime > 4
                    ? `* **Secondary Delay Reason**: ${
                          dataForm?.delayReasons?.secondaryDelayReason ? dataForm?.delayReasons?.secondaryDelayReason?.__description : '-'
                      }\n`
                    : ''
            }` +
            `${
                delayTime > 4
                    ? `* **Delay Reason Description**: ${
                          dataForm?.delayReasons?.delayReasonText ? dataForm?.delayReasons?.delayReasonText : ''
                      }\n\n`
                    : ''
            }`;

        if (!hideDiagTreatInv) {
            note += Formatter.formatDiagnosisTreatmentInvestigationToNote(diagnosis, treatments, investigations);
        }

        note +=
            `### Discharge From Emergency Care\n` +
            `* **Departure Date and Time**: ${dataForm?.departureDate ? moment(dataForm?.departureDate).format(config.defaultDateFormat) : '-'} ` +
            `${dataForm?.departureTime ? moment(dataForm?.departureTime).format(config.defaultTimeFormat) : '-'}\n` +
            //`* **Discharge Clinician**: ${dataForm?.who ? dataForm?.who?.__description : "-"}\n` +
            `* **Discharge Clinician**: ${dataForm?.who ? getConsultantName(dataForm?.who) : '-'}\n` +
            `* **Discharge Status**: ${dataForm?.dischargeStatus ? dataForm?.dischargeStatus?.__description : '-'}\n` +
            `* **Discharge Destination**: ${dataForm?.dischargeDestination ? dataForm?.dischargeDestination?.__description : '-'}\n` +
            `* **Discharge Follow Up**: ${dataForm?.dischargeFollowUp ? dataForm?.dischargeFollowUp?.__description : '-'}\n\n`;

        if (nokQuestionEnabled && nokQuestionCodes?.includes(dataForm?.dischargeDestination?.snomedCode)) {
            const getValue = (val) => {
                if (_.isNull(val)) return 'N/R';
                return val ? 'Yes' : 'No';
            };
            const isNo = dataForm?.nokContacted === false;

            note += `### Next of Kin\n`;
            note += `* **Have the Next of Kin been contacted**: ${getValue(dataForm?.nokContacted)}${isNo ? '\n' : '\n\n'}`;
            if (isNo) {
                note += `* **Reason & Actions**: ${dataForm?.nokContactedReason || '-'} \n\n`;
            }
        }

        if (dataForm.dischargeDestination?.snomedCode === '19712007') {
            note += `${
                dataForm?.dischargeDestination?.snomedCode === '19712007'
                    ? `* **Transfer To Hospital**: ${dataForm?.hospital ? _.capitalize(dataForm?.hospital?.organizationName) : '-'}\n\n`
                    : '\n\n'
            }`;
        } else if (dialogType === 'dischargeEDAU' && dataForm.dischargeDestination?.snomedCode === '306706006') {
            note += `${
                dataForm?.dischargeDestination?.snomedCode === '306706006'
                    ? `* Transfer to ward: ${dataForm?.ward ? _.startCase(dataForm.ward?.wardName) : '-'}\n\n`
                    : '\n\n'
            }`;
        }
        if (
            dialogType === 'dischargeEDAU' &&
            dataForm.dischargeDestination?.snomedCode !== '306706006' &&
            dataForm.dischargeDestination?.snomedCode !== '19712007'
        ) {
            note += `${
                dataForm?.dischargeDestination?.snomedCode !== '19712007' && dataForm?.dischargeDestination?.snomedCode !== '306706006'
                    ? `* **Discharged to Other**: ${dataForm?.dischargeOther ? dataForm?.dischargeOther : '-'}\n\n`
                    : '\n\n'
            }`;
        }

        if (dialogType === 'dischargeEDAU') {
            note +=
                '## EDAU\n\n' +
                `* **Clinical Conclusion Date and Time**: ${
                    dataForm.clinicalConclusion ? moment(dataForm.clinicalConclusion).format(config.defaultDateTimeFormat) : '-'
                }\n`;

            if (dischargeGroup.includes(dataForm.dischargeDestination?.group)) {
                note +=
                    `### Medical Discharge Checklist\n\n` +
                    `* **Ensure safe home environment**: ${dataForm.safeHomeEnv ? _.startCase(dataForm.safeHomeEnv) : '-'}\n` +
                    `* **Ensure mobility is adequate for discharge**: ${dataForm.mobility ? _.startCase(dataForm.mobility) : '-'}\n` +
                    `* **Analgesia/TTOs have been prescribed**: ${dataForm.analgesia ? _.startCase(dataForm.analgesia) : '-'}\n` +
                    `* **EDS has been completed**: ${dataForm.eds ? _.startCase(dataForm.eds) : '-'}\n` +
                    `* **Advice leaflets given**: ${dataForm.leaflets ? _.startCase(dataForm.leaflets) : '-'}\n\n`;

                note +=
                    `### Instructions for Discharging Staff Member\n\n` +
                    `* **Cannula has been removed**: ${dataForm.cannula ? _.startCase(dataForm.cannula) : '-'}\n` +
                    `* **Transport arranged**: ${dataForm.transport ? _.startCase(dataForm.transport) : '-'}\n` +
                    `${
                        dataForm?.transport === 'yes'
                            ? `* **Hospital Transport Booking Reference**: ${
                                  dataForm.transportDescription ? dataForm.transportDescription : '-'
                              }\n`
                            : ''
                    }` +
                    `* **Patient has received all TTOs**: ${dataForm.tto ? _.startCase(dataForm.tto) : '-'}\n` +
                    `* **Patient has access to home**: ${dataForm.homeAccess ? _.startCase(dataForm.homeAccess) : '-'}\n` +
                    `* **Carer or Next of Kin informed of discharge**: ${dataForm.carerNok ? _.startCase(dataForm.carerNok) : '-'}\n\n`;
            }

            if (dataForm.dischargeDestination?.group === 'Admitted') {
                note += `### SBAR Handover\n\n` + `* **SBAR Handover to be completed**: ${dataForm.sbar ? 'Yes' : 'No'}\n`;
            }
        } else {
            note += `### Message to GP\n`;
            note += `* **Message**: ${clinicalCourse.clinicalCourse ? clinicalCourse?.clinicalCourse : ' - '}\n\n`;
        }

        note +=
            `### Plan and Requested Actions Given\n` +
            `* **Status**: ${clinicalCourse?.actionsGivenStatus ? clinicalCourse?.actionsGivenStatus : ' - '}\n` +
            `* **Actions**: ${clinicalCourse.actionsGiven ? clinicalCourse.actionsGiven : ' - '}\n\n`;

        note +=
            `### Information and Advice Given\n` + `* **Advice**: ${clinicalCourse.actionsGiven ? clinicalCourse.adviceGiven : ' - '}\n\n`;

        note +=
            `### Safeguarding\n` +
            `* **Was the safeguarding team notified about this attendance, because a ChAD, CAMHS, or Social Services referral was made?**: ${dataForm?.chadOrCamhs ? 'Yes' : 'No'}\n`;
        if (age < 18) {
            note += `* **Exclude safeguarding referral details in the EDS/EDN**: ${dataForm?.edSisters ? 'Yes' : 'No'}\n`;
        }
        return note;
    }

    static getEDAUDischargeNote(form) {
        let note = '## *EDAU Departure Checklist*\n\n';
        note += form.option === 'discharged' ? `### Discharged to: ${form.discharged ? form.discharged : '-'}\n` : '';
        note += form.option === 'transferred' ? `### Transferred to a ward: ${form.transferred ? form.transferred : '-'}\n` : '';
        note += form.option === 'other' ? `### Other: ${form.other ? form.other : '-'}\n` : '';

        note +=
            `### Medical Discharge Checklist\n\n` +
            `* **Ensure safe home environment**: ${form.safeHomeEnv ? _.startCase(form.safeHomeEnv) : '-'}\n` +
            `* **Ensure mobility is adequate for discharge**: ${form.mobility ? _.startCase(form.mobility) : '-'}\n` +
            `* **Analgesia/TTOs have been prescribed**: ${form.analgesia ? _.startCase(form.analgesia) : '-'}\n` +
            `* **EDS has been completed**: ${form.eds ? _.startCase(form.eds) : '-'}\n` +
            `* **Advice leaflets given**: ${form.leaflets ? _.startCase(form.leaflets) : '-'}\n\n`;

        note +=
            `### Instructions for Discharging Staff Member\n\n` +
            `* **Cannula has been removed**: ${form.cannula ? _.startCase(form.cannula) : '-'}\n` +
            `* **Transport arranged**: ${form.transport ? _.startCase(form.transport) : '-'}\n` +
            `${
                form?.transport === 'yes'
                    ? `* **Hospital Transport Booking Reference**: ${form.transportDescription ? form.transportDescription : '-'}\n`
                    : ''
            }` +
            `* **Patient has received all TTOs**: ${form.tto ? _.startCase(form.tto) : '-'}\n` +
            `* **Patient has access to home**: ${form.homeAccess ? _.startCase(form.homeAccess) : '-'}\n` +
            `* **Carer or Next of Kin informed of discharge**: ${form.carerNok ? _.startCase(form.carerNok) : '-'}\n\n`;

        return note;
    }

    static getDischargeInpatientNote(discharge, deceased) {
        const dateFormat = getConfig('defaultDateTimeFormat');
        let note = '## *Patient Discharge from ward*\n\n';

        note +=
            `### Medical Discharge Checklist\n\n` +
            `* **Discharge Date and Time**: ${moment(discharge.dischargeDateTime).format(dateFormat)}\n` +
            `* **Discharge Method**: ${discharge?.dischargeMethod?.__description || '-'}\n` +
            `* **Discharge Destination**: ${discharge?.dischargeDestination?.__description || '-'}\n\n`;

        if (deceased) {
            note +=
                `### Patient death\n\n` +
                `* **Death Date and Time**: ${moment(discharge.deathDateTime).format(dateFormat)}\n` +
                `* **Death Location**: ${discharge?.deathLocation || '-'}\n\n`;
        }

        note +=
            `### Waiting list Admissions\n\n` +
            `* **Waiting list outcome**: ${discharge?.waitingListOutcome?.__description || '-'}\n` +
            `* **Waiting list deferred by**: ${discharge?.waitingListDeferredBy?.__description || '-'}\n` +
            `* **Waiting list type**: ${discharge?.waitingListType || '-'}\n\n`;

        return note;
    }

    static getAdmissionToEDAUNote(transferForm, dischargeNote) {
        let text = dischargeNote;
        text = text.replace('Patient Discharge', 'Discharge Patient to Ward');

        text +=
            `\n\n ### Reason for admission\n` +
            `* What is the patient waiting for: ${transferForm?.reason ? transferForm?.reason : '-'}\n` +
            `* If tests are normal, is the patient going home after a period of observation?: ${transferForm?.goingHome ? 'Yes' : 'No'}\n\n`;

        text +=
            `### Admission Personnel\n` +
            `* Admitting Clinician: ${transferForm?.nursesClinicians ? transferForm?.nursesClinicians?.__description : '-'}\n` +
            `* Admitting Specialty: ${transferForm?.specialty ? transferForm?.specialty?.__description : '-'}\n` +
            `* Admitting Consultant: ${transferForm?.consultant ? transferForm?.consultant.familyName + ', ' + transferForm?.consultant.firstName : '-'}\n\n`;

        text +=
            `### Admission Information\n` +
            `* Patient Type: ${transferForm?.patientType ? transferForm?.patientType?.__description : '-'}\n` +
            `* Admission Source: ${transferForm?.admissionSource ? transferForm?.admissionSource?.__description : '-'}\n\n`;

        text +=
            `### Admission Pathway\n` +
            `* Pathway: ${transferForm?.admissionPathway ? _.startCase(transferForm?.admissionPathway) : '-'}\n\n`;

        text +=
            `### Admission Date and Time\n` +
            `* Date and Time: ${
                transferForm?.admissionDate ? moment(transferForm.admissionDate).format(config.defaultDateTimeFormat) : '-'
            }\n\n`;
        return text;
    }

    static getAdmissionToWardNote(admissionForm) {
        const dateFormat = getConfig('defaultDateTimeFormat');
        let note = `## *Patient Admission*\n\n`;

        note +=
            `### Admission \n` +
            `* **Date and time of Admission**: ${moment(admissionForm?.admissionDateTime).format(dateFormat)}\n` +
            `* **Decision to Admit**: ${admissionForm?.decisionToAdmit ? moment(admissionForm?.decisionToAdmit).format(dateFormat) : '-'}\n` +
            `* **Admission Method**: ${admissionForm?.admissionMethod?.__description || '-'}\n` +
            `* **Management Intention**: ${admissionForm?.managementIntention?.__description || '-'}\n` +
            `* **Admission Source**: ${admissionForm?.admissionSource?.__description || '-'}\n` +
            `* **Admitted from location**: ${admissionForm?.admittedFromLocation || '-'}\n\n`;

        note +=
            `### Ward \n` +
            `${admissionForm?.destination?.hospitalId ? `* **Hospital**: ${admissionForm?.destination?.hospitalId} \n` : ''}` +
            `* **Ward**: ${admissionForm?.destination?.wardName || '-'}\n` +
            `* **Bay**: ${admissionForm?.destination?.bayName || '-'}\n` +
            `* **Bed**: ${admissionForm?.destination?.bedName || '-'}\n` +
            `* **Specialty**: ${admissionForm?.specialty?.__description || '-'}\n` +
            `* **Consultant**: ${admissionForm?.consultant ? `${admissionForm.consultant.familyName}, ${admissionForm.consultant.firstName}` : '-'}\n` +
            `* **Category**: ${admissionForm?.category?.__description || '-'}\n` +
            `* **Intended Procedure**: ${admissionForm?.intendedProcedure || '-'}\n\n`;

        note +=
            `### Stay \n` +
            `* **Planned Stay**: ${admissionForm?.plannedStay || '-'}\n` +
            `* **Intended Discharge Date**: ${admissionForm?.intendedDischargeDate ? moment(admissionForm?.intendedDischargeDate).format(dateFormat) : '-'}\n` +
            `* **Confirmed Discharge Date**: ${admissionForm?.confirmedDischargeDate ? moment(admissionForm?.confirmedDischargeDate).format(dateFormat) : '-'}\n` +
            `* **Reason for Admission**: ${admissionForm?.reasonForAdmission || '-'}\n` +
            `* **Referral Details**: ${admissionForm?.referralDetails || '-'}\n`;
        return note;
    }

    static getTransferNote(transfer, isED, hospitalList) {
        const dateFormat = getConfig('defaultDateTimeFormat');
        let note = `## *${transfer.transferType === 'ward' ? 'Ward' : 'Clinician / Specialty'} Transfer*\n\n`;
        const hospital = _.find(hospitalList, (h) => h?.__id === transfer?.destination?.hospitalId);

        if (transfer.transferType === 'ward') {
            note +=
                `* **Transfer Date Time**: ${moment(transfer?.transferDateTime).format(dateFormat)}\n` +
                `${transfer?.destination?.hospitalId ? `* **Hospital**: ${hospital?.description} \n` : ''}`;
            note += `* **Ward**: ${transfer?.destination?.wardName || '-'}\n`;
            note += transfer?.destination?.bayName ? `* **Bay**: ${transfer?.destination?.bayName || 'N/A'}\n` : '';
            note += transfer?.destination?.bedName ? `* **Bed**: ${transfer?.destination?.bedName || 'N/A'}\n` : '';
        } else {
            note += `* **Transfer Date Time**: ${moment(transfer?.transferDateTime).format(dateFormat)}\n`;
            if (isED) {
                note += `* **Consultant**: ${transfer?.consultant ? transfer.consultant.__description : '-'}\n`;
            } else {
                note +=
                    `* **Specialty**: ${transfer?.specialty?.__description || '-'}\n` +
                    `* **Consultant**: ${transfer?.consultant ? `${transfer.consultant.familyName}, ${transfer.consultant.firstName}` : '-'}\n`;
            }
        }
        return note;
    }

    // Ice Note
    static getInvestigationsNote(iceReportsCount) {
        let note = `## *Investigations Reports*\n\n`;
        note += `* **Action to confirm all Investigations Reports has been triggered.**\n`;
        note += `* **Current counter state**: ${iceReportsCount}`;

        return note;
    }

    // Sepsis
    static getSepsisPathwayNote(sObj) {
        const getValue = (value) => {
            let valueText = 'N/A';
            if (value) valueText = 'Yes';
            if (value === null) valueText = 'No';
            return valueText;
        };
        const getSepsisToolStatus = (status) => {
            let statusText = '-';
            switch (status) {
                case 0:
                    statusText = 'incomplete';
                    break;
                case 1:
                    statusText = 'complete';
                    break;
                case 2:
                    statusText = 'not required';
                    break;
                default:
                    break;
            }
            return statusText;
        };
        const getAuthor = (obj, field) => {
            let vName = '-';
            let vDate = '';

            if (obj[field + 'CheckedBy']) {
                vName = obj[field + 'CheckedBy'];
                vDate = moment(obj[field + 'CheckedByDate'])?.format('DD/MM/YYYY HH:mm');
            }
            return vName + ' ' + vDate;
        };
        const reasonsSepsisTool = (obj, field) => {
            let NAReason = '';
            if (obj[field + 'NAReason']) {
                NAReason = obj[field + 'NAReason'];
                return NAReason;
            }
            return ' ';
        };
        const reasonsSepsisToolNA = (obj, field) => {
            let NAReason = '';
            if (obj[field + 'Reason']) {
                NAReason = obj[field + 'Reason'];
                return NAReason;
            }
            return ' ';
        };
        const getValueBoolean = (value) => {
            let mapped;
            switch (value) {
                case null:
                case '':
                    mapped = '-';
                    break;
                case true:
                    mapped = 'Yes';
                    break;
                case false:
                    mapped = 'No';
                    break;
                default:
                    mapped = value;
            }
            return mapped;
        };

        let note;
        note = `## *Sepsis Pathway*\n\n`;
        if (sObj.paeds) {
            note += `|Created|User|Oxygen|Bp & Urin|Iv/Io|Blood|Meds|Fluid|Status|Complete/Stop|User|\n`;
            note += `|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|\n`;
            note += `|${moment(sObj.dateCreate).format('DD/MM/YYYY HH:mm')}|${sObj.userCreate}|${getValue(sObj.oxygen)}|${getValue(sObj.balance)}|${getValue(sObj.ivio)}|${getValue(sObj.bloods)}|${getValue(sObj.mbc)}|${getValue(sObj.fluids)}|${getSepsisToolStatus(sObj.statusObj)}|${sObj.dateEnd ? moment(sObj.dateEnd).format('DD/MM/YYYY HH:mm') : ''}|${sObj.userEnd ? sObj.userEnd : ''}|\n`;
            note += `|||${getAuthor(sObj, 'oxygen')}|${getAuthor(sObj, 'balance')}|${getAuthor(sObj, 'ivio')}|${getAuthor(sObj, 'bloods')}|${getAuthor(sObj, 'mbc')}|${getAuthor(sObj, 'fluids')}||||\n`;
            note += `|||${reasonsSepsisTool(sObj, 'oxygen')}|${reasonsSepsisTool(sObj, 'balance')}|${reasonsSepsisTool(sObj, 'ivio')}|${reasonsSepsisTool(sObj, 'bloods')}|${reasonsSepsisTool(sObj, 'mbc')}|${reasonsSepsisTool(sObj, 'fluids')}|${reasonsSepsisToolNA(sObj, 'notRequired')}| \n\n\n`;

            note += `||||\n`;
            note += `|:---:|:---:|:---:|\n`;
            note += `|HR or RR still above age specific normal range or CRT over3 seconds?|**${getValueBoolean(sObj.hrorrr)}**|(${getAuthor(sObj, sObj.hrorrr ? 'hrorrr' : 'hrorrrNAReason')})|\n`;
            note += `|Venous (or arterial) Lactate over 2?|**${getValueBoolean(sObj.venous)}**|(${getAuthor(sObj, sObj.venous ? 'venous' : 'venousNAReason')})|\n`;
            note += `|Signs of fluid overload ( hepatomegaly, desaturations, crepitations)?|**${getValueBoolean(sObj.fluidoverload)}**|(${getAuthor(sObj, sObj.fluidoverload ? 'fluidoverload' : 'fluidoverloadNAReason')})|\n`;
        } else {
            note += `|Created|User|Neutropenic|Inform ST3|Oxygen|Fluids|Bloods|MBC|Antibiotics|Balance|Status|Complete/Stop|User|\n`;
            note += `|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|:---:|\n`;
            note += `|${moment(sObj.dateCreate).format('DD/MM/YYYY HH:mm')}|${sObj.userCreate}|${getValue(sObj.neutropenic)}|${getValue(sObj.informST3)}|${getValue(sObj.oxygen)}|${getValue(sObj.fluids)}|${getValue(sObj.bloods)}|${getValue(sObj.mbc)}|${getValue(sObj.antibiotics)}|${getValue(sObj.balance)}|${getSepsisToolStatus(sObj.statusObj)}|${sObj.dateEnd ? moment(sObj.dateEnd).format('DD/MM/YYYY HH:mm') : ''}|${sObj.userEnd ? sObj.userEnd : ''}|\n`;
            note += `||||${getAuthor(sObj, 'informST3')}|${getAuthor(sObj, 'oxygen')}|${getAuthor(sObj, 'fluids')}|${getAuthor(sObj, 'fluids')}|${getAuthor(sObj, 'mbc')}|${getAuthor(sObj, 'antibiotics')}|${getAuthor(sObj, 'balance')}||||\n`;
            note += `||||${reasonsSepsisTool(sObj, 'informST3')}|${reasonsSepsisTool(sObj, 'oxygen')}|${reasonsSepsisTool(sObj, 'fluids')}|${reasonsSepsisTool(sObj, 'fluids')}|${reasonsSepsisTool(sObj, 'mbc')}|${reasonsSepsisTool(sObj, 'antibiotics')}|${reasonsSepsisTool(sObj, 'balance')}|${reasonsSepsisToolNA(sObj, 'notRequired')}|||\n`;
        }

        return note;
    }

    static getSepsisToolNote(sepsis) {
        // Header text of note
        const prepareOverall = (sepsis) => {
            const obsUsedDate = sepsis.obsUsed ? moment(sepsis.obsUsed).format(config.defaultDateTimeFormat) : 'No observation was made';
            let reasons =
                sepsis.status === SEPSIS_TOOL_TYPE.NOT_REQUIRED
                    ? `* Reason for Not Required Sepsis Pathway:\n *${sepsis.reason}*`
                    : sepsis.status === SEPSIS_TOOL_TYPE.COMPLETED_NOT_REQUIRED
                      ? `* Reason for Starting Sepsis Pathway:\n *${sepsis.reason}*`
                      : '';

            return `* Date of observations used: ${obsUsedDate}
* Form Type: ${sepsis.type}
* Form Status: ${convertStatus(sepsis.status)}
${reasons}`;
        };

        // Body text of note
        const createSummary = (sepsis) => {
            let summary = '';
            if (sepsis.type === 'adult') {
                summary += '\n ### Due to an infection\n';
                summary += sepsis.infection.arthritisOrInfection ? '* &#9745; Cellulitis / septic arthritis / infected wound\n' : '';
                summary += sepsis.infection.deviceInfection ? '* &#9745; Device-related infection\n' : '';
                summary += sepsis.infection.meningitis ? '* &#9745; Meningitis\n' : '';
                summary += sepsis.infection.painOrDistension ? '* &#9745; Abdominal pain or distension\n' : '';
                summary += sepsis.infection.pneumonia ? '* &#9745; Pneumonia\n' : '';
                summary += sepsis.infection.sourceUnclear ? '* &#9745; Yes, but source unclear at present\n' : '';
                summary += sepsis.infection.urinaryInfection ? '* &#9745; Urinary Tract Infection\n' : '';
                summary += sepsis.infection.other ? `* &#9745; Other\nDescription: ${sepsis.infection.otherSpecify}\n` : '';
                if (!checkIfSectionSelected(sepsis.infection)) {
                    summary += `* Nothing Selected\n`;
                }

                summary += '\n ### Red flag present\n';
                summary += sepsis.redFlag.chemotherapy
                    ? '* &#9745; Chemotherapy in last 6/52 or haematological disorder causing neutropenia\n'
                    : '';
                summary += sepsis.redFlag.heartRate ? '* &#9745; Heart Rate (HR) >= 130 per minute\n' : '';
                summary += sepsis.redFlag.lactate ? '* &#9745; Lactate >= 2 mmol/L\n' : '';
                summary += sepsis.redFlag.needOxygen ? '* &#9745; Needs oxygen to keep Sp02 >= 92% (88% in COPD)\n' : '';
                summary += sepsis.redFlag.respRate ? '* &#9745; Respiratory Rate (RR) >= 25 per minute\n' : '';
                summary += sepsis.redFlag.responds ? '* &#9745; Responds only to voice, pain or is unresponsive\n' : '';
                summary += sepsis.redFlag.skinIssues ? '* &#9745; Non-blanching rash, mottled / ashen / cyanotic\n' : '';
                summary += sepsis.redFlag.systolicBP ? '* &#9745; Systolic BP =< 90 mmHg (or drop >40 from normal\n' : '';
                summary += sepsis.redFlag.urine ? '* &#9745; Urine output less than 0.5 ml/kg/hr or no UO in last 18 hrs\n' : '';
                if (!checkIfSectionSelected(sepsis.redFlag)) {
                    summary += `* Nothing Selected\n`;
                }

                summary += '\n ### Moderate risk criteria\n';
                summary += sepsis.moderateRiskCriteria.alteredMentalState
                    ? '* &#9745; Relatives concerned about altered mental state\n'
                    : '';
                summary += sepsis.moderateRiskCriteria.deteriorationFunctionalAbility
                    ? '* &#9745; Acute deterioration in functional ability\n'
                    : '';
                summary += sepsis.moderateRiskCriteria.heartRateOrArrhythmia ? '* &#9745; Heart rate 91-130 OR new arrhythmia\n' : '';
                summary += sepsis.moderateRiskCriteria.immunosuppressed ? '* &#9745; Immunosuppressed\n' : '';
                summary += sepsis.moderateRiskCriteria.notPassedUrine ? '* &#9745; Not passed urine for 12-18 hrs\n' : '';
                summary += sepsis.moderateRiskCriteria.respRateHardBreath ? '* &#9745; Respiratory rate 21-24 OR breathing hard\n' : '';
                summary += sepsis.moderateRiskCriteria.rigors ? '* &#9745; Rigors\n' : '';
                summary += sepsis.moderateRiskCriteria.systolic ? '* &#9745; Systolic BP 91-100 mmHg\n' : '';
                summary += sepsis.moderateRiskCriteria.temperature ? '* &#9745; Temperature <36C\n' : '';
                summary += sepsis.moderateRiskCriteria.traumaSurgery ? '* &#9745; Trauma, surgery or procedure in last 6 weeks\n' : '';
                summary += sepsis.moderateRiskCriteria.woundDeviceSkinInfection
                    ? '* &#9745; Clinical signs of wound, device or skin infection\n'
                    : '';
                if (!checkIfSectionSelected(sepsis.moderateRiskCriteria)) {
                    summary += `* Nothing Selected\n`;
                }

                if (checkIfSectionSelected(sepsis.moderateRiskCriteria)) {
                    summary += '\n ### Other\n';
                    summary += sepsis.sendBloods.date ? `* Blood sent: ${sepsis.sendBloods.check ? 'Yes\n' : 'No\n'}` : '';
                    summary += sepsis.doctorReview.date
                        ? `* Senior doctor has been contacted: ${sepsis.doctorReview.check ? 'Yes\n' : 'No\n'}`
                        : '';
                    summary += sepsis.clinicianAttended.date
                        ? `* Clinician was attended: ${sepsis.clinicianAttended.check ? 'Yes\n' : 'No\n'}`
                        : '';
                    summary += sepsis.aki ? `* Aki: ${sepsis.aki === 'yes' ? 'Yes\n' : 'No\n'}` : '';
                    summary += sepsis.antimicrobial.date
                        ? `* Clinician to make antimicrobial: ${sepsis.antimicrobial.check ? 'Yes\n' : 'No\n'}`
                        : '';
                    if (
                        !sepsis.sendBloods.date &&
                        !sepsis.doctorReview.date &&
                        !sepsis.clinicianAttended.date &&
                        !sepsis.aki &&
                        !sepsis.antimicrobial.date
                    ) {
                        summary += `* Nothing Selected\n`;
                    }
                }
            }

            if (sepsis.type === 'paeds') {
                summary += '\n ### Could this child have an infection\n';
                summary += sepsis.temperature ? `* Temperature under 36 or over 38.5°C: ${sepsis.temperature}\n` : '';
                summary += sepsis.tachycardia ? `* Tachycardia (áHR). Tachypnoea (áRR): ${sepsis.tachycardia}\n` : '';
                summary += sepsis.heartRate ? `* HR over ${sepsisToolAgeParameter(sepsis.age, 'HR')}: ${sepsis.heartRate}\n` : '';
                summary += sepsis.respRate ? `* RR over ${sepsisToolAgeParameter(sepsis.age, 'RR')}: ${sepsis.respRate}\n` : '';
                if (!sepsis.temperature && !sepsis.tachycardia && !sepsis.heartRate && !sepsis.respRate) {
                    summary += `* Nothing Selected\n`;
                }

                summary += '\n ### Second section\n';
                summary += sepsis.mentalState
                    ? `* Altered mental state: Sleepy, floppy, lethargic or irritable: ${sepsis.mentalState}\n`
                    : '';
                summary += sepsis.skin
                    ? `* Mottled skin OR prolonged capillary refill time OR ‘flash’ capillary refill time: ${sepsis.skin}\n`
                    : '';
                summary += sepsis.clinicalConcern ? `* Clinical concern regarding possible sepsis: ${sepsis.clinicalConcern}\n` : '';
                summary += sepsis.source ? `* Source: ${sepsis.source}\nDescription: *${sepsis.sourceText}*` : '';
                if (!sepsis.mentalState && !sepsis.skin && !sepsis.clinicalConcern && !sepsis.source) {
                    summary += `* Nothing Selected\n`;
                }
            }
            return summary;
        };

        let note;
        note = '## *Sepsis Tool*\n\n';
        note += sepsis.type === 'peads' ? `**Age of the patient during the study: ${sepsis.age}**\n` : '';
        note += prepareOverall(sepsis);
        note += createSummary(sepsis);

        return note;
    }

    static getCancelSepsisToolNote(selectedSepsis) {
        let note = '## *Sepsis Tool*\n\n';
        note += `* Sepsis Tool at ${
            moment(selectedSepsis.date, config.defaultDateTimeFormat).isValid()
                ? selectedSepsis.date
                : moment(selectedSepsis.date).format(config.defaultDateTimeFormat)
        } have been cancelled`;

        return note;
    }

    // Other
    static getDiagTreatInvestDependencyNote(modifications, patientWithoutModify) {
        const getLocalDateText = (obj, type) => {
            const local = UtilsMomentJS.getLocalDateFromUTCECDSFormat(obj.procedureDate, obj.procedureTime);

            if (local) {
                return type === 'date' ? local.format(config.defaultDateFormat) : local.format(config.defaultTimeFormat);
            }
            return '-';
        };
        const startText = '## *Diagnosis, Treatment and Investigations, Dependency*\n\n';
        const diag = _.find(modifications, { name: 'diagnosis' });
        const triage = _.find(modifications, { name: 'triage' });

        let note = startText;
        // Add Diagnosis Notes
        if (diag && !_.isEqual(diag.value.primary, patientWithoutModify?.diagnosis?.primary)) {
            note += `### Change in Diagnosis:\n`;
            if (!_.isEmpty(diag.value?.primary)) {
                note += `|Diagnosis|Procedure Date and Time|Diagnosis Qualifier|Sequence Number|\n|:---|:---:|:---:|:---:|\n`;
                _.forEach(diag.value.primary, (element) => {
                    if (element.value) {
                        note += `|${element.value[element?.value?.diagnosisMax ? 'snomedFullySpecifiedName' : 'snomedDescription']}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|`;
                        note += `${element.qualifier !== null ? element.qualifier.snomedDescription : '----'}|${element.sequenceNumber === '' ? '----' : element.sequenceNumber}|\n`;
                    }
                });
            } else {
                note += `The field has been cleared\n`;
            }
        }

        if (triage) {
            const treatment = triage ? triage.value.treatmentAndManagementPlan : null;
            const investigation = triage ? triage.value.investigations : null;
            const dependency = triage.value['dependency'] ? triage.value['dependency'] : null;

            // Add Treatment Notes
            if (treatment && !_.isEqual(treatment, patientWithoutModify?.triage?.treatmentAndManagementPlan)) {
                note += `### Change in Treatments:\n`;
                if (!_.isEmpty(treatment)) {
                    note += `|Treatment|Procedure Date and Time|\n|:---|:---:|\n`;
                    _.forEach(treatment, (element) => {
                        if (element.value) {
                            note += `|${element.value.__description}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|\n`;
                        }
                    });
                } else {
                    note += `The field has been cleared\n`;
                }
            }

            // Add Investigation Notes
            if (investigation && !_.isEqual(investigation, patientWithoutModify?.triage?.investigations)) {
                note += `### Change in Investigations\n`;

                if (!_.isEmpty(investigation)) {
                    note += `|Investigation|Procedure Date and Time|\n|:---|:---:|\n`;
                    _.forEach(investigation, (element) => {
                        if (element.value) {
                            note += `|${element.value.__description}|${element.procedureDate === '' ? '----' : `${getLocalDateText(element, 'date')} ${getLocalDateText(element, 'time')}`}|\n`;
                        }
                    });
                } else {
                    note += `The field has been cleared\n`;
                }
            }

            if (!_.isEqual(dependency, patientWithoutModify?.triage?.dependency)) {
                note += `### Change in Dependency:\n`;
                note += dependency ? `${dependency.__description ? dependency.__description : '-'} \n\n` : `The field has been cleared\n\n`;
            }
        }
        return note === startText ? '' : note;
    }

    static getCliniciansNote(modifications, patientWithoutModify) {
        const generateChangeText = (category, newObj, oldObj) => {
            let changeText =
                `* ${category} changed from ${
                    _.isNil(oldObj)
                        ? 'Not Set'
                        : `${
                              !_.isNil(oldObj?.who)
                                  ? //? oldObj?.who?.__description : "Not Set"},` +
                                    getConsultantName(oldObj.who)
                                  : 'Not Set'
                          },` +
                          ` Date and Time ${!_.isNil(oldObj?.when) ? moment(oldObj?.when).format(config.defaultDateTimeFormat) : 'Not Set'}`
                }` +
                ` to ${
                    _.isNil(newObj)
                        ? 'Not Set'
                        : `${
                              !_.isNil(newObj.who)
                                  ? //? newObj?.who?.__description : "Not Set"},` +
                                    getConsultantName(newObj?.who)
                                  : 'Not Set'
                          },` +
                          ` Date and Time ${!_.isNil(newObj?.when) ? moment(newObj?.when).format(config.defaultDateTimeFormat) : 'Not Set'}`
                }\n`;

            if (newObj.text) {
                changeText += `\n &emsp;&emsp; Note: ${newObj.text}\n`;
            }
            if (newObj.speciality) {
                changeText += `\n &emsp;&emsp; Speciality: ${newObj.speciality.__description}\n`;
            }
            return changeText;
        };

        const generateChangeTextClinician = (category, newObj, oldObj) => {
            let changeText =
                `* ${category} changed from ${
                    _.isNil(oldObj)
                        ? 'Not Set'
                        : `${!_.isNil(oldObj?.consultant) ? `${getConsultantName(oldObj?.consultant)}` : 'Not Set'},` +
                          ` Date and Time ${!_.isNil(oldObj?.when) ? moment(oldObj?.when).format(config.defaultDateTimeFormat) : 'Not Set'}`
                }` +
                ` to ${
                    _.isNil(newObj)
                        ? 'Not Set'
                        : `${!_.isNil(newObj?.consultant) ? `${getConsultantName(oldObj?.consultant)}` : 'Not Set'},` +
                          ` Date and Time ${!_.isNil(newObj?.when) ? moment(newObj?.when).format(config.defaultDateTimeFormat) : 'Not Set'}`
                }\n`;

            if (newObj.speciality) {
                changeText += `\n &emsp;&emsp; Speciality: ${newObj.speciality.__description}\n`;
            }
            return changeText;
        };

        let check = false;
        const firstClinician = _.find(modifications, { name: 'firstClinician' });
        const seniorReview = _.find(modifications, { name: 'seniorReview' });
        const handover = _.find(modifications, { name: 'handover' });
        const currentClinician = _.find(modifications, { name: 'currentClinician' });
        const ambulanceHandover = _.find(modifications, { name: 'ambulanceHandover' });
        const nurseHandover = _.find(modifications, { name: 'studies.edau.nurseHandover' });
        const currentNurse = _.find(modifications, { name: 'studies.edau.currentNurse' });
        const clinicianIP = _.find(modifications, { name: 'currentClinicianIP' });
        const currentNurseIP = _.find(modifications, { name: 'currentNurse' });

        let note = '## *Clinicians*\n\n';
        if (firstClinician && !_.isEqual(firstClinician.value, patientWithoutModify?.firstClinician)) {
            note += generateChangeText('First Clerking Clinician', firstClinician.value, patientWithoutModify?.firstClinician);
            check = true;
        }
        if (clinicianIP && !_.isEqual(clinicianIP.value, patientWithoutModify?.currentClinicianIP)) {
            note += generateChangeTextClinician('Clinician', clinicianIP.value, patientWithoutModify?.currentClinicianIP);
            check = true;
        }
        if (currentNurseIP && !_.isEqual(currentNurseIP.value, patientWithoutModify?.currentNurse)) {
            note += generateChangeText('Nurse', currentNurseIP.value, patientWithoutModify?.currentNurse);
            check = true;
        }
        if (seniorReview && !_.isEqual(seniorReview.value, patientWithoutModify?.seniorReview)) {
            note += generateChangeText('Senior Review', seniorReview.value, patientWithoutModify?.seniorReview);
            check = true;
        }
        if (handover && !_.isEqual(handover.value, patientWithoutModify?.handover)) {
            note += generateChangeText('Handover', handover.value, patientWithoutModify?.handover);
            check = true;
        }
        if (currentClinician && !_.isEqual(currentClinician.value, patientWithoutModify?.currentClinician)) {
            note +=
                `* Current Clinician changed from ${_.isNil(patientWithoutModify?.currentClinician) ? 'Not Set' : `${patientWithoutModify?.currentClinician?.__description},`}` +
                //` to ${_.isNil(currentClinician.value) ? "Not Set" : `${currentClinician.value?.__description}`}\n`;
                ` to ${_.isNil(currentClinician.value) ? 'Not Set' : `${getConsultantName(currentClinician.value)}`}\n`;
            check = true;
        }
        if (ambulanceHandover && !_.isEqual(ambulanceHandover.value, patientWithoutModify?.ambulanceHandover)) {
            note += generateChangeText('Ambulance Handover', ambulanceHandover.value, patientWithoutModify?.ambulanceHandover);
            check = true;
        }
        if (nurseHandover && !_.isEqual(nurseHandover.value, patientWithoutModify?.studies?.edau?.nurseHandover)) {
            note += generateChangeText('Nurse Handover', nurseHandover.value, patientWithoutModify?.studies?.edau?.nurseHandover);
            check = true;
        }
        if (currentNurse && !_.isEqual(currentNurse.value, patientWithoutModify?.studies?.edau?.currentNurse)) {
            note += generateChangeText('Current Nurse', currentNurse.value, patientWithoutModify?.studies?.edau?.currentNurse);
            check = true;
        }

        return note;
    }

    static getCurrentClinicalBulk(clinician) {
        let note = '## *Clinician*\n\n';
        //note += `* Current Clinician changed to ${clinician?.__description}\n`;
        note += `* Current Clinician changed to ${getConsultantName(clinician)}\n`;
        return note;
    }

    static getConsultantSpecialtyTransferBulk(consultant, specialty) {
        let note = '## *Specialty*\n\n';
        //note += `* Current Consultant changed to ${consultant.familyName}, ${consultant.firstName}\n`;
        note += `* Current Consultant changed to ${getConsultantName(consultant)}\n`;
        note += `* Specialty changed to ${specialty.__description}\n`;
        return note;
    }

    static getSafeguardingChangeNote(value) {
        let note = '## *Change in Safeguarding*\n\n';
        note += `|Description|Code|\n|:---|---:|\n`;
        value.forEach((element) => {
            if (element.snomedDescription) {
                note += `|${element.snomedDescription}|`;
                note += `${element.snomedCode !== null ? element.snomedCode : '----'}|\n`;
            }
        });

        return note;
    }

    static getTemporaryTransferFormEDAUNote(reason) {
        let note = `## *Temporary Transfer from EDAU*\n\n`;
        note += `* **The patient was a temporary transfer from EDAU to ED.**\n`;
        note += `* **Reason**: ${reason}`;

        return note;
    }

    // ECDS
    static getECDSNote(
        patientPathway,
        patientIdentity,
        patientCharacteristics,
        mentalHealth,
        gp,
        attendanceLocation,
        ambulanceDetails,
        attendanceActivity,
        timeOfTreatment,
        assessment,
        observations,
        findings,
        history,
        injury,
        agreement,
        care,
        diagnosis,
        investigations,
        treatments,
        otherService,
        discharge,
        outbreak,
        social,
        eMed,
        nhsStatus,
        getDict,
        ecdsVersion,
    ) {
        /*
         * Helper methods and variables
         * */
        const localInitAss = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
            attendanceActivity.EmergencyCareInitialAssessmentDate,
            attendanceActivity.EmergencyCareInitialAssessmentTime,
        );
        const localSeen = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
            attendanceActivity.EmergencyCareDateSeenForTreatment,
            attendanceActivity.EmergencyCareTimeSeenForTreatment,
        );
        const localArrival = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
            attendanceActivity?.EmergencyCareArrivalDate,
            attendanceActivity?.EmergencyCareArrivalTime,
        );

        const resolveDict = (code, dict, other = false, specialField = null) => {
            const predicate = other ? '__code' : 'snomedCode';
            const check = _.find(getDict(dict), { [predicate]: code });
            if (check && code) {
                return check[specialField || '__description'];
            }
            return '-';
        };

        const getPtName = (ptName) => {
            if (ptName?.PersonFullName) {
                return ptName.PersonFullName;
            } else if (ptName?.PersonRequestedName) {
                return ptName.PersonRequestedName;
            } else if (ptName?.PersonName_Structured) {
                const { PersonGivenName, PersonFamilyName } = ptName.PersonName_Structured;
                return `${PersonGivenName} ${PersonFamilyName}`;
            } else {
                return '-';
            }
        };

        const getPtAddress = (ptAddress) => {
            const address = (arr) => {
                if (!arr || _.isEmpty(arr)) return '-';
                let text = '';
                _.forEach(arr, (element) => {
                    text += element + ' ';
                });
                return text;
            };
            return ptAddress?.UnstructuredAddress || address(ptAddress?.StructuredAddress);
        };

        const getLocalDate = (timestamp, date, time, type) => {
            if (!_.isEmpty(timestamp) && timestamp !== '-') {
                if (type) {
                    const isUtc = moment(timestamp).isUTC();
                    if (type === 'timestamp')
                        return isUtc
                            ? moment(timestamp).format(config.defaultDateTimeFormat)
                            : moment.utc(timestamp, config.ecdsDateTimeFormat).local().format(config.defaultDateTimeFormat);
                    return isUtc
                        ? moment(timestamp).format(type === 'date' ? config.defaultDateFormat : config.defaultTimeFormat)
                        : moment
                              .utc(timestamp, config.ecdsDateTimeFormat)
                              .local()
                              .format(type === 'date' ? config.defaultDateFormat : config.defaultTimeFormat);
                }
                return timestamp;
            } else if (!_.isEmpty(date) && !_.isEmpty(time) && date !== '-' && time !== '-') {
                const local = UtilsMomentJS.getLocalDateFromUTCECDSFormat(date, time);
                if (local) {
                    if (type === 'timestamp') return local.format(config.defaultDateTimeFormat);
                    return local.format(type === 'date' ? config.defaultDateFormat : config.defaultTimeFormat);
                }
            }
            return '-';
        };

        const getMental = () => {
            if (!_.isEmpty(mentalHealth)) {
                let text = '';
                _.forEach(mentalHealth, (element) => {
                    const startTime = getLocalDate(
                        element?.MentalHealthActLegalStatusClassificationAssignmentPeriodStartTimestamp,
                        element?.StartDate_MentalHealthActLegalStatusClassificationAssignmentPeriod,
                        element?.StartTime_MentalHealthActLegalStatusClassificationAssignmentPeriod,
                        'timestamp',
                    );
                    const expiryTime = getLocalDate(
                        element?.MentalHealthActLegalStatusClassificationExpiryTimestamp,
                        element?.ExpiryDate_MentalHealthActLegalStatusClassification,
                        element?.ExpiryTime_MentalHealthActLegalStatusClassification,
                        'timestamp',
                    );
                    text += `* ${resolveDict(element.MentalHealthActLegalStatusClassificationCode, 'mentalHealthClassification', true)} | Start Date and Time, ${startTime}, | Expiry Date and Time ${expiryTime}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getSocial = () => {
            if (!_.isEmpty(social)) {
                let text = '';
                _.forEach(social, (element) => {
                    const recorded = getLocalDate(element?.SocPerRecordedTimestamp, null, null, 'timestamp');
                    text += `* ${resolveDict(element.SocPer_SnomedCt, 'socialAndPersonal')} | Recorded Timestamp, ${recorded}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getAssessment = () => {
            if (!_.isEmpty(assessment)) {
                let text = '';
                _.forEach(assessment, (element) => {
                    const obj = resolveDict(element.CodedAssessmentToolType_SnomedCt, 'scoredAssessment');
                    text += `* ${obj} Date and Time ${
                        element.AssessmentToolValidationTimestamp
                            ? moment.utc(element.AssessmentToolValidationTimestamp).local().format(config.defaultDateTimeFormat)
                            : '-'
                    } Score ${element.PersonScore}\n`;
                });
                return text;
            }
            return 'No Observation\n';
        };

        const getObservation = () => {
            if (!_.isEmpty(observations)) {
                let text = '';
                _.forEach(observations, (element) => {
                    const obj = resolveDict(element.CodedObservation_SnomedCt, 'clinicalObservations');
                    text +=
                        `* ${obj} Date and Time ${
                            element.CodedObservationTimeStamp
                                ? moment.utc(element.CodedObservationTimeStamp).local().format(config.defaultDateTimeFormat)
                                : '-'
                        }` +
                        ` Value ${element.ObservationValue ? element.ObservationValue : '-'}` +
                        ` Unit ${element.UCUMunitOfMeasurement ? element.UCUMunitOfMeasurement : '-'}\n`;
                });
                return text;
            }
            return 'No Observation\n';
        };

        const getFindings = () => {
            if (!_.isEmpty(findings)) {
                let text = '';
                _.forEach(findings, (element) => {
                    const obj = resolveDict(element.CodedFinding_SnomedCt, 'clinicalFindings');
                    text += `* ${obj} Date and Time ${
                        element.CodedFinding_TimeStamp
                            ? moment.utc(element.CodedFinding_TimeStamp).local().format(config.defaultDateTimeFormat)
                            : '-'
                    }\n`;
                });
                return text;
            }
            return 'No Observation\n';
        };

        const getDrugAndAlcohol = (arr) => {
            let text = '-';
            if (!_.isEmpty(arr)) {
                text = '';
                _.forEach(arr, (element) => {
                    text += resolveDict(element, 'injuryAlcoholOrDrug') + ', ';
                });
            }
            return text;
        };

        const getHistory = () => {
            if (!_.isEmpty(history?.Comorbidity_SnomedCt)) {
                let text = '';
                _.forEach(history?.Comorbidity_SnomedCt, (element) => {
                    const obj = resolveDict(element, 'comorbidity');
                    text += `* ${obj}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getCommissioners = (list) => {
            if (!_.isEmpty(list)) {
                let text = '* **Commissioners**\n';
                text += `| Start Date /| End Date /| Code of Commissioner /| Commissioner Reference Identifier /| Agreement Identifier /| Agreement Line Identifier /| Provider Reference Identifier /| Service Code |\n|---|---|---|---|---|---|---|---|\n`;
                _.forEach(list, (el) => {
                    text += `|${el?.StartDate_CommissionerAssignmentPeriod || '-'}|${el?.EndDate_CommissionerAssignmentPeriod || '-'}|${el?.OrganisationIdentifier_CodeOfCommissioner || '-'}|`;
                    text += `${el?.CommissionerReferenceIdentifier || '-'}|${el?.NHSServiceAgreementIdentifier || '-'}|${el?.NHSServiceAgreementLineIdentifier || '-'}|`;
                    text += `${el?.ProviderReferenceIdentifier || '-'}|${el?.ServiceCode || '-'}|\n`;
                });
                return text;
            }
            return '';
        };

        const getCare = () => {
            if (!_.isEmpty(care?.CareProfessionals_EmergencyCare)) {
                let text = '';
                _.forEach(care?.CareProfessionals_EmergencyCare, (element) => {
                    const issuer = resolveDict(element?.ProfessionalRegistrationIssuerCode, 'clinicianIssuerCode', true);
                    const tier = resolveDict(element?.CareProfessionalTier_EmergencyCare, 'clinicianTier', true);
                    const responsible = element?.CareProfessionalDischargeResponsibilityIndicator_EmergencyCare;
                    const time = getLocalDate(element?.CareProfessionalClinicalResponsibilityTimeStamp, null, null, 'timestamp');
                    text +=
                        `* **Entry Id** ${element?.ProfessionalRegistrationEntryIdentifier || '-'},` +
                        ` **Registration Issuer** ${issuer},` +
                        ` **Tier** ${tier},` +
                        ` **Responsible for Discharge** ${responsible === 'Y' ? 'Yes' : 'No'},` +
                        ` **Date and Time** ${time}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getDiagnosis = () => {
            if (!_.isEmpty(diagnosis?.EmergencyCareDiagnoses_SnomedCt)) {
                let text = '';
                _.forEach(diagnosis?.EmergencyCareDiagnoses_SnomedCt, (element) => {
                    const diag = resolveDict(element?.EmergencyCareDiagnosis_SnomedCt, 'diagnosis', false, 'snomedDescription');
                    const diagQualifier = resolveDict(element?.EmergencyCareDiagnosisQualifier_SnomedCt, 'diagnosisQualifier');
                    text += `* Diagnosis ${diag}, Diagnosis Qualifier ${diagQualifier}, Clinical Entry Sequence Number ${element?.CodedClinicalEntrySequenceNumber || '-'}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getInvestigations = () => {
            if (!_.isEmpty(investigations?.EmergencyCareInvestigations_SnomedCt)) {
                let text = '';
                _.forEach(investigations?.EmergencyCareInvestigations_SnomedCt, (element) => {
                    const obj = resolveDict(element?.EmergencyCareClinicalInvestigation_SnomedCt, 'investigation');
                    const localTime = getLocalDate(
                        element?.CodedProcedureTimestamp_UrgentAndEmergencyCareClinicalInvestigation,
                        element.ProcedureDate_EmergencyCareClinicalInvestigation,
                        element.ProcedureTime_EmergencyCareClinicalInvestigation,
                        'timestamp',
                    );
                    text += `* Treatment ${obj}, Procedure Date and Time ${localTime}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getTreatments = () => {
            if (!_.isEmpty(treatments?.EmergencyCareTreatments_SnomedCt)) {
                let text = '';
                _.forEach(treatments?.EmergencyCareTreatments_SnomedCt, (element) => {
                    const obj = resolveDict(element?.EmergencyCareProcedure_SnomedCt, 'treatment');
                    const localTime = getLocalDate(
                        element?.CodedProcedureTimestamp_UrgentAndEmergencyCareProcedure,
                        element.ProcedureDate_EmergencyCareProcedure,
                        element.ProcedureTime_EmergencyCareProcedure,
                        'timestamp',
                    );

                    text += `* Treatment ${obj}, Procedure Date and Time ${localTime}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getReferrals = () => {
            if (!_.isEmpty(otherService?.ReferralsToOtherServices)) {
                let text = '';
                _.forEach(otherService?.ReferralsToOtherServices, (element) => {
                    const obj = resolveDict(element?.ReferredToService_SnomedCt, 'referrals');
                    const localRequest = getLocalDate(
                        element?.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare,
                        element?.ActivityServiceRequestDate_EmergencyCare,
                        element?.ActivityServiceRequestTime_EmergencyCare,
                        'timestamp',
                    );
                    const localAssessment = getLocalDate(
                        element?.ReferredToServiceAssessmentTimestamp,
                        element?.ReferredToServiceAssessmentDate,
                        element?.ReferredToServiceAssessmentTime,
                        'timestamp',
                    );
                    text += `* ${obj}, Request Date and Time ${localRequest}, Assessment Date and Time ${localAssessment}\n`;
                });
                return text;
            }
            return 'No selected\n';
        };

        const getSafeguarding = (array) => {
            if (!_.isEmpty(array)) {
                let text = '';
                _.forEach(array, (element) => {
                    const obj = resolveDict(element, 'safeguarding');
                    text += obj + ', ';
                });
                return text;
            }
            return '-';
        };

        /*
         * Create ECDS note
         * */
        let note = '## *Emergency Care Data Set*\n\n';

        // Patient Pathway
        if (ecdsVersion === 'V3') {
            note +=
                `### Patient Pathway\n` +
                `* **Unique Booking Reference Number**: ${patientPathway?.PatientPathwayIdentity?.UniqueBookingReferenceNumber_Converted || '-'}\n` +
                `* **Patient Pathway Identifier**: ${patientPathway?.PatientPathwayIdentity?.PatientPathwayIdentifier || '-'}\n` +
                `* **Patient Pathway Identifier Issuer**: ${patientPathway?.PatientPathwayIdentity?.OrganisationIdentifier_PatientPathwayIdentifierIssuer || '-'}\n` +
                `* **Treatment Period Characteristics**: ${resolveDict(patientPathway?.RTTPeriodCharacteristics?.ReferralToTreatmentPeriod_Status, 'treatmentStatus', true)}\n` +
                `* **Waiting Time Measurement Type**: ${resolveDict(patientPathway?.RTTPeriodCharacteristics?.WaitingTimeMeasurementType, 'waitingTimeMeasurementType', true)}\n` +
                `* **Treatment Period Start Date**: ${
                    patientPathway?.RTTPeriodCharacteristics?.ReferralToTreatmentPeriod_StartDate
                        ? moment(patientPathway?.RTTPeriodCharacteristics?.ReferralToTreatmentPeriod_StartDate).format(
                              config.defaultDateFormat,
                          )
                        : '-'
                }\n` +
                `* **Treatment Period End Date**: ${
                    patientPathway?.RTTPeriodCharacteristics?.ReferralToTreatmentPeriod_EndDate
                        ? moment(patientPathway?.RTTPeriodCharacteristics?.ReferralToTreatmentPeriod_EndDate).format(
                              config.defaultDateFormat,
                          )
                        : '-'
                }\n\n`;
        }

        // Patient Identity
        switch (nhsStatus) {
            case '01':
                note +=
                    `### Patient Identity\n` +
                    `* **NHS Status Indicator**: ${resolveDict(patientIdentity?.VerifiedIdentity_Structure?.DataElementStructure?.NHSNumberStatusIndicatorCode, 'nhsCodeStatus', true)}\n` +
                    `* **NHS Number**: ${patientIdentity?.VerifiedIdentity_Structure?.DataElementStructure?.NHSNumber || '-'}\n` +
                    `* **Local Patient Identifier**: ${patientIdentity?.VerifiedIdentity_Structure?.LocalIdentifierStructure?.LocalPatientIdentifier_Extended || '-'}\n` +
                    `* **Organisation Identifier - Local Patient Identifier**: ${patientIdentity?.VerifiedIdentity_Structure?.LocalIdentifierStructure?.OrganisationIdentifier_LocalPatientIdentifier || '-'}\n` +
                    `* **Organisation Identifier - Residence Responsibility**: ${patientIdentity?.VerifiedIdentity_Structure?.DataElementStructure?.OrganisationIdentifier_ResidenceResponsibility || '-'}\n` +
                    `* **Person Birth Date**: ${patientIdentity?.VerifiedIdentity_Structure?.DataElementStructure?.PersonBirthDate || '-'}\n` +
                    `* **Postcode**: ${patientIdentity?.VerifiedIdentity_Structure?.DataElementStructure?.PostcodeOfUsualAddress || '-'}\n\n`;
                break;

            case '':
            case null:
                note +=
                    `### Patient Identity\n` +
                    `* **NHS Status Indicator**: ${resolveDict(patientIdentity?.WithheldIdentity_Structure?.NHSNumberStatusIndicatorCode, 'nhsCodeStatus', true)}\n` +
                    `* **Organisation Identifier - Residence Responsibility**: ${patientIdentity?.WithheldIdentity_Structure?.OrganisationIdentifier_ResidenceResponsibility || '-'}\n` +
                    `* **Withheld Reason**: ${resolveDict(patientIdentity?.WithheldIdentity_Structure?.WithheldIdentityReason, 'withheldReason', true)}\n\n`;
                break;

            default:
                note +=
                    `### Patient Identity\n` +
                    `* **NHS Status Indicator**: ${resolveDict(patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure?.NHSNumberStatusIndicatorCode, 'nhsCodeStatus', true)}\n` +
                    `* **NHS Number**: ${patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure?.NHSNumber || '-'}\n` +
                    `* **Local Patient Identifier**: ${patientIdentity?.UnverifiedIdentity_Structure?.LocalIdentifierStructure?.LocalPatientIdentifier_Extended || '-'}\n` +
                    `* **Organisation Identifier - Local Patient Identifier**: ${patientIdentity?.UnverifiedIdentity_Structure?.LocalIdentifierStructure?.OrganisationIdentifier_LocalPatientIdentifier || '-'}\n` +
                    `* **Organisation Identifier - Residence Responsibility**: ${patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure2?.OrganisationIdentifier_ResidenceResponsibility || '-'}\n` +
                    `* **Person Birth Date**: ${patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure2?.PersonBirthDate || '-'}\n` +
                    `* **Postcode**: ${patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure2?.PostcodeOfUsualAddress || '-'}\n` +
                    `* **Patient Name**: ${getPtName(patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure?.PatientName)}\n` +
                    `* **Patient Address**: ${getPtAddress(patientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure?.PatientUsualAddress)}\n\n`;
                break;
        }

        // Patient Characteristics
        note +=
            `### Patient Characteristics\n` +
            `* **Sex**: ${resolveDict(patientCharacteristics?.PersonStatedGenderCode, 'gender', true)}\n` +
            `* **Ethnic Category**: ${resolveDict(patientCharacteristics?.EthnicCategory, 'ethnic', true)}\n` +
            `* **Preferred Spoken Language**: ${resolveDict(patientCharacteristics?.PreferredSpokenLanguage_SnomedCt, 'spokenLang')}\n` +
            `* **Interpreter Status**: ${resolveDict(patientCharacteristics?.AccessibleInformationProfessionalRequiredCode_SnomedCt, 'interStatus')}\n` +
            `* **Interpreter Language**: ${resolveDict(patientCharacteristics?.InterpreterLanguage_SnomedCt, 'interLang')}\n` +
            `* **Accommodation status**: ${resolveDict(patientCharacteristics?.AccommodationStatus_SnomedCt, 'accommodation')}\n` +
            `* **Overseas Visitor Charging Category**: ${resolveDict(patientCharacteristics?.OverseasVisitorChargingCategoryAtCdsActivityDate, 'overseasVisitorCat', true)}\n\n`;

        // Social
        if (ecdsVersion === 'V4') {
            note += `### Social And Personal Circumstances\n${getSocial()}\n`;
        }

        // Mental Health
        note += `### Mental Health Act Legal Status\n${getMental()}\n`;

        // GP
        note +=
            `### General Medical Registration\n` +
            `* **GM Practitioner**: ${gp?.GeneralMedicalPractitioner_Specified || '-'}\n` +
            `* **GM Practice Code**: ${gp?.GeneralPractice_PatientRegistration || '-'}\n\n`;

        // Attendance Location
        note +=
            `### ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care Attendance Location\n` +
            `* **Organisation Site Identifier of Treatment**: ${attendanceLocation?.OrganisationSiteIdentifier_OfTreatment || '-'}\n` +
            `* **Department Type**: ${resolveDict(attendanceLocation?.EmergencyCareDepartmentType, 'departmentType', true)}\n\n`;

        // Ambulance Details
        note +=
            `### Ambulance Details\n` +
            `* **Ambulance Call Identifier**: ${ambulanceDetails?.AmbulanceCallIdentifier || '-'}\n` +
            `* **Care Contact Identifier**: ${ambulanceDetails?.CareContactIdentifier_AmbulanceService || '-'}\n` +
            `* **Organisation Identifier - Conveying Ambulance Trust**: ${ambulanceDetails?.OrganisationIdentifier_ConveyingAmbulanceTrust || '-'}\n\n`;

        // Attendance Activity
        note += `### ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care Attendance Activity Characteristics\n`;
        if (ecdsVersion === 'V3') {
            const localArrival = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                attendanceActivity?.EmergencyCareArrivalDate,
                attendanceActivity?.EmergencyCareArrivalTime,
            );
            const localInitAss = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                attendanceActivity.EmergencyCareInitialAssessmentDate,
                attendanceActivity.EmergencyCareInitialAssessmentTime,
            );
            const localSeen = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                attendanceActivity.EmergencyCareDateSeenForTreatment,
                attendanceActivity.EmergencyCareTimeSeenForTreatment,
            );

            note +=
                `* **Attendance Identifier**: ${attendanceActivity?.EmergencyCareAttendanceIdentifier || '-'}\n` +
                `* **Emergency Care Attendance Category**: ${resolveDict(attendanceActivity?.EmergencyCareAttendanceCategory, 'attendanceCat', true)}\n` +
                `* **Arrival Mode**: ${resolveDict(attendanceActivity?.EmergencyCareArrivalMode_SnomedCt, 'arrival')}\n` +
                `* **Attendance Source**: ${resolveDict(attendanceActivity?.EmergencyCareAttendanceSource_SnomedCt, 'attSource')}\n` +
                `* **Organisation Site Identifier - Emergency Care Attendance Source**: ${attendanceActivity?.OrganisationSiteIdentifier_EmergencyCareAttendanceSource || '-'}\n` +
                `* **Age at CDS Activity Date**: ${attendanceActivity?.AgeAtCdsActivityDate || '-'}\n` +
                `* **Arrival Date and Time**: ${localArrival ? localArrival.format(config.defaultDateFormat) : '-'}` +
                ` ${localArrival ? localArrival.format(config.defaultTimeFormat) : '-'}\n` +
                `* **Initial Assessment Date and Time**: ${localInitAss ? localInitAss.format(config.defaultDateFormat) : '-'}` +
                ` ${localInitAss ? localInitAss.format(config.defaultTimeFormat) : '-'}\n` +
                `* **Acuity**: ${resolveDict(attendanceActivity?.EmergencyCareAcuity_SnomedCt, 'acuity')}\n` +
                `* **Chief Complaint**: ${resolveDict(attendanceActivity?.EmergencyCareChiefComplaint_SnomedCt, 'chiefComplain')}\n` +
                `* **Date Seen for Treatment and Time**: ${localSeen ? localSeen.format(config.defaultDateFormat) : '-'}` +
                ` ${localSeen ? localSeen.format(config.defaultTimeFormat) : '-'}\n\n`;
        } else {
            note +=
                `* **Attendance Identifier**: ${attendanceActivity?.UrgentAndEmergencyCareActivityIdentifier || '-'}\n` +
                `* **Episode Identifier**: ${attendanceActivity?.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier || '-'}\n` +
                `* **Arrival Mode**: ${resolveDict(attendanceActivity?.UrgentAndEmergencyCareArrivalMode_SnomedCt, 'arrival')}\n` +
                `* **Consultation Mechanism**: ${resolveDict(attendanceActivity?.ConsultationMechanism_UrgentAndEmergencyCare, 'consultationMechanism', true)}\n` +
                `* **Activity start Date and Time**: ${getLocalDate(attendanceActivity?.UrgentAndEmergencyCareActivityStartDateAndTime, null, null, 'timestamp')}\n` +
                `* **Attendance Category**: ${resolveDict(attendanceActivity?.UrgentAndEmergencyCareAttendanceCategory, 'attendanceCat', true)}\n` +
                `* **Attendance Source**: ${resolveDict(attendanceActivity?.UrgentAndEmergencyCareAttendanceSource_SnomedCt, 'attSource')}\n` +
                `* **Organisation Site Identifier - Attendance Source**: ${attendanceActivity?.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource || '-'}\n` +
                `* **Age at CDS Activity Date**: ${attendanceActivity?.AgeAtCDSActivityDate || '-'}\n` +
                `* **Chief Complaint**: ${resolveDict(attendanceActivity?.UrgentAndEmergencyCareChiefComplaint_SnomedCt, 'chiefComplain')}\n` +
                `* **Acuity**: ${resolveDict(attendanceActivity?.UrgentAndEmergencyCareAcuity_SnomedCt, 'acuity')}\n` +
                `* **Initial Assessment Date and Time**: ${getLocalDate(attendanceActivity?.UrgentAndEmergencyCareInitialAssessmentTimestamp, null, null, 'timestamp')}\n` +
                `* **Date Seen for Treatment and Time**: ${getLocalDate(attendanceActivity?.UrgentAndEmergencyCareTimestampSeenForTreatment, null, null, 'timestamp')}\n\n`;
        }

        // Time of Treatment (Flag 111)
        const treatmentField = ecdsVersion === 'V3' ? 'EmergencyCareTreatmentTimestamp' : 'EmergencyCareTreatmentAllocationTimestamp';
        const expectedField =
            ecdsVersion === 'V3' ? 'EmergencyCareExpectedDateTimestampTreatment' : 'EmergencyCareExpectedDateAndTimestampOfTreatment';
        note +=
            `### Expected Time of Treatment\n` +
            `* **Treatment Date and Time**: ${!_.isEmpty(timeOfTreatment) ? moment.utc(timeOfTreatment?.[0]?.[treatmentField]).format(config.defaultDateTimeFormat) : '-'}\n` +
            `* **Expected Treatment Date and Time**: ${!_.isEmpty(timeOfTreatment) ? moment.utc(timeOfTreatment?.[0]?.[expectedField]).format(config.defaultDateTimeFormat) : '-'}\n\n`;

        // Obs Assessment
        note += `### Scored Assessment\n${getAssessment()}\n`;

        // Observations
        note += `### Clinical Observations\n${getObservation()}\n`;

        // Obs Findings
        note += `### Clinical Findings\n${getFindings()}\n`;

        // Injury form
        const injuryLocal = getLocalDate(injury.InjuryDateTime, injury.InjuryDate, injury.InjuryTime, 'timestamp');
        note +=
            `### Injury Characteristics\n` +
            `* **Date and Time of Injury**: ${injuryLocal}\n` +
            `* **Place of Injury**: ${resolveDict(injury?.EmergencyCarePlaceOfInjury_SnomedCt, 'placeOfInjury')}\n` +
            `* **Coordinates**: ${
                injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Latitude ||
                injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Longitude
                    ? `* **Lat**: ${injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Latitude || '-'} * **Lon**: ${injury?.EmergencyCarePlaceOfInjury_LatitudeAndLongitude?.Longitude || '-'}\n`
                    : '-'
            }` +
            `* **Injury Intent**: ${resolveDict(injury?.EmergencyCareInjuryIntent_SnomedCt, 'injuryIntent')}\n` +
            `* **Injury Activity Status**: ${resolveDict(injury?.EmergencyCareInjuryActivityStatus_SnomedCt, 'injuryActivityStatus')}\n` +
            `* **Injury Activity Type**: ${resolveDict(injury?.EmergencyCareInjuryActivityType_SnomedCt, 'injuryActivityType')}\n` +
            `* **Injury Mechanism**: ${resolveDict(injury?.EmergencyCareInjuryMechanism_SnomedCt, 'injuryMechanism')}\n` +
            `* **Assault Location Description**: ${injury?.AssaultLocationDescription || '-'}\n` +
            `* **Injury Alcohol or Drug Involvement**: ${getDrugAndAlcohol(injury?.EmergencyCareInjuryAlcoholOrDrugInvolvement_SnomedCt)}\n\n`;

        // Patient History
        note += `### Patient Clinical History\n${getHistory()}\n`;

        // Service Agreement
        note += `### Service Agreement Details\n`;
        if (ecdsVersion === 'V3') {
            note +=
                `* **Provider Reference Number**: ${agreement?.ProviderReferenceNumber || '-'}\n` +
                `* **NHS Service Agreement Line Number**: ${agreement?.NhsServiceAgreementLineNumber || '-'}\n` +
                `* **Commissioner Reference Number**: ${agreement?.CommissionerReferenceNumber || '-'}\n` +
                `* **Commissioning Serial Number**: ${agreement?.CommissioningSerialNumber || '-'}\n` +
                `* **Organisation Identifier - Code of Provider**: ${agreement?.OrganisationIdentifier_CodeOfProvider || '-'}\n` +
                `* **Organisation Identifier - Code of Commissioner**: ${agreement?.OrganisationIdentifier_CodeOfCommissioner || '-'}\n\n`;
        } else if (ecdsVersion === 'V4') {
            note += `* **Organisation Identifier (Code of Provider)**: ${agreement?.OrganisationIdentifier_CodeOfProvider || '-'}\n`;
            note += getCommissioners(agreement?.Commissioners);
            note += '\n\n';
        }

        // Care Professionals
        note += `### Care Professionals\n${getCare()}\n`;

        // Diagnosis
        note += `### ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care Diagnosis\n${getDiagnosis()}\n`;

        // Investigations
        note += `### ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care Investigations\n${getInvestigations()}\n`;

        // Treatments
        note += `### ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care Treatments\n${getTreatments()}\n`;

        // Referrals
        note += `### Referrals to Other Services\n${getReferrals()}\n`;

        // eMed 3
        if (ecdsVersion === 'V4') {
            const getFollowUpIndicator = (val) => {
                if (val) {
                    return val === 'Y'
                        ? 'Yes - a follow up CARE CONTACT is required at the end of the eMED3 Fit Note Applicable Period'
                        : 'No - a follow up CARE CONTACT is not required at the end of the eMED3 Fit Note Applicable Period';
                }
                return '-';
            };

            note +=
                `### eMed 3 Fit Note\n` +
                `* **Fit Note Assessment Date**: ${eMed?.FitNoteAssessmentDate || '-'}\n` +
                `* **Fit Note Diagnosis (ICD)**: ${eMed?.FitNoteDiagnosis_ICD || '-'}\n` +
                `* **Fit Note Condition**: ${resolveDict(eMed?.FitNoteCondition_SnomedCt, 'sicknessAbsenceCategory')}\n` +
                `* **Fit Note Start Date**: ${eMed?.FitNoteStartDate || '-'}\n` +
                `* **Fit Note End Date**: ${eMed?.FitNoteEndDate || '-'}\n` +
                `* **Fit Note Duration**: ${eMed?.FitNoteDuration || '-'}\n` +
                `* **Fit Note Recorded Date**: ${eMed?.FitNoteRecordedDate || '-'}\n` +
                `* **Fit Note Issuer**: ${eMed?.FitNoteIssuer || '-'}\n` +
                `* **Fit Note Follow Up Assessment Required Indicator**: ${getFollowUpIndicator(eMed?.FitNoteFollowUpIndicator)}\n\n`;
        }

        // Discharge
        const localAdmit = getLocalDate(
            discharge?.DecidedToAdmitDateAndTime,
            discharge?.DecidedToAdmitDate,
            discharge?.DecidedToAdmitTime,
            'timestamp',
        );
        const localDeparture = getLocalDate(
            discharge?.UrgentAndEmergencyCareActivityEndTimestamp,
            discharge?.EmergencyCareDepartureDate,
            discharge?.EmergencyCareDepartureTime,
            'timestamp',
        );
        const localRdyToProc = getLocalDate(discharge?.EmergencyCareClinicallyReadyToProceedTimeStamp, null, null, 'timestamp');
        note +=
            `### Discharge from ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care\n` +
            `* **Decided to Admit Date and Time**: ${localAdmit} \n` +
            `* **Activity Treatment**: ${resolveDict(discharge?.ActivityTreatmentFunctionCode_DecisionToAdmit, 'activityTreatment', true)}\n` +
            `* **Organisation Site Identifier - Discharge from ${ecdsVersion === 'V4' ? 'Urgent And ' : ''}Emergency Care**: ${discharge?.OrganisationSiteIdentifier_DischargeFromEmergencyCare || '-'}\n` +
            `* **Discharge Status**: ${resolveDict(discharge?.EmergencyCareDischargeStatus_SnomedCt, 'dischargeStatus')}\n` +
            `* **Departure Date and Time**: ${localDeparture} \n` +
            `* **Safeguarding Concern**: ${getSafeguarding(discharge?.SafeguardingConcern_SnomedCt)}\n` +
            `* **Discharge Destination**: ${resolveDict(discharge?.EmergencyCareDischargeDestination_SnomedCt, 'dischargeDestination')}\n` +
            `* **Discharge Follow Up**: ${resolveDict(discharge?.EmergencyCareDischargeFollowUp_SnomedCt, 'dischargeFollowUp')}\n`;
        if (ecdsVersion === 'V4') {
            const getIngoGiven = (indicator) => {
                if (indicator) {
                    return indicator === 'Y'
                        ? 'Yes - a copy of the discharge letter was given to the PATIENT'
                        : 'No - a copy of the discharge letter was NOT given to the PATIENT';
                }
                return '-';
            };
            note += `* **Discharge Information Given**: ${getIngoGiven()}\n`;
        } else {
            note += `* **Discharge Information Given**: ${resolveDict(discharge?.EmergencyCareDischargeInformationGiven_SnomedCt, 'dischargeInfoGiven')}\n`;
        }
        note += `* **Clinically Ready to Proceed Date and Time**: ${localRdyToProc}\n\n`;

        // Disease Outbreak
        note +=
            `### Research and Disease Outbreak Notification\n` +
            `* **Clinical Trial Identifier**: ${outbreak?.ClinicalTrialIdentifier || '-'}\n` +
            `* **Disease Outbreak Description**: ${outbreak?.DiseaseOutBreakNotificationDescription || '-'}\n` +
            `* **Disease Outbreak Notification**: ${outbreak?.DiseaseOutBreakNotificationSnomedCt || '-'}\n\n`;
        return note;
    }

    static getDiseaseOutbreakNote(outbreak) {
        return (
            `## *Research and Disease Outbreak Notification*\n\n` +
            `* **Clinical Trial Identifier**: ${outbreak?.ClinicalTrialIdentifier ? outbreak?.ClinicalTrialIdentifier : '-'}\n` +
            `* **Disease Outbreak Description**: ${outbreak?.DiseaseOutBreakNotificationDescription ? outbreak?.DiseaseOutBreakNotificationDescription : '-'}\n` +
            `* **Disease Outbreak Notification**: ${outbreak?.DiseaseOutBreakNotificationSnomedCt ? outbreak?.DiseaseOutBreakNotificationSnomedCt : '-'}\n\n`
        );
    }

    static getSafeguardingFormNote() {
        let note;
        note = '## *Safeguarding for Children Form completed*\n\n';

        return note;
    }

    static getMentalHealthFormNote(isChild, notNeeded) {
        let note;
        note = `## *${isChild ? 'CAMHS Referral' : 'Adult Psychiatry Referral'} Form ${notNeeded ? 'not needed' : 'completed'}*\n\n`;

        return note;
    }

    static getCauseForConcernFormNote() {
        let note;
        note = `## *Cause for Concern for Adults Referral Form completed*\n\n`;

        return note;
    }

    static getPrescriptionNote(prescription) {
        let note = '## *Prescription*\n\n';
        note += `* **Prescription Date and Time**: ${moment(prescription.when).format(config.defaultDateTimeFormat)}\n`;
        //note += `* **Clinician**: ${prescription?.who?.__description}\n`;
        note += `* **Clinician**: ${getConsultantName(prescription?.who)}\n`;
        note += `* **GMC/NMC**: ${getConsultantCode(prescription?.who)}\n`;

        note += `### Drugs:\n`;
        note += `|Drug|Dose|Route|Frequency|Duration|\n|:---|:---:|:---:|:---:|:---:|\n`;
        _.forEach(prescription?.drugs, (drug) => {
            note += `|${drug.drug}|${drug.dose}|${drug.route}|${drug.frequency}|${drug.duration}| \n`;
        });
        return note;
    }

    static getPrescriptionExternalNote(prescription) {
        let note = '## *Prescription*\n\n';
        note += `* **Prescription Date and Time**: ${moment(prescription.when).format(config.defaultDateTimeFormat)}\n`;
        //note += `* **Clinician**: ${prescription?.who?.__description}\n`;
        note += `* **Clinician**: ${getConsultantName(prescription?.who)}\n`;
        note += `* **GMC/NMC**: ${getConsultantCode(prescription?.who)}\n`;

        note += `### Drugs:\n`;
        note += `|Drugs|Dose|Route|Frequency|Duration|\n|:---|:---:|:---:|:---:|:---:|\n`;
        _.forEach(prescription?.medications, (med) => {
            const drug = med.ampp || med.amp || med.vmp;
            note += `|${drug.display}|${med.dose.value}|${med.route.value}|${med.frequency.value}|${med.duration.value}| \n`;
        });
        return note;
    }

    static getRePrintPrescriptionNote(prescription) {
        let note = '## *Print Prescription*\n\n';
        note += `Prescription From Date ${moment(prescription.values?.when).format(config.defaultDateTimeFormat)}\n has been reprinted`;
        return note;
    }

    static getRePrintPrescriptionExternalNote(prescription) {
        let note = '## *Print Prescription*\n\n';
        note += `Prescription From Date ${moment(prescription.values?.when).format(config.defaultDateTimeFormat)}\n has been reprinted`;
        return note;
    }

    static getDischargeFormNotRequiredNote(notReqVal, discharge) {
        const followUp = discharge?.dischargeFollowUp?.snomedCode === config?.dischargeFollowUpCheck?.fractureClinic ? 'vfc' : 'phy';
        let note = '## *Discharge Follow UP*\n\n';
        if (followUp === 'vfc') {
            note += `* **Discharge Follow UP selected as 'Fracture Clinic', but form VFC not submitted and selected as not required.**`;
        } else {
            note += `* **Discharge Follow UP selected as 'Physiotherapy' and form Physiotherapy Form mark as ${notReqVal ? 'Submitted' : 'Not Required'}**`;
        }
        return note;
    }

    static getOxygenNote(oxygen) {
        let note = '';
        note = `## *Oxygen Form* \n`;
        if (oxygen) {
            if (oxygen.hypercapnic)
                note += `${oxygen.hypercapnic ? 'This patient has NOT BEEN DIAGNOSED with Hypercapnic respiratory failure' : ''} \n\n`;
            if (oxygen.noHypercapnic)
                note += `${oxygen.noHypercapnic ? 'This Patient has confirmed Hypercapnic respiratory failure on blood gas analysis on either a prior or their current hospital admission' : ''} \n\n`;

            note += `Entry target sats ${oxygen.lower}% - ${oxygen.upper}% \n`;
        } else {
            note += `Oxygen Form Deleted \n`;
        }

        return note;
    }
    static getIntendentManagementNote(intendentManagement) {
        let note = '';
        note = `## *Intendent Management* \n`;
        if (intendentManagement) {
            note += `* EDD: ${intendentManagement.edd ? moment(intendentManagement.edd).format(config.defaultDateTimeFormat) : 'Not Set'} \n\n`;
            note += `* CDD: ${intendentManagement.cdd ? moment(intendentManagement.cdd).format(config.defaultDateTimeFormat) : 'Not Set'} \n\n`;
        } else {
            note += `Intendent Management Form Deleted \n`;
        }
        return note;
    }

    static getBoardRoundNote(boardRound) {
        let note = '';
        note = `## *Board Round* \n`;
        note += `* EDD: ${boardRound.edd ? moment(boardRound.edd).format(config.defaultDateTimeFormat) : 'Not Set'} \n\n`;
        note += `* CDD: ${boardRound.cdd ? moment(boardRound.cdd).format(config.defaultDateTimeFormat) : 'Not Set'} \n\n`;
        note += `* Time of Board Round: ${boardRound.timeBoardRound ? boardRound.timeBoardRound : 'Not Set'} \n\n`;
        note += `* Medically optimised to leave: ${boardRound.optimisedToLeave ? boardRound.optimisedToLeave : 'Not Set'} \n\n`;
        note += `* Type of Board Round: ${boardRound.typeBoardRound ? boardRound.typeBoardRound : 'Not Set'} \n\n`;
        note += `* Discharge Today: ${boardRound.dischargeToday ? boardRound.dischargeToday : 'Not Set'} \n\n`;
        note += `* Green Day: ${boardRound.greenDay ? boardRound.greenDay : 'Not Set'} \n\n`;
        note += `* Conclusion: ${boardRound.conclusion ? boardRound.conclusion : 'Not Set'} \n\n`;

        return note;
    }

    static getSmokingNote(smoking) {
        let note = '';
        note = `## *Smoking Status* \n`;
        if (smoking) {
            note += `* ${smoking.smoker} \n\n`;
            if (smoking.smoker === 'Smoker') {
                note += ` ${smoking.cigarettesPerDay} cigarettes per day \n\n`;
            }
        } else {
            note += `Smoking Status Deleted \n`;
        }
        return note;
    }
    static getAuditCNote(auditC) {
        let note = '';
        note = `## *Audit C Form* \n`;
        if (auditC) {
            // Support nested shape { base: {...}, extra: {...} } or flat shape { howOftenDrinkAlcohol, ... }
            let base = auditC.base || {};
            let extra = auditC.extra || {};
            if ((!base || Object.keys(base).length === 0) && (auditC.howOftenDrinkAlcohol !== undefined || auditC.standardDrinkDay !== undefined || auditC.howOftenMoreDrink !== undefined)) {
                base = {
                    howOftenDrinkAlcohol: auditC.howOftenDrinkAlcohol,
                    standardDrinkDay: auditC.standardDrinkDay,
                    howOftenMoreDrink: auditC.howOftenMoreDrink,
                };
            }
            if ((!extra || Object.keys(extra).length === 0) && (auditC.notAbleToStopDrinking !== undefined || auditC.failedBecauseDrinking !== undefined)) {
                extra = {
                    notAbleToStopDrinking: auditC.notAbleToStopDrinking,
                    failedBecauseDrinking: auditC.failedBecauseDrinking,
                    neededAlcoholicMorning: auditC.neededAlcoholicMorning,
                    unableRememberWhatHappened: auditC.unableRememberWhatHappened,
                    injuredResultDrinking: auditC.injuredResultDrinking,
                    relativeConcerned: auditC.relativeConcerned,
                };
            }

            // Maps to resolve primitive values to display text (mirror of auditCForm.js tabs)
            const A_MAP = {
                0: 'never',
                1: 'monthly or less',
                2: '2-4 times a month',
                3: '2-3 times a week',
                4: '4 or more times a week',
            };
            const B_MAP = {
                0: '0',
                1: '1 or 2',
                2: '3 or 4',
                3: '5 or 6',
                4: '7 to 9',
                5: '10 or more',
            };
            const C_MAP = {
                0: 'never',
                1: 'less than monthly',
                2: 'monthly',
                3: 'weekly',
                4: 'daily or almost daily',
            };
            const D_MAP = {
                0: 'no',
                1: 'yes, but not in the last year',
                2: 'yes, during last year',
            };

            const resolve = (opt, map) => {
                if (opt === null || opt === undefined || opt === '') return { value: null, text: 'Not selected' };
                if (typeof opt === 'object') {
                    return { value: Number(opt.value ?? null), text: opt.text ?? String(opt.value ?? '') };
                }
                const v = Number(opt);
                const text = map && map.hasOwnProperty(v) ? map[v] : String(opt);
                return { value: v, text };
            };

            const hFreq = resolve(base.howOftenDrinkAlcohol, A_MAP);
            const stdDay = resolve(base.standardDrinkDay, B_MAP);
            const hMore = resolve(base.howOftenMoreDrink, C_MAP);

            const baseScore = (hFreq.value || 0) + (stdDay.value || 0) + (hMore.value || 0);

            let extraScore = 0;
            const notAble = resolve(extra.notAbleToStopDrinking, C_MAP);
            const failed = resolve(extra.failedBecauseDrinking, C_MAP);
            const needed = resolve(extra.neededAlcoholicMorning, C_MAP);
            const unable = resolve(extra.unableRememberWhatHappened, C_MAP);
            const injured = resolve(extra.injuredResultDrinking, D_MAP);
            const relative = resolve(extra.relativeConcerned, D_MAP);

            [notAble, failed, needed, unable, injured, relative].forEach((e) => {
                if (e && (e.value || e.value === 0)) extraScore += e.value || 0;
            });

            note += `* How Often Do You Have a Drink Containing Alcohol? ${hFreq.text} \n`;
            note += `* How Many Standard Drinks Containing Alcohol Do You Have on a Typical Day? ${stdDay.text} \n`;
            note += `* How Often Do You Have 6 or More Drinks on One Occasion? ${hMore.text} \n\n`;
            note += `Score: ${baseScore} \n\n`;

            if (baseScore > 4) {
                note += `* How often during the last year have you found that you were not able to stop drinking once you had started? ${notAble.text} \n`;
                note += `* How often during the last year have you failed to do what was normally expected from you because of your drinking? ${failed.text} \n`;
                note += `* How often during the last year have you needed an alcoholic drink in the morning to get yourself going after a heavy drinking session? ${needed.text} \n`;
                note += `* How often during the last year have you been unable to remember what happened the night before because you had been drinking? ${unable.text} \n`;
                note += `* Have you or somebody else been injured as a result of your drinking? ${injured.text} \n`;
                note += `* Has a relative or friend, doctor or other health worker been concerned about your drinking or suggested that you cut down? ${relative.text} \n\n`;
                note += ` Total Score: ${baseScore + extraScore} \n`;
            }
        } else {
            note += `Audit C Deleted \n`;
        }
        return note;
    }

    static getClerkingIPNote(clerking) {
        let note = '';
        note = '## *Clerking*\n\n';

        note += `* Chief Complaint: ${clerking.chiefComplaint?.__description ?? '-'} \n`;
        note += `* Past Medical History: ${clerking.pastMedicalHistory ?? '-'} \n`;
        note += `* Medications: ${clerking.medications ?? '-'} \n`;
        note += `* Allergies and reactions: ${clerking.allergies ?? '-'} \n`;
        note += `* Social History: ${clerking.socialHistory ?? '-'} \n`;
        note += `* Examinations: ${clerking.examination?.examination ?? '-'} \n`;
        note += `* Working diagnosis: ${clerking.plan?.workingDiagnosis ?? '-'} \n`;
        note += `* Management plan: ${clerking.plan?.managementPlan ?? '-'} \n`;
        note += `* LMWH Prescribed: ${clerking.plan?.LMWHPrescribed ?? '-'} \n`;

        return note;
    }

    static getCancelDirectNote(bedObj) {
        let note = '';
        note = '## *Cancel Direct Admission*\n\n';
        note += `* Canceled direct admission to bed '${bedObj.bed?.name || bedObj.bedId}' on zone '${bedObj.area?.name || bedObj.areaId}'`;
        return note;
    }
    static getNewDncprNote(dncpr) {
        let note = '';
        note = '## *DO NOT ATTEMPT Cardiopulmonary Resuscitation (DNACPR) - ORDER*\n\n';
        note += `* Create By: ${dncpr.createBy ?? '-'} \n`;
        note += `* Create Date: ${dncpr.createDate ? moment(dncpr.createDate).format(config.defaultDateTimeFormat) : '-'} \n`;
        note += `* Expired at: ${dncpr.validUntilDate ? moment(dncpr.validUntilDate).format(config.defaultDateTimeFormat) : '-'} \n`;

        return note;
    }

    static getCanceledDncprNote(dncpr) {
        let note = '';
        note = '## *DO NOT ATTEMPT Cardiopulmonary Resuscitation (DNACPR) - CANCEL*\n\n';
        note += `* Canceled By: ${dncpr.canceledBy ?? '-'} \n`;
        note += `* Canceled Date: ${dncpr.canceledDate ? moment(dncpr.canceledDate).format(config.defaultDateTimeFormat) : '-'} \n`;
        note += `* Canceled Reason : ${dncpr.canceledReason ?? '-'} \n`;

        return note;
    }

    static getExtendDncprNote(dncpr) {
        let note = '';
        note = '## *DO NOT ATTEMPT Cardiopulmonary Resuscitation (DNACPR) - EXTEND*\n\n';

        let lastExtend = dncpr.awaiting[dncpr.awaiting.length - 1];

        note += `* Extend By: ${lastExtend.validAddedBy ?? '-'} \n`;
        note += `* Extend Date: ${lastExtend.validAddedSetDate ? moment(lastExtend.validAddedSetDate).format(config.defaultDateTimeFormat) : '-'} \n`;
        note += `* Extend to : ${lastExtend.validAddedDate ? moment(lastExtend.validAddedDate).format(config.defaultDateTimeFormat) : '-'} \n`;

        return note;
    }

    static getSignedDncprNote(dncpr) {
        let note = '';
        note = '## *DO NOT ATTEMPT Cardiopulmonary Resuscitation (DNACPR) - SIGNED*\n\n';
        note += `* Signed By: ${dncpr.signedBy ?? '-'} \n`;
        note += `* Signed Date: ${dncpr.signedDate ? moment(dncpr.signedDate).format(config.defaultDateTimeFormat) : '-'} \n`;

        return note;
    }

    static getICD10(icd) {
        let note = '';
        note = '## *ICD-10*\n\n';
        if (_.isEmpty(icd)) {
            note += 'Codes have been cleared.';
        } else {
            note += `|Code|Description|Data|\n|:---|:---|:---|\n`;
            _.forEach(icd, (element) => {
                if (element.value) {
                    note += `|${element.value.altCode}|${element.value.__description}|${moment(element?.requestedTimestamp).format(config?.defaultDateTimeFormat)}|\n`;
                }
            });
        }

        return note;
    }

    static getOffWardNote(form) {
        let note = '## *Off Ward Form*\n\n';
        note += `* Off ward: ${form.offWard ? 'Yes' : 'No'} \n`;
        note += `* Home leave: ${form.homeLeave ? 'Yes' : 'No'} \n`;
        note += `* Information: ${form.information || ''} \n`;
        note += `* Start Date: ${form.startDate ? moment(form.startDate).format(config.defaultDateTimeFormat) : '-'} \n`;
        note += `* End Date: ${form.endDate ? moment(form.endDate).format(config.defaultDateTimeFormat) : '-'} \n\n`;
        if (form.completed) {
            note += `### *Patient returned*`;
        }
        return note;
    }

    static getDischargeTimestampListNote(form, isCleared = false) {
        let note = '## *Discharge Timestamp*\n\n';
        if (isCleared) {
            note += `* The timestamp has been deleted\n`;
        } else {
            note += `* **Departure Date and Time**: ${moment(form.departureDate).format(config.defaultDateFormat)} ${moment(form.departureTime).format(config.defaultTimeFormat)} \n`;
            // note += `* **Clinician**: ${form?.who?.__description || 'UNK'} \n`;
            note += `* **Clinician**: ${getConsultantName(form?.who) || 'UNK'} \n`;
            note += `* **Discharge Status**: ${form?.dischargeStatus?.__description || 'UNK'} \n`;
            note += `* **Discharge Destination**: ${form?.dischargeDestination?.__description || 'UNK'} \n`;
            note += `* **Discharge Follow Up**: ${form?.dischargeFollowUp?.__description || 'UNK'} \n`;
        }
        return note;
    }

    static getPlansNote(category, newObj, originalObj) {
        let note = `## *${category}*\n\n`;

        if (!_.isNil(newObj.notes)) {
            newObj?.notes.forEach(
                (n) =>
                    (note += ` * ${n.text} - ${n.checkedBy ? n.checkedBy + ' ' + moment(n.timestamp).format('DD/MM/YYYY HH:mm') : 'NOT COMPLETED'} \n `),
            );
        } else {
            note += 'Not Set';
        }
        note += ` \n by ${!_.isNil(newObj.who) ? newObj?.who?.__description : 'Not Set'} `;
        note += `Date and Time ${!_.isNil(newObj?.when) ? moment(newObj?.when).format(config.defaultDateTimeFormat) : 'Not Set'}`;

        // deleted plans
        if (!_.isEmpty(originalObj?.notes)) {
            let deletedPlans = _.cloneDeep(originalObj.notes);

            if (!_.isEmpty(newObj?.notes)) {
                newObj.notes.forEach((element) => {
                    deletedPlans = _.filter(deletedPlans, (filter) => {
                        return filter.id !== element.id;
                    });
                });
            }

            if (!_.isEmpty(deletedPlans)) {
                note += `\n### Canceled Plans\n\n`;
                deletedPlans.forEach(
                    (n) =>
                        (note += ` * ~${n.text} - ${n.checkedBy ? n.checkedBy + ' ' + moment(n.timestamp).format('DD/MM/YYYY HH:mm') : 'NOT COMPLETED'}~\n`),
                );
            }
        }
        return note;
    }

    static getReferralAck() {
        let note = '';
        note = '## *Patient referral acknowledged*';
        return note;
    }

    static getChangeOfPatientLocationNote(locationBody) {
        const dateFormat = getConfig('defaultDateTimeFormat');
        let note = `## Patient Transfer\n\n`;

        note +=
            `* **Transfer Date Time**: ${moment(locationBody.timeOfTransfer).format(dateFormat)}\n` +
            `${locationBody?.hospital ? `* **Hospital**: ${locationBody?.hospital?.description} \n` : ''}` +
            `* **Ward**: ${locationBody?.ward?.name || '-'}\n\n`;

        note +=
            `### Destination location\n` + `* **Bay**: ${locationBody?.newZone || '-'}\n` + `* **Bed**: ${locationBody?.newBed || '-'}\n\n`;

        note += `### Original location\n` + `* **Bay**: ${locationBody?.oldZone || '-'}\n` + `* **Bed**: ${locationBody?.oldBed || '-'}\n`;
        return note;
    }

    static getTimestampInitialAssessmentFormNote(clinician, time) {
        let note = '';
        note = `## *Time Stamp Initial Assessment*\n\n`;
        note += `* **Initial Assessment Clinician**: ${getConsultantName(clinician)} \n`;
        note += `* **Start Time of Clerking**: ${time}\n`;
        return note;
    }

    static getInitialAssessmentFormNote(form, isClear, tester, clinician, time) {
        let note = '## *Initial Assessment*\n\n';
        if (isClear) {
            note += `The form was cleared by ${tester}`;
        } else {
            note += `* **Initial Assessment Clinician**: ${getConsultantName(clinician)}\n`;
            note += `* **Date and Time**: ${time}\n`;
            note += `* **Initial Assessment Summary**: ${form.additionalInformation ?? '-'} \n`;
            if (form.presentation) {
                note += `* **${config?.modules?.customLabels?.triage?.presentation || 'Presentation'}**: ${form.presentation ? form.presentation?.__description : '-'}\n`;
            }
            const discriminator = form.presentation ? 'Discriminator' : 'Acuity Score';
            note += `* **${discriminator}**:  ${form.discriminator ? form.discriminator?.__description : '-'}\n`;
        }
        return note;
    }
}

export default Notes;
